import { Clipboard } from '@angular/cdk/clipboard';
import { Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";
import { ApolloError, ApolloQueryResult } from '@apollo/client/core';
import { PlusApollo, dayjs } from '@karve.it/core';
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { ChartOptions, SankeyControllerDatasetOptions, SankeyDataPoint } from "chart.js";
import { getHoverColor } from "chart.js/helpers";
import { gql } from "graphql-tag";
import { cloneDeep, isNil, mergeWith, parseInt, startCase, uniq } from "lodash";
import { unparse } from "papaparse";
import pluralize from "pluralize";
import { MenuItem } from "primeng/api";
import { EMPTY, Observable, Subject, combineLatest, debounceTime, filter, forkJoin, map, of, skip, startWith, switchMap, tap, withLatestFrom } from "rxjs";

import { FreyaNotificationsService } from "src/app/services/freya-notifications.service";

import { environment } from '../../environments/environment';
import { baseEnvironment } from '../../environments/environment.base';
import {
  AggregationDataOnlyGQL,
  AggregationDataOnlyQuery,
  BaseConfigValueFragment,
  DataOnlyGQL,
  DataOnlyQuery,
  PreviewReportFilter,
  PreviewReportQueryVariables,
  PreviewReportWithConfigsGQL,
} from "../../generated/graphql.generated";
import { ExtractObservableType } from "../../typescript";
import { ColorSchemeService } from "../color-scheme.service";
import { GraphQLModule } from "../core/public-api";
import { transactionVolumeChartOptions } from "../dashboard/dashboard.constants";
import { PeriodService } from "../dashboard/period.service";
import { ReportParamsForm } from "../dynamic-reports/preview-dynamic-report/preview-dynamic-report.component";

import { DATE_PARAM_FORMAT, DistanceUnit, jobStageIcons, jurisdictions } from "../global.constants";
import { strToTitleCase } from "../js";
import { formatCurrency } from "../lib.ts/currency.util";
import { AdvancedPeriodOptions } from "../reports/reports.constants";
import { BrandingService } from "../services/branding.service";
import { DemoModeService } from "../services/demo-mode.service";
import { AnyParams, QueryParamsService } from "../shared/query-params.service";
import { childTierOf, lastTierOf, tier1Of } from "../shared/source-tiers";
import { currentTimeSeconds, getBeginningOfTimeUnix, getEndOfTimeUnix } from "../time";
import { getParsedConfigValueByKey } from "../utilities/configs.util";
import { getDashboardErrorMessage } from "../utilities/errors.util";
import { parseEmbeddedUrl, parseHyperlink } from "../utilities/generic.util";


import { AmountDelta, CurrencyDelta } from './delta/delta.component';
import { getDemoPipelinePatch, getDemoRevenuePatch } from './demo-fixtures';

export const DASHBOARD_PATHS = [
  'revenue',
  'engine',
  'activations',
  'sales-leaderboard',
  'system-revenue',
  'otr-system-revenue',
  'otr-labor-tracking',
  'ops-readiness',
  'otr-ops-readiness',
  'self-improving-ai',
  'payments',
] as const;

type KeyOfEndingWith<T, Suffix extends string> = {
  [K in keyof T]: K extends `${string}${Suffix}` ? K : never;
}[keyof T];

type SizePrefix = {
  [K in keyof DashboardState]: K extends `${infer S}Count` ?
    `${S}Total` extends keyof DashboardState ? S : never : never;
}[keyof DashboardState];

export type DashboardPath = typeof DASHBOARD_PATHS[number];

export const comparedToLsKey = 'compared-to' as const;

export const comparedToValues = [
  'Previous Month',
  'Same Month Last Year',
] as const;

export const pipelineMetrics = [
  'Job Count',
  'Revenue',
] as const;

export const pipelinePeriods = [
  'Lead Created',
  'Estimated',
  'Booked',
  'Happening',
  'Invoiced',
  'Completed',
] as const;

export type PipelinePeriod = typeof pipelinePeriods[number];

export type PipelineMetric = typeof pipelineMetrics[number];

export const salesLeaderboardRoles = [
  'Sales Agent',
  'Estimator',
] as const;

export type SalesLeaderboardRole = typeof salesLeaderboardRoles[number];

// Order maps to display order, with `discountedSubTotal DESC` as the default.
// Direction is encoded in each value so we can mix ASC/DESC later if a column warrants it.
export const salesLeaderboardRankByValues = [
  'discountedSubTotal DESC',
  'subTotal DESC',
  'discountTotal DESC',
] as const;

export type SalesLeaderboardRankBy = typeof salesLeaderboardRankByValues[number];

export const salesLeaderboardSortKeys = [
  'revenue',
  'estimatedRevenue',
  'discountedSubTotal',
  'connectedLeadCount',
  'estimateCount',
  'bookingCount',
  'ajs',
  'netConversionRate',
  'estimateConversionRate',
  'discountPercent',
  'cancellationRate',
] as const;

export type SalesLeaderboardSortKey = typeof salesLeaderboardSortKeys[number];

// Requirement: ticket 7056 - a job estimated in the period counts as Booked once it reaches these stages.
const BOOKED_JOB_STAGES = [ 'booking', 'invoice' ];

export type SalesLeaderboardDrillDown = {
  periodType: string;
  groupBy: string;
  dataColumns: string[];
  eventStatuses?: string[];
  // Requirement: `#7020` - the drill-down must list the same events as the number it opens from,
  // and every leaderboard query except Leads counts not-shared events only.
  eventSharedStatuses?: string[];
  // Requirement: `#7020` - applied on the Estimator tab only; the Sales Agent tab still counts them.
  estimatorExcludeClosedReasons?: string[];
  // Requirement: ticket 7056 - Booked lists jobs estimated in the period that have since been booked.
  jobStages?: string[];
  // Requirement: ticket 7056 (Oct 20 meeting) - replaces the fields above on the Estimator tab, which
  // counts Booked by the booking date while the Sales Agent tab counts it by the estimate month.
  estimator?: Partial<Pick<SalesLeaderboardDrillDown, 'periodType' | 'jobStages'>>;
};

/**
 * Requirement: `#7020` - closure reason for a customer who never showed up to the estimate. Must
 * match `CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON` in the backend `jobs.helpers.ts` and the entry added by
 * hand to each zone's `jobs.closedReasons` config, or the exclusion silently matches nothing.
 */
export const CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON = 'customer_no_show_estimate';

const NOT_SHARED_EVENTS = [ 'not-shared' ];

/**
 * Requirement: `#7020` - on the Estimator tab a customer no-show is not part of the dashboard at all:
 * the estimator never spoke to the customer, so the job must not count as their lead, their
 * estimate or their cancellation. The Sales Agent tab keeps counting it.
 *
 * Applied to every Estimator-tab query and drill-down (ticket 7279), not only the three a no-show
 * usually reaches (Leads, Estimated, Cancellation). The backend only accepts the reason in the lead
 * and estimate stages, but a job that still reaches booking would otherwise count as booked without
 * counting as estimated, pushing Est. Conversion above 100%.
 */
function estimatorNoShowExclusion(role: SalesLeaderboardRole): { job?: { excludeClosedReasons: string[] } } {
  return role === 'Estimator'
    ? { job: { excludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ] } }
    : {};
}

export type SalesLeaderboardColumn = {
  key: string;
  label: string;
  format: 'currency' | 'count' | 'percentage';
  tooltip: string;
  // Shown on the Estimator tab instead of `tooltip`, for columns that count differently there.
  estimatorTooltip?: string;
  drillDown?: SalesLeaderboardDrillDown;
};

// Columns shown when drilling into a job-level metric (estimate, booked, leads).
// Ordered to match the user's workflow: who → what stage → dates → money → crew.
export const JOB_DRILLDOWN_COLUMNS = [
  'job',
  'customer',
  'jobStage',
  'jobEstimateStageDate',
  'jobBookingStageDate',
  'discountedSubTotal',
  'salesAgent',
  'estimator',
  'lastScheduledEventEndDateTime',
  'jobCreatedAt',
  'events',
] as const;

// Columns shown when drilling into an event-level metric (cancellation %, revenue).
// lastScheduledEventEndDateTime is not available for groupBy:event — use eventBookingDateTime.
export const EVENT_DRILLDOWN_COLUMNS = [
  'event',
  'job',
  'customer',
  'eventStatus',
  'eventType',
  'eventBookingDateTime',
  'discountedSubTotal',
  'salesAgent',
  'estimator',
] as const;

export const SALES_LEADERBOARD_COLUMNS: SalesLeaderboardColumn[] = [
  { key: 'connectedLeadCount', label: 'Leads', format: 'count',
    tooltip: 'Jobs created in this period',
    drillDown: {
      periodType: 'jobCreatedAt',
      groupBy: 'job',
      dataColumns: [ ...JOB_DRILLDOWN_COLUMNS ],
      estimatorExcludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ],
    } },
  { key: 'estimateCount', label: 'Estimated', format: 'count',
    tooltip: 'Jobs that received an estimate in this period',
    drillDown: {
      periodType: 'jobEstimateStageDate',
      groupBy: 'job',
      dataColumns: [ ...JOB_DRILLDOWN_COLUMNS ],
      eventStatuses: [ 'required', 'pending', 'booked', 'confirmed', 'completed' ],
      eventSharedStatuses: NOT_SHARED_EVENTS,
      estimatorExcludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ],
    } },
  { key: 'estimatedRevenue', label: 'Estimated<br>$$$', format: 'currency',
    tooltip: 'Revenue for jobs estimated in this period',
    drillDown: {
      periodType: 'jobEstimateStageDate',
      groupBy: 'job',
      dataColumns: [ ...JOB_DRILLDOWN_COLUMNS ],
      eventStatuses: [ 'required', 'pending', 'booked', 'confirmed', 'completed' ],
      eventSharedStatuses: NOT_SHARED_EVENTS,
      estimatorExcludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ],
    } },
  { key: 'bookingCount', label: 'Booked', format: 'count',
    tooltip: 'Jobs estimated in this period that are now booked',
    estimatorTooltip: 'Jobs booked in this period',
    drillDown: {
      periodType: 'jobEstimateStageDate',
      groupBy: 'job',
      dataColumns: [ ...JOB_DRILLDOWN_COLUMNS ],
      eventStatuses: [ 'booked', 'confirmed', 'completed' ],
      eventSharedStatuses: NOT_SHARED_EVENTS,
      jobStages: BOOKED_JOB_STAGES,
      estimatorExcludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ],
      estimator: { periodType: 'jobBookingStageDate', jobStages: undefined },
    } },
  // Requirement: ticket 7056 (Oct 21 meeting) - "revenue booked" feeds sales per hour; "revenue executed" is
  // what commission is paid on. The old labels (Booked $$$ / Revenue) were read as the same thing.
  { key: 'discountedSubTotal', label: 'Revenue<br>booked', format: 'currency',
    tooltip: 'Revenue of the jobs booked in this period, whatever the move date',
    drillDown: {
      periodType: 'jobBookingStageDate',
      groupBy: 'job',
      dataColumns: [ ...JOB_DRILLDOWN_COLUMNS ],
      eventStatuses: [ 'booked', 'confirmed', 'completed' ],
      eventSharedStatuses: NOT_SHARED_EVENTS,
      estimatorExcludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ],
    } },
  { key: 'ajs', label: 'AJS', format: 'currency',
    tooltip: 'Average job size: revenue booked divided by the jobs booked in this period' },
  { key: 'netConversionRate', label: 'Net<br>Conversion', format: 'percentage',
    tooltip: 'Share of the leads created in this period that were reached and are now booked. '
      + 'A period\'s figure keeps rising as its leads book' },
  { key: 'estimateConversionRate', label: 'Est.<br>Conversion', format: 'percentage',
    tooltip: 'Booked divided by Estimated, both counted by the month of the estimate',
    estimatorTooltip: 'Share of the jobs estimated in this period that are now booked. Not Booked divided by '
      + 'Estimated: on this tab Booked counts the bookings made in the period' },
  { key: 'discountPercent', label: 'Discount<br>%', format: 'percentage',
    tooltip: 'Average discount off the estimated price for jobs booked in this period' },
  { key: 'cancellationRate', label: 'Cancellation<br>%', format: 'percentage',
    tooltip: 'Cancelled events divided by total events booked in this period',
    drillDown: {
      periodType: 'eventBookedAt',
      groupBy: 'event',
      dataColumns: [ ...EVENT_DRILLDOWN_COLUMNS ],
      // Requirement: `#7020` - list only the cancelled events behind the percentage.
      eventStatuses: [ 'cancelled' ],
      eventSharedStatuses: NOT_SHARED_EVENTS,
      estimatorExcludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ],
    } },
  { key: 'revenue', label: 'Revenue<br>executed', format: 'currency',
    tooltip: 'Revenue of the work done in this period',
    drillDown: {
      periodType: 'eventEnd',
      groupBy: 'event',
      dataColumns: [ ...EVENT_DRILLDOWN_COLUMNS ],
      eventStatuses: [ 'booked', 'confirmed', 'completed' ],
      eventSharedStatuses: NOT_SHARED_EVENTS,
      estimatorExcludeClosedReasons: [ CUSTOMER_NO_SHOW_ESTIMATE_CLOSED_REASON ],
    } },
];


export type SalesLeaderboardEntry = {
  name: string;
  embeddedName: string;
  isUnassigned: boolean;
  userId: string | null;
  currency: string;
  [key: string]: number | string | boolean | null;
};

// These constants are kept here because getQueryVars() still handles salesLeaderboard* cases
// (called by DashboardSalesLeaderboardStore). The data loading, selectors, and view model
// have moved to stores/dashboard-sales-leaderboard.store.ts.
const SALES_LEADERBOARD_DATA_COLUMNS = [
  'eventCount', 'cancelledEventCount',
  'estimateCount',
  'connectedLeadCount', 'connectedQualifiedLeadCount',
  'discountedSubTotal',
];
const SALES_LEADERBOARD_LEADS_DATA_COLUMNS = ['jobCount', 'connectedQualifiedBookingCount'];
const SALES_LEADERBOARD_REVENUE_DATA_COLUMNS = ['discountedSubTotal', 'subTotal', 'discountTotal'];
const SALES_LEADERBOARD_ESTIMATED_DATA_COLUMNS = ['discountedSubTotal', 'jobCount'];
// Requirement: ticket 7056 - subTotal/discountTotal feed Discount %, which follows the booking date like Booked $$$.
const SALES_LEADERBOARD_BOOKED_DATA_COLUMNS = ['discountedSubTotal', 'subTotal', 'discountTotal'];
const salesLeaderboardRoleToGroupBy: Record<SalesLeaderboardRole, 'salesAgent' | 'estimator'> = {
  'Sales Agent': 'salesAgent',
  'Estimator': 'estimator',
};

const systemRevenueDataColumns = [
  'franchise',
  // Pre-share split
  'internalRevenue',
  // Shared in and out
  'netSharedRevenue',
  // Post-share split
  'discountedSubTotal',
  // Used internally — drives the per-franchise drill-down link. Filtered out of
  // both the rendered table and the CSV via SYSTEM_REVENUE_HIDDEN_HEADERS.
  'franchiseId',
] as const;

// OTR - System Revenue dashboard — per-job breakdown drill-down (the
// 'otrSystemRevenue' report; NOT the 'systemRevenue' key, which is the franchise
// event-based Local report). One row per job (groupBy 'job') exposing the raw
// per-job OTR calculator values behind the dashboard's OTR / Interstate / Labor
// numbers via the calculator_<key> "Add Calculator Column" mechanism (allowed
// for groupBy 'job'). Order matches the
// user-specified column list; headers auto-title-case ("Total Price To Customer").
// All 20 calc columns are dollar amounts (no ratios), so all are currency-formatted.
const OTR_SYSTEM_REVENUE_CALC_COLUMNS = [
  'calculator_totalPriceToCustomer',
  'calculator_totalJobRevenueFP',
  'calculator_flatFeeBonusPotential',
  'calculator_totalLaborAssumed',
  'calculator_consumablesCostLoadUnload',
  'calculator_totalSupplyCost',
  'calculator_truckTravelCost',
  'calculator_additionalChargesCost',
  'calculator_royaltyFP',
  'calculator_fpMargin',
  'calculator_referralRebate',
  'calculator_insuranceValuation',
  'calculator_priceAfterInsurance',
  'calculator_laborPriceLoadUnload',
  'calculator_packingLaborAndSupplyPrice',
  'calculator_truckTravelPrice',
  'calculator_additionalChargesPrice',
  'calculator_other3rdPartyCharges',
  'calculator_subcontractedLaborFlatFee',
  'calculator_insuranceDamageFund',
  // Requirement: `#6538` - the two figures the crew-pay override moves. `laborSubcontractorPortion`
  // is the labor the FP is paid for (flat fee + bonus, net of anything forwarded), and
  // `crewPayPaidToFp` is the amount forwarded because individual crew are paid locally. Without
  // these the drill-down cannot explain why FP revenue and OTR Fees differ from the unadjusted
  // labor figures already in this list.
  'calculator_laborSubcontractorPortion',
  'calculator_crewPayPaidToFp',
  // Requirement: `#6620` - where the FP payout GOES. Without these the report shows FP Revenue
  // dropping to 0 on an OTR-Operated or Third-Party job while the customer total is unchanged,
  // i.e. money apparently vanishing: the execution-type column says why it left but nothing said
  // where it landed. OTR-Operated moves it into OTR Fees; Third-Party moves it, plus both labor
  // lines, into the Third Party line.
  'calculator_otrRevenue',
  'calculator_jobBreakdown_thirdParty',
  // The negotiated figure behind that Third Party total. Its default is FP + labor flat fee +
  // bonus, so on its own it also shows whether the deal was struck at par or conceded.
  'calculator_thirdPartyAgencyPayout',
  // Requirement: `#6765` - the per-job figures behind the dashboard's "Margin on FP OTRs" row, so a
  // number that carries a provisional caveat can actually be investigated.
  //
  // This works because `calculator_*` is a RAW property read with no COALESCE
  // (`previewReport.utils.ts`), so a job missing the key renders an EMPTY cell — whereas the
  // aggregated `calc*` columns coalesce absence to 0 and make it indistinguishable from a real
  // zero. Sorting this report by Job Breakdown_labor therefore surfaces exactly the jobs driving
  // the footnote. That asymmetry is the whole reason these three are worth carrying.
  'calculator_jobBreakdown_otr',
  'calculator_jobBreakdown_labor',
  // NOTE: the aggregate's fallback source, `calculator_totalLaborAssumed`, is deliberately NOT
  // repeated here — it is already in this list further up (with the cost-side columns). Listing a
  // column twice emits the same `AS` alias twice and Neo4j rejects the whole query with "Multiple
  // result columns with the same name are not supported", so the report fails to open at all.
] as const;

// Requirement: #6516 rebuilt the report to address suggestions from OTR. 
// Job identity + last scheduled event date (the sort key) + the franchises a job
// is shared into + billed total ('total' default column) followed by the calc columns.
const OTR_SYSTEM_REVENUE_COLUMNS = [
  'job',
  'customer',
  'lastScheduledEventEndDateTime',
  'sharedWithFranchises',
  // Requirement: `#6620` - who executed the job. Belongs on THIS report above all others: it is the
  // reason an FP Revenue of 0 with a large OTR Fees (or a large Third Party) is correct rather than
  // a data error, and those are the very columns below. Blank on Partner FP jobs, which are almost
  // all of them.
  //
  // The engine's mapped column, not the raw `calculator_` passthrough: nothing here branches on the
  // value, so the report should read "Third-Party" rather than "thirdParty". Also keeps it clear of
  // OTR_SYSTEM_REVENUE_CALC_COLUMNS, which is mapped wholesale to `format: 'currency'` further down
  // and would try to render a string as money.
  'jobExecutionType',
  // Requirement: `#6517` - per-job FP revenue split (e.g. "Calgary: $600, Toledo: $400").
  // Raw calculator_ text column (groupBy 'job'); glance-only, not summable — the per-franchise
  // totals live on the dashboard rows. Not currency-formatted (it's a text summary).
  'calculator_fpSplitSummary',
  'total',
  ...OTR_SYSTEM_REVENUE_CALC_COLUMNS,
] as const;

// Requirement: `#6620` - the text columns on the System Revenue report. Every entry in
// OTR_SYSTEM_REVENUE_CALC_COLUMNS is force-formatted as currency further down, so putting one of
// these in that list renders a string as money — a mangled cell with nothing failing.
//
// The guard below is a COMPILE-TIME check, not a unit test, on purpose: `npx tsc --noEmit` is
// required before committing (CLAUDE.md) whereas CI does not run the frontend test suite, so this
// is the stronger of the two. `Extract` is `never` while the lists stay disjoint; the moment a text
// column is added to the currency list it resolves to a real type and `AssertNever` rejects it with
// `TS2344 ... does not satisfy the constraint 'never'` on the line below. The error points at this
// guard rather than at the offending column, so start from the two lists above when it fires.
//
// NOTE the constraint has to be `extends never` — an earlier attempt used
// `const x: TextColumnWronglyCurrencyFormatted[] = []`, which compiles either way, because an empty
// array is assignable to every array type. A guard that cannot fail is worse than none.
// Declared as a type, not a `const`: it is only ever needed in type position, and a value that is
// never read at runtime trips no-unused-vars in the commit hooks.
type OtrSystemRevenueTextColumn =
  | 'jobExecutionType'
  | 'calculator_fpSplitSummary';

type CurrencyFormattedColumn = typeof OTR_SYSTEM_REVENUE_CALC_COLUMNS[number];
type TextColumnWronglyCurrencyFormatted =
  Extract<OtrSystemRevenueTextColumn, CurrencyFormattedColumn>;

type AssertNever<T extends never> = T;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _NoTextColumnIsCurrencyFormatted = AssertNever<TextColumnWronglyCurrencyFormatted>;

// Requirement: `#6855` - the columns behind the dashboard's data-quality alerts. One row per
// interstate job; every alert is derived from these client-side, so no new engine column and no
// snapshot churn.
//
// Two things about this list are load-bearing and easy to undo by accident:
//
//  1. It is fetched with NO `columnFormats`. The drill-down link maps every OTR_SYSTEM_REVENUE_CALC
//     _COLUMNS entry to `format: 'currency'`, and a currency-formatted `calculator_*` cell renders a
//     MISSING metadata key as "$0.00" (`parseFloat(undefined)` is NaN, the formatter substitutes 0)
//     — indistinguishable from a genuine zero. Unformatted, an absent key arrives as the string
//     'None', which is what makes the missing-labor alert possible at all. Verified live on
//     TLED-QC7-TI7, whose two labor keys are genuinely absent: '$0.00' formatted vs 'None' raw.
//  2. It is fetched with `linkFormat: 'web'` (`CODE <url>`), not 'sheets' (`=HYPERLINK(...)`).
//     The job id is only recoverable from the URL, and `parseHyperlink` returns just
//     { text, zoneId } — no id — so 'sheets' would give codes with nothing to link to. Same choice
//     the OTR - Labor Tracking dashboard makes, for the same reason. The cost is that money cells
//     arrive currency-formatted, hence `parseAlertNumber` below.
const OTR_ALERT_COLUMNS = [
  'job',
  // Requirement: `#6521` - not displayed; present because `sort` must name a SELECTED dataColumn
  // (the engine ORDER BYs after RETURN DISTINCT) or the request 400s. Sorting gives the alert job
  // lists a deterministic newest-first order instead of whatever Neo4j returned.
  'lastScheduledEventEndDateTime',
  // `abs(total - calculator_totalPriceToCustomer) > 0.02` — charges vs calculator.
  // `total` is charge.total (post-tax) rather than discountedSubTotal (pre-tax) at Marcelo's
  // request 2026-09-04: some OTR prices are recorded as tax-inclusive finals, so which one
  // matches the calculator is still open. Revisit together with that.
  'total',
  'calculator_totalPriceToCustomer',
  // The amount reported alongside the Partner-FP-not-shared alert, so the alert and the table row
  // it annotates quote the same number.
  'calculator_totalJobRevenueFP',
  // Partner-FP-not-shared test: Execution Type 'Partner FP' AND no receiving franchise.
  // `sharedWithFranchises` walks SHARED_WITH_ZONE with NO event-status filter, which is exactly
  // what the engine's own calcOtrUnattributedFpRevenue predicate does — so an empty string here
  // means "never shared with any franchise" identically, and the alert reconciles with the row.
  'jobExecutionType',
  'sharedWithFranchises',
  // Missing-labor test: both absent (=== 'None'). COALESCE order matches calcOtrMarginOnFpOtrs.
  'calculator_jobBreakdown_labor',
  'calculator_totalLaborAssumed',
] as const;

// Dynamic-report columns for groupBy: jobCrewMember (one row per job × crew member),
// shared by the OTR - Labor Tracking dashboard's own fetch and its drill-down link.
// Two sources feed these: the Job's calculator metadata (crewPay*, keyed by crew-member
// id, and crewDocContract from the calculator INPUT) and the crew member's User node
// (crewMember, crewDocAch, crewDocW9). The dashboard appends its own calculator_* rate
// columns on top; the drill-down does not (see the 'otrLaborTracking' case).
export const OTR_LABOR_DATA_COLUMNS = [
  'job',
  // Requirement: `#6751` - which franchise(s) the job's events were shared INTO, so the operator
  // running payroll can see whose move this was without opening the job. Comma-separated names
  // (apoc.text.join over a deduplicated pattern comprehension — see sharedWithFranchisesExpr in the
  // reports engine), empty when the job was never shared. Already permitted for groupBy
  // 'jobCrewMember' and job-scoped, so it survives the pre-RETURN WITH DISTINCT without fanning
  // rows out. Same column and same header as the OTR - System Revenue drill-down, deliberately: the
  // two reports describe the same relationship and should not word it differently.
  'sharedWithFranchises',
  'customer',
  'crewMember',
  'lastScheduledEventEndDateTime',
  'jobCreatedAt',
  'crewPayCrewLead',
  'crewPayFlatRate',
  'crewPayPacking',
  'crewPayUnpacking',
  'crewPayLoad',
  'crewPayUnload',
  'crewPayFees',
  'crewPayBonus',
  'crewPayTip',
  'crewPayTotal',
  'crewPayPaid',
  'crewPayUnpaid',
  // Requirement: `#6538` - "Pay Labor to FP" override (Yes/No), per (job × crew member). Must be
  // its own column: when it is set the crew-pay amounts are all 0, which is indistinguishable
  // from "not assigned", so the dashboard cannot infer it the way it infers the checkboxes.
  'crewLaborPaidToFp',
  // Requirement: `#6538` - the crew member's assigned pay phases ("crewLead, load"), read from
  // the manual `__enabled` inputs. This is the dashboard's source of truth for the phase
  // checkboxes: it used to infer them from `amount > 0`, which the override zeroes, and which
  // also mis-reads a phase enabled at a $0 rate.
  'crewLaborAssignedPhases',
  // Requirement: `#6620` - how the job is executed, as the human label ("Third-Party"). This list
  // also builds the drill-down report below, which is read by people, so it carries the mapped
  // column rather than the raw `partnerFp`/`otrOperated` value. The dashboard needs the UNMAPPED
  // value to branch on and gets it from `OTR_LABOR_CALC_COLUMNS` in the component instead — never
  // by comparing against this label, which is defined in another package and could drift.
  'jobExecutionType',
  // Document-compliance flags (Yes/No). Contract is per (job × crew member); ACH/W-9 are
  // per crew member. Rendered as the three rightmost checkbox columns.
  'crewDocContract',
  'crewDocAch',
  'crewDocW9',
];

// Lowercased report header labels (as returned by the backend) for columns the
// system revenue dashboard requests but does not render. `currency` is appended
// by the backend whenever linkFormat is 'sheets'.
const SYSTEM_REVENUE_HIDDEN_HEADERS = new Set([
  'currency',
  'franchise id',
]);

const placeholderSystemRevenueHeaders = systemRevenueDataColumns
  .map((column) => startCase(column))
  .filter((header) => !SYSTEM_REVENUE_HIDDEN_HEADERS.has(header.toLowerCase()));

export interface SystemRevenueFranchiseLink {
  text: string;
  routerLink: any[];
  queryParams: Record<string, unknown>;
}

export type SystemRevenueCell = string | SystemRevenueFranchiseLink;

/**
 * Requirement: `#6855` - one data-quality warning under the OTR - System Revenue table.
 *
 * Replaces the single `marginFootnote` string. A list rather than N more string fields because
 * every one of these has the same shape — a sentence, then the jobs that caused it — and the
 * template renders them uniformly, so alert #4 is a push into an array rather than another
 * template branch.
 *
 * `jobs` carries real links (the per-job fetch gives code AND id), so a reader clicks straight
 * through to the job instead of copying a code into search. Empty `jobs` renders the text alone.
 */
export interface DashboardAlert {
  /** Stable key for trackBy / tests. */
  key: 'charges-out-of-sync' | 'partner-fp-not-shared' | 'missing-labor-cost';
  severity: 'warn' | 'info';
  text: string;
  jobs: SystemRevenueJobLink[];
}

export interface SystemRevenueJobLink {
  code: string;
  routerLink: any[];
  queryParams: Record<string, unknown>;
}

type SystemRevenueRowKind = 'franchise' | 'subtotal' | 'interstate' | 'grandtotal';

type SystemRevenueDisplayRow = {
  cells: SystemRevenueCell[];
  kind: SystemRevenueRowKind;
};

export interface User {
  name: string;
  id: string;
}

export interface FlowInfo extends SankeyDataPoint {
  selectedJobType: SelectedJobType;
}

export const groupLeadsByValues = [
  'Source',
  'Job Origin',
  'Estimate Method',
  'Estimator',
  'Sales Agent',
  'UTM Source',
  'UTM Medium',
  'UTM Campaign',
  'UTM Content',
  'UTM Term',
] as const;

export type ComparedTo = typeof comparedToValues[number];

export type GroupLeadsBy = typeof groupLeadsByValues[number];

export const sortByValues = [
  'Recently Active',
  'Created On',
  'Customer',
  'Estimator',
  'Job Stage',
] as const;

export type SortBy = typeof sortByValues[number];

const sortByMap: Record<SortBy, string> = {
  'Recently Active': 'jobUpdatedAt DESC',
  'Created On': 'jobCreatedAt DESC',
  'Estimator': 'estimator DESC',
  'Customer': 'customer DESC',
  'Job Stage': 'jobStage DESC',
}

interface ReportData {
  headers: string[];
  rows: string[][];
}
export const jobStages = [ 'Lead', 'Estimate', 'Booking', 'Invoice' ] as const;

export const selectedJobsColumns = [
  'job',
  'customer',
  'jobStage',
  'jobStatus',
  'estimator',
  'discountedSubTotal',
  'jobCreatedAt'
] as const;

export type SelectedJob = {
  code: string;
  id: string;
  description: string;
  createdAt: string;
  discountedSubTotal: string;
  icon: string;
  customer: {
    name: string;
    id: string;
  },
  estimator?: {
    name: string;
    id: string;
  },
  status: 'Open' | 'Closed',
}

export type JobStage = typeof jobStages[number];

type NullableToOptional<T> = T extends infer U | null ? U | undefined : T;

const selectedJobTypeKeys: (keyof DashboardState)[] = [
  'jobStage',
  'status',
  'source',
  'jobOrigin',
  'estimateMethod',
  'jobClosureReason',
  'attentionExplanation',
  'estimator',
  'salesAgent',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
] as const;

export type SelectedJobType = {
  [K in keyof Pick<DashboardState, typeof selectedJobTypeKeys[number]>]?: NullableToOptional<DashboardState[K]>
};

// Top to bottom
const stagePriority: Record<string, number> = {
  Lead: 1,
  Estimate: 1,
  Booking: 1,
  Invoice: 1,
};

// Left to right
const stageColumn: Record<string, number> = {
  Lead: 1,
  Estimate: 2,
  Booking: 3,
  Invoice: 4,
};

type ExpandedFlow = Pick<SelectedJobType, 'jobStage' | 'status'>;

const delimiter = '__';

export interface DashboardState {
  period: AdvancedPeriodOptions | 'Month of Year';
  customPeriod: string[];
  comparedTo: ComparedTo;
  month: string;
  currency: 'USD' | 'CAD' | 'All';
  groupLeadsBy: GroupLeadsBy;
  sortEngineJobsBy: SortBy,
  expandedFlows: ExpandedFlow[];
  pipelinePeriod: PipelinePeriod;
  pipelineMetric: PipelineMetric;
  jobStage: JobStage | null;
  status: 'open' | 'closed' | null;
  source: string | null;
  jobOrigin: string | null;
  estimateMethod: string | null;
  estimator: string | null;
  salesAgent: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string  | null;
  utmContent: string | null;
  utmTerm: string | null;
  attentionExplanation: string | null;
  jobClosureReason: string | null;
  invoiceSizeCount: number;
  invoiceSizeTotal: number;
  averageInvoiceSizeLoading: boolean;
  averageInvoiceSizeError: string | null;
  pastInvoiceSizeCount: number;
  pastInvoiceSizeTotal: number;
  pastAverageInvoiceSizeLoading: boolean;
  jobSizeCount: number;
  jobSizeTotal: number;
  averageJobSizeLoading: boolean;
  averageJobSizeError: string | null;
  pastJobSizeCount: number;
  pastJobSizeTotal: number;
  pastAverageJobSizeLoading: boolean;
  bookingSizeCount: number
  bookingSizeTotal: number;
  averageBookingSizeLoading: boolean;
  averageBookingSizeError: string | null;
  pastBookingSizeCount: number;
  pastBookingSizeTotal: number;
  pastAverageBookingSizeLoading: boolean;
  scheduledJobSizeCount: number;
  scheduledJobSizeTotal: number;
  averageScheduledJobSizeLoading: boolean;
  averageScheduledJobSizeError: string | null;
  pastScheduledJobSizeCount: number;
  pastScheduledJobSizeTotal: number;
  pastAverageScheduledJobSizeLoading: boolean;
  pastAverageScheduledJobSizeError: string | null;
  realizedRevenue: CurrencyAmount;
  scheduledRevenue: CurrencyAmount;
  expectedRevenue: CurrencyAmount;
  sharedRevenue: CurrencyAmount;
  pastRealizedRevenue: CurrencyAmount;
  pastScheduledRevenue: CurrencyAmount;
  pastExpectedRevenue: CurrencyAmount;
  pastSharedRevenue: CurrencyAmount;
  distanceBooked: number;
  distanceCompleted: number;
  travelTimeBooked: number;
  travelTimeCompleted: number;
  pastDistanceBooked: number;
  pastDistanceCompleted: number;
  pastTravelTimeBooked: number;
  pastTravelTimeCompleted: number;
  revenueAndLogisticsLoading: boolean;
  revenueAndLogisticsError: string | null;
  pastRevenueAndLogisticsLoading: boolean;
  transactionsPaidPerDay: string[][];
  eventsCreatedPerDay: string[][];
  eventsHappeningPerDay: string[][];
  activityLoading: boolean;
  activityError: string | null;
  role: SalesLeaderboardRole;
  salesLeaderboardSortKey: SalesLeaderboardSortKey;
  salesLeaderboardSortDesc: boolean;
  systemRevenue: ReportData;
  systemRevenueLoading: boolean;
  systemRevenueError: string | null;
  otrRevenue: ReportData;
  otrInterstate: ReportData;
  otrLocalRevenue: ReportData;
  // Requirement: `#6855` - one row per interstate job, feeding the data-quality alerts under the
  // table. Deliberately a separate slice from the three aggregates: it is the only OTR fetch that
  // is per-job, and the alerts need job identity (code + id) to link, which a `groupBy: 'none'`
  // aggregate cannot carry.
  otrJobs: ReportData;
  otrRevenueLoading: boolean;
  otrRevenueError: string | null;
  eventsCreated: ReportData;
  eventsCreatedLoading: boolean;
  eventsCreatedError: string | null;
  eventsHappening: ReportData;
  eventsHappeningLoading: boolean;
  eventsHappeningError: string | null;
  sources: string[][];
  sourcesLoading: boolean;
  pipeline: string[][];
  pipelineLoading: boolean;
  pipelineError: string | null;
  engineJobsLoading: boolean;
  engineJobsError: string | null;
  engineJobsHasMore: boolean;
  engineJobsFetchingMore: boolean;
  engineJobs: { [ query: string ]: { data: { rows: string[][] } } },
  units: DistanceUnit;
  lastReload: number;
  /** Incremented by triggerReload() to force all reload triggers to re-emit. */
  reloadKey: number;
  jobClosureReasons: { [ closureReasonId: string ]: string };
  attentionExplanations: { [ closureReasonId: string ]: { label: string; description: string } };
}

export interface CurrencyAmount {
  currency: string;
  amount: number;
}

type DashboardReport =
| 'averageInvoiceSize'
| 'averageJobSize'
| 'averageBookingSize'
| 'averageScheduledJobSize'
|  'eventsCreated'
|  'eventsHappening'
|  'revenueAndLogistics'
|  'leadConversions'
|  'oseConversion'
|  'veConversion'
|  'estimateConversion'
|  'transactionsPaid'
|  'eventsCreatedPerDay'
|  'eventsHappeningPerDay'
|  'pipeline'
|  'engineJobs'
|  'salesLeaderboard'
|  'salesLeaderboardLeads'
|  'salesLeaderboardCancellation'
|  'salesLeaderboardRevenue'
|  'salesLeaderboardEstimated'
|  'salesLeaderboardBooked'
|  'salesLeaderboardBookedCount'
|  'salesLeaderboardBookedByEstimate'
|  'systemRevenue'
|  'otrFranchise'
|  'otrInterstate'
// Requirement: `#6855` - per-job fetch behind the dashboard's data-quality alerts. Same job set as
// 'otrSystemRevenue' but a narrower column list and NO columnFormats — see OTR_ALERT_COLUMNS.
|  'otrAlertJobs'
// Job-level drill-down (one row per job) backing the OTR - System Revenue
// dashboard's "explore in report" links — NOT used to fetch the dashboard table
// itself, and distinct from the 'systemRevenue' key above (the franchise
// event-based Local report).
|  'otrSystemRevenue'
// Crew-pay drill-down (one row per job × crew member) backing the OTR - Labor
// Tracking dashboard's "See crew pay rows" link. Link-only: that dashboard fetches
// its own table directly from the component (it needs extra calculator_* rate
// columns the report viewer cannot display usefully).
|  'otrLaborTracking'
// Note: salesLeaderboard* reports are handled by DashboardSalesLeaderboardStore
;

const placeholderEventHeaders = [
  'Event Type',
  'Net Subtotal',
  'Event Count',
  'Average Net Subtotal per Event',
];

const placeholderSources = [
  [ 'Not Specified', '0', '0', 'USD' ],
];

export const ENGINE_JOBS_QUERY_LIMIT = 5;

const initialState: DashboardState = {
  period: 'Month of Year',
  customPeriod: getCurrentMonthPeriod(),
  comparedTo: 'Same Month Last Year',
  month: getCurrentMonth(),
  currency: 'All',
  groupLeadsBy: 'Source',
  sortEngineJobsBy: 'Recently Active',
  expandedFlows: [],
  pipelinePeriod: 'Lead Created',
  pipelineMetric: 'Revenue',
  jobStage: 'Lead',
  status: null,
  source: null,
  jobOrigin: null,
  estimateMethod: null,
  estimator: null,
  salesAgent: null,
  utmSource: null,
  utmMedium: null,
  utmCampaign: null,
  utmContent: null,
  utmTerm: null,
  attentionExplanation: null,
  jobClosureReason: null,
  invoiceSizeCount: 0,
  invoiceSizeTotal: 0,
  averageInvoiceSizeLoading: false,
  averageInvoiceSizeError: null,
  pastInvoiceSizeCount: 0,
  pastInvoiceSizeTotal: 0,
  pastAverageInvoiceSizeLoading: false,
  jobSizeCount: 0,
  jobSizeTotal: 0,
  averageJobSizeLoading: false,
  averageJobSizeError: null,
  pastJobSizeCount: 0,
  pastJobSizeTotal: 0,
  pastAverageJobSizeLoading: false,
  bookingSizeCount: 0,
  bookingSizeTotal: 0,
  averageBookingSizeLoading: false,
  averageBookingSizeError: null,
  pastBookingSizeCount: 0,
  pastBookingSizeTotal: 0,
  pastAverageBookingSizeLoading: false,
  scheduledJobSizeCount: 0,
  scheduledJobSizeTotal: 0,
  averageScheduledJobSizeLoading: false,
  averageScheduledJobSizeError: null,
  pastScheduledJobSizeTotal: 0,
  pastScheduledJobSizeCount: 0,
  pastAverageScheduledJobSizeLoading: false,
  pastAverageScheduledJobSizeError: null,
  realizedRevenue: getDefaultAmount(),
  scheduledRevenue: getDefaultAmount(),
  expectedRevenue: getDefaultAmount(),
  sharedRevenue: getDefaultAmount(),
  pastRealizedRevenue: getDefaultAmount(),
  pastScheduledRevenue: getDefaultAmount(),
  pastExpectedRevenue: getDefaultAmount(),
  pastSharedRevenue: getDefaultAmount(),
  revenueAndLogisticsLoading: false,
  revenueAndLogisticsError: null,
  pastRevenueAndLogisticsLoading: false,
  transactionsPaidPerDay: [],
  eventsCreatedPerDay: [],
  eventsHappeningPerDay: [],
  activityLoading: false,
  activityError: null,
  distanceBooked: 0,
  distanceCompleted: 0,
  travelTimeBooked: 0,
  travelTimeCompleted: 0,
  pastDistanceBooked: 0,
  pastDistanceCompleted: 0,
  pastTravelTimeBooked: 0,
  pastTravelTimeCompleted: 0,
  role: 'Sales Agent',
  salesLeaderboardSortKey: 'discountedSubTotal',
  salesLeaderboardSortDesc: true,
  systemRevenue: {
    headers: placeholderSystemRevenueHeaders,
    rows: [],
  },
  systemRevenueLoading: false,
  systemRevenueError: null,
  otrRevenue: {
    headers: [],
    rows: [],
  },
  otrInterstate: {
    headers: [],
    rows: [],
  },
  otrLocalRevenue: {
    headers: [],
    rows: [],
  },
  otrJobs: {
    headers: [],
    rows: [],
  },
  otrRevenueLoading: false,
  otrRevenueError: null,
  eventsCreated: {
    headers: placeholderEventHeaders,
    rows: [],
  },
  eventsCreatedLoading: false,
  eventsCreatedError: null,
  eventsHappening: {
    headers: placeholderEventHeaders,
    rows: [],
  },
  eventsHappeningLoading: false,
  eventsHappeningError: null,
  sources: placeholderSources,
  sourcesLoading: false,
  pipeline: [],
  pipelineLoading: false,
  pipelineError: null,
  engineJobsLoading: false,
  engineJobsError: null,
  engineJobsHasMore: false,
  engineJobsFetchingMore: false,
  engineJobs: {},
  units: environment.defaultUnits,
  lastReload: currentTimeSeconds(),
  reloadKey: 0,
  jobClosureReasons: {},
  attentionExplanations: {},
};

function getDefaultAmount() {
  return {
    currency: baseEnvironment.defaultCurrency,
    amount: 0,
  };
}

// Requirement: `#6751` - franchises hidden from the OTR - System Revenue table. Toledo is an
// internal test franchise: it looks like any other partner but its jobs are not real business, and
// its `Local` revenue in particular was large enough to move the Franchise Total and Grand Total.
//
// Scoped to that ONE dashboard on purpose. Notably NOT applied to:
//   - the System Revenue dashboard, which reads the same `systemRevenue` report and still shows
//     Toledo in full — deliberately, so the excluded `Local` figure remains inspectable somewhere;
//   - the "See OTR jobs" drill-down, which is interstate-owned and franchise-agnostic, so a job
//     shared INTO Toledo still appears there with "Toledo" in its Shared With Franchises cell;
//   - the Interstate row, which aggregates interstate-OWNED jobs with groupBy 'none' and never
//     resolves a franchise, so no franchise-id filter can reach it. An interstate job shared into
//     Toledo therefore keeps contributing its OTR margin there. That is intended: the job is real
//     OTR work, and only its FP attribution to a test franchise was meaningless.
//
// A hardcoded Set rather than a Config value: the list is one stable id, and source is the only
// place that can carry the reason above. Revisit if it ever needs changing without a deploy.
const EXCLUDED_FRANCHISE_IDS = new Set([ 'ymm-toledo' ]);

// Requirement: `#6765` - synthetic row id for "OTR Direct". Not a real zone: it is the bucket for
// jobs OTR runs itself, rendered among the partner franchises. Prefixed so it can never collide
// with a franchise id, and kept out of EXCLUDED_FRANCHISE_IDS-style lookups by construction.
const OTR_DIRECT_ROW_ID = '__otr-direct__';

// The interstate program zone: the franchise that owns every OTR job, and therefore the
// base filter of every OTR report. Exported because the OTR dashboard components need the
// same id — for the zone gate (dashboardv2.component) and for the Labor Tracking
// dashboard's own query — and three independent copies of a magic string is how they drift.
export const OTR_INTERSTATE_ZONE_ID = 'ymm-interstate-usa';

// Requirement: `#6585` - Filter the jobs behind the OTR reports by stage: booking and invoice only.
// The OTR dashboards count only committed work: jobs whose CURRENT stage is booking or invoice —
// "booked and beyond" in the lead → estimate → booking → invoice pipeline — so leads, estimates and
// cancelled jobs are excluded.
//
// Named for the RULE rather than for its consumers: the list has grown from the three System
// Revenue reports to also cover OTR - Labor Tracking (`#6751`), and a name like
// OTR_DASHBOARD_JOB_STAGES would have needed rewording each time rather than just re-importing.
//
// Applied to: the System Revenue franchise rows, its interstate aggregate and its per-job
// drill-down; and the Labor Tracking drill-down plus that dashboard's own query (which builds its
// filter in the component — see buildQueryVars). Keeping all five on one constant is what makes a
// dashboard and the report it links to agree.
//
// Deliberately NOT applied to the shared 'systemRevenue' report that feeds the Local column: that
// case also backs the System Revenue dashboard, and filtering it here would silently change that
// dashboard too.
export const OTR_BOOKED_JOB_STAGES = [ 'booking', 'invoice' ];

@Injectable()
export class DashboardStore extends ComponentStore<DashboardState> {

  constructor(
    private dataOnlyGQL: DataOnlyGQL,
    private aggregationDataOnlyGQL: AggregationDataOnlyGQL,
    private previewReportWithConfigsGQL: PreviewReportWithConfigsGQL,
    private graphqlModule: GraphQLModule,
    private queryParamsService: QueryParamsService,
    private branding: BrandingService,
    private router: Router,
    private apollo: PlusApollo,
    private periodService: PeriodService,
    private activatedRoute: ActivatedRoute,
    private colorScheme: ColorSchemeService,
    readonly demoModeService: DemoModeService,
    private clipboard: Clipboard,
    private localNotify: FreyaNotificationsService,
    private brandingService: BrandingService,
  ) {
    super(initialState);
  }

  readonly timezone$ = this.branding.currentBranding.asObservable().pipe(
    map((branding) => branding.timezone),
  );

  readonly period$ = this.select((state) => state.period);
  readonly setPeriod = this.updater((state, period: AdvancedPeriodOptions | 'Month of Year'): DashboardState => ({ ...state, period }));

  readonly customPeriod$ = this.select((state) => state.customPeriod);
  readonly setCustomPeriod = this.updater((state, customPeriod: string[]): DashboardState => ({ ...state, customPeriod }));

  readonly month$ = this.select((state) => state.month);
  readonly setMonth = this.updater((state, month: string): DashboardState => ({ ...state, month }));

  readonly expandedFlows$ = this.select((state) => state.expandedFlows);
  readonly setExpandedFlows = this.updater((state, expandedFlows: ExpandedFlow[]): DashboardState => ({ ...state, expandedFlows }))

  readonly pipelinePeriod$ = this.select((state) => state.pipelinePeriod);
  readonly setPipelinePeriod = this.updater((state, pipelinePeriod: PipelinePeriod): DashboardState => ({ ...state, pipelinePeriod }));;

  readonly pipelineMetric$ = this.select((state) => state.pipelineMetric);
  readonly setPipelineMetric = this.updater((state, pipelineMetric: PipelineMetric): DashboardState => ({ ...state, pipelineMetric }));

  readonly sortEngineJobsBy$ = this.select((state) => state.sortEngineJobsBy);
  readonly setSortEngineJobsBy = this.updater((state, sortEngineJobsBy: SortBy): DashboardState => ({ ...state, sortEngineJobsBy }));

  readonly jobStage$ = this.select((state) => state.jobStage);
  readonly setJobStage = this.updater((state, jobStage: JobStage): DashboardState => ({ ...state, jobStage }));

  readonly status$ = this.select((state) => state.status);
  readonly setStatus = this.updater((state, status: 'open' | 'closed'): DashboardState => ({ ...state, status }));

  readonly source$ = this.select((state) => state.source);
  readonly setSource = this.updater((state, source: string): DashboardState => ({ ...state, source }));

  readonly jobOrigin$ = this.select((state) => state.jobOrigin);
  readonly setJobOrigin = this.updater((state, jobOrigin: string): DashboardState => ({ ...state, jobOrigin }));

  readonly estimateMethod$ = this.select((state) => state.estimateMethod);
  readonly setEstimateMethod = this.updater((state, estimateMethod: string): DashboardState => ({ ...state, estimateMethod }));

  readonly estimator$ = this.select((state) => state.estimator);
  readonly setEstimator = this.updater((state, estimator: string): DashboardState => ({ ...state, estimator }));

  readonly salesAgent$ = this.select((state) => state.salesAgent);
  readonly setSalesAgent = this.updater((state, salesAgent: string): DashboardState => ({ ...state, salesAgent }));

  readonly utmSource$ = this.select((state) => state.utmSource);
  readonly setUtmSource = this.updater((state, utmSource: string): DashboardState => ({ ...state, utmSource }));

  readonly utmTerm$ = this.select((state) => state.utmTerm);
  readonly setUtmTerm = this.updater((state, utmTerm: string): DashboardState => ({ ...state, utmTerm }));

  readonly utmMedium$ = this.select((state) => state.utmMedium);
  readonly setUtmMedium = this.updater((state, utmMedium: string): DashboardState => ({ ...state, utmMedium }));

  readonly utmCampaign$ = this.select((state) => state.utmCampaign);
  readonly setUtmCampaign = this.updater((state, utmCampaign: string): DashboardState => ({ ...state, utmCampaign }));

  readonly utmContent$ = this.select((state) => state.utmContent);
  readonly setUtmContent = this.updater((state, utmContent: string): DashboardState => ({ ...state, utmContent }));

  readonly groupLeadsBy$ = this.select((state) => state.groupLeadsBy);
  readonly setGroupLeadsBy = this.updater((state, groupLeadsBy: GroupLeadsBy): DashboardState => ({ ...state, groupLeadsBy }));

  readonly role$ = this.select((state) => state.role);
  readonly setRole = this.updater((state, role: SalesLeaderboardRole): DashboardState => ({ ...state, role }));

  readonly salesLeaderboardSortKey$ = this.select((state) => state.salesLeaderboardSortKey);
  readonly salesLeaderboardSortDesc$ = this.select((state) => state.salesLeaderboardSortDesc);
  // eslint-disable-next-line max-len
  readonly setSalesLeaderboardSortKey = this.updater((state, salesLeaderboardSortKey: SalesLeaderboardSortKey): DashboardState => ({ ...state, salesLeaderboardSortKey }));
  // eslint-disable-next-line max-len
  readonly setSalesLeaderboardSortDesc = this.updater((state, salesLeaderboardSortDesc: boolean): DashboardState => ({ ...state, salesLeaderboardSortDesc }));

  readonly jobClosureReason$ = this.select((state) => state.jobClosureReason);
  readonly setJobClosureReason = this.updater((state, jobClosureReason: string): DashboardState => ({ ...state, jobClosureReason }));

  readonly attentionExplanation$ = this.select((state) => state.attentionExplanation);
  // eslint-disable-next-line max-len
  readonly setAttentionExplanation = this.updater((state, attentionExplanation: string): DashboardState => ({ ...state, attentionExplanation }));

  readonly jobClosureReasons$ = this.select((state) => state.jobClosureReasons);
  readonly attentionExplanations$ = this.select((state) => state.attentionExplanations);

  readonly selectedJobType$ = this.select(this.select({
    jobStage: this.jobStage$,
    status: this.status$,
    source: this.source$,
    jobOrigin: this.jobOrigin$,
    estimateMethod: this.estimateMethod$,
    jobClosureReason: this.jobClosureReason$,
    attentionExplanation: this.attentionExplanation$,
    estimator: this.estimator$,
    salesAgent: this.salesAgent$,
    utmSource: this.utmSource$,
    utmMedium: this.utmMedium$,
    utmCampaign: this.utmCampaign$,
    utmContent: this.utmContent$,
    utmTerm: this.utmTerm$,
  }, { debounce: true }), (selectedJobType) => {
    const jobTypeSelected = selectedJobTypeKeys.some((k) => selectedJobType[k] !== null);

    if (!jobTypeSelected) { return null; }

    return selectedJobType;
  });

  readonly engineJobs$ = this.select((state) => state.engineJobs);
  readonly engineJobsLoading$ = this.select((state) => state.engineJobsLoading);

  readonly engineJobsHasMore$ = this.select((state) => state.engineJobsHasMore);
  readonly engineJobsFetchingMore$ = this.select((state) => state.engineJobsFetchingMore);

  readonly comparedTo$ = this.select((state) => state.comparedTo);
  readonly setComparedTo = this.updater((state, comparedTo: ComparedTo): DashboardState => ({ ...state, comparedTo }));

  readonly currency$ = this.select((state) => state.currency);
  readonly setCurrency = this.updater((state, currency: 'USD' | 'CAD' | 'All'): DashboardState => ({ ...state, currency }));

  readonly lastReload$ = this.select((state) => state.lastReload);
  readonly setLastReload = this.updater((state, lastReload: number): DashboardState => ({ ...state, lastReload }));

  readonly reloadKey$ = this.select((state) => state.reloadKey);
  /** Call from a tab component's refresh button to re-trigger all data fetching for the active tab. */
  readonly triggerReload = this.updater((state): DashboardState => ({ ...state, reloadKey: state.reloadKey + 1 }));

  readonly isMonthMode$ = this.select((state) => state.period === 'Month of Year');

  readonly deltasEnabled$ = this.select((state) => state.period === 'Month of Year' || state.period === 'MTD');

  readonly customPeriodDisabled$ = this.select((state) => state.period !== 'Custom');

  readonly somethingLoading$ = this.select((state) => {

    const simpleLoadingProps: (keyof DashboardState)[] = [
      'averageInvoiceSizeLoading',
      'pastAverageInvoiceSizeLoading',
      'revenueAndLogisticsLoading',
      'pastRevenueAndLogisticsLoading',
      'activityLoading',
      'eventsCreatedLoading',
      'eventsHappeningLoading',
      'sourcesLoading',
      'pipelineLoading',
    ];

    const complexLoadingProps: (keyof DashboardState)[] = [
    ];

    return simpleLoadingProps.some((prop) => state[prop])
      || complexLoadingProps.some((prop) => [ 'initial', 'more'].includes(state[prop] as string));
  });

  readonly pastMonth$ = this.select(
    this.month$,
    this.comparedTo$,
    (month, comparedTo) => {

      const currentMonthDayJs = dayjs(month, 'MMMM YYYY');

      const substractUnit = comparedTo === 'Previous Month' ? 'month' : 'year';

      return currentMonthDayJs.subtract(1, substractUnit).format('MMMM YYYY');
    }
  );

  readonly unixPeriod$ = this.select(
    this.customPeriod$,
    this.timezone$,
    (customPeriod, timezone) => {
      const [ startDateStr, endDateStr ] = customPeriod;
      return {
        start: startDateStr ? dayjs.tz(startDateStr, timezone).startOf('day').unix() : getBeginningOfTimeUnix(),
        end: endDateStr ? dayjs.tz(endDateStr, timezone).endOf('day').unix() : getEndOfTimeUnix(),
      };
    },
  );

  readonly pastUnixPeriod$ = this.select(
    this.period$,
    this.pastMonth$,
    this.comparedTo$,
    this.timezone$,
    (period, pastMonth, comparedTo, timezone) => {

      if (period === 'Month of Year') {

        const pastMonthDayJs = dayjs.tz(pastMonth, 'MMMM YYYY', timezone);

        return {
          start: pastMonthDayJs.startOf('month').unix(),
          end: pastMonthDayJs.endOf('month').unix(),
        };

      } else if (period === 'MTD') {

        const today = dayjs.tz(undefined, timezone);

        const substractUnit = comparedTo === 'Previous Month' ? 'month' : 'year';

        const previousMonth = today.subtract(1, substractUnit);

        const daysInPreviousMonth = previousMonth.daysInMonth();

        const dayOfTheMonth = Math.min(today.date(), daysInPreviousMonth);

        return {
          start: previousMonth.startOf('month').unix(),
          end: previousMonth.date(dayOfTheMonth).endOf('day').unix(),
        }
      }
    },
  );

  readonly displayPeriod$ = this.select(
    this.period$,
    this.month$,
    this.customPeriod$,
    this.timezone$,
    (period, month, customPeriod, timezone) => {

      if (period === 'Month of Year') {
        return month;
      }

      if (period !== 'Custom') {
        return period;
      }

      return customPeriod.map((dateStr) => dayjs.tz(dateStr, timezone).format('MMM D, YYYY')).join(' - ');
    },
  );

  readonly invoiceSize$ = this.select((state) => this.calculateAverage(state, 'invoiceSize'));
  readonly averageInvoiceSize$ =this.select(this.invoiceSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageInvoiceSizeLoading$ = this.select((state) => state.averageInvoiceSizeLoading);
  readonly averageInvoiceSizeDescription$ = this.select(this.invoiceSize$, (data) => {

    const baseDescription = 'For all invoices finalized during the selected period, ';

    const extra = 'An invoice is considered finalized if it has been sent to the customer or opened on Pandadoc.';

    return this.getDescription({
      baseDescription,
      data,
      extra,
    });
  });
  readonly pastInvoiceSize$ = this.select((state) => this.calculateAverage(state, 'pastInvoiceSize'));
  readonly pastAverageInvoiceSize$ = this.select(this.pastInvoiceSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly pastAverageInvoiceSizeLoading$ = this.select((state) => state.pastAverageInvoiceSizeLoading);

  readonly jobSize$ = this.select((state) => this.calculateAverage(state, 'jobSize'));
  readonly averageJobSize$ = this.select(this.jobSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageJobSizeLoading$ = this.select((state) => state.averageJobSizeLoading);
  readonly averageJobSizeDescription$ = this.select(this.jobSize$, (data) => {
    return this.getDescription({
      baseDescription: 'For all leads that came in during the selected period (whether they have been booked or not), ',
      data,
    });
  });
  readonly pastJobSize$ = this.select((state) => this.calculateAverage(state, 'pastJobSize'));
  readonly pastAverageJobSize$ = this.select(this.pastJobSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly pastAverageJobSizeLoading$ = this.select((state) => state.pastAverageInvoiceSizeLoading);

  readonly bookingSize$ = this.select((state) => this.calculateAverage(state, 'bookingSize'));
  readonly averageBookingSize$ = this.select(this.bookingSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageBookingSizeLoading$ = this.select((state) => state.averageBookingSizeLoading);
  readonly averageBookingSizeDescription$ = this.select(this.bookingSize$, (data) => {
    return this.getDescription({
      baseDescription: 'For all jobs booked during the selected period, ',
      data,
      extra: 'Jobs with a $0 net subtotal are excluded from the count.',
    });
  });
  readonly pastBookingSize$ = this.select((state) => this.calculateAverage(state, 'pastBookingSize'));
  readonly pastAverageBookingSize$ = this.select(this.pastBookingSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly pastAverageBookingSizeLoading$ = this.select((state) => state.pastAverageBookingSizeLoading);

  readonly scheduledJobSize$ = this.select((state) => this.calculateAverage(state, 'scheduledJobSize'));
  readonly averageScheduledJobSize$ = this.select(this.scheduledJobSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageScheduledJobSizeLoading$ = this.select((state) => state.averageScheduledJobSizeLoading);
  readonly averageScheduledJobSizeDescription$ = this.select(this.scheduledJobSize$, (data) => {

    const total = formatCurrency(data.total, data.currency);

    return `For all events scheduled to start in the selected period, 
      the sum of their net subtotal (${ total }) divided by the number of jobs containing such events,
      excluding jobs with a $0 net subtotal (${data.count}). 
      `;

  });
  readonly pastScheduledJobSize$ = this.select((state) => this.calculateAverage(state, 'pastScheduledJobSize'));
  readonly pastAverageScheduledJobSize$ = this.select(
    this.pastScheduledJobSize$,
    (size) => ({ amount: size.average, currency: size.currency }),
  );
  readonly pastAverageScheduledJobSizeLoading$ = this.select((state) => state.pastAverageScheduledJobSizeLoading);

  readonly pipeline$ = this.select((state) => state.pipeline);
  readonly pipelineLoading$ = this.select((state) => state.pipelineLoading);

  readonly pipelineBreakdown$ = this.select(
    this.pipeline$,
    this.groupLeadsBy$,
    this.source$,
    (rows, groupLeadsBy, activeSourceTier) => {

      const groupByCount: Record<string, { jobCount: number; revenue: number }> = {};

      const stageCount: Partial<Record<JobStage, { jobCount: number; revenue: number }>> = {};

      const jobInfoCount: Record<string, { jobCount: number; revenue: number; }> = {};

      let jobsTotal = 0;

      let revenueTotal = 0;

      for (const [ jobData, rawJobCount, rawRevenue ] of rows) {

        const [ stage, group, jobClosureReason, attentionExplanation ] = jobData.split(';');

        const jobStage = stage as JobStage;

        const jobCount = parseInt(rawJobCount);
        const revenue = parseFloat(rawRevenue);

        // For Source grouping, fold the full stored path to the tier currently
        // being shown: tier 1 when no source filter is active, otherwise one
        // tier deeper than the active filter (the drill-down). Distinct full
        // paths that fold to the same display group are merged here. Other
        // groupings (and flat/legacy values) are unaffected. See
        // docs/tiered-job-sources.md.
        const displayGroup = groupLeadsBy === 'Source'
          ? (activeSourceTier ? childTierOf(group, activeSourceTier) : tier1Of(group))
          : group;

        groupByCount[displayGroup] = groupByCount[displayGroup] || {
          jobCount: 0,
          revenue: 0,
        };
        groupByCount[displayGroup].jobCount += jobCount;
        groupByCount[displayGroup].revenue += revenue;

        stageCount[jobStage] = stageCount[jobStage] || {
          jobCount: 0,
          revenue: 0,
        };
        stageCount[jobStage].jobCount += jobCount;
        stageCount[jobStage].revenue += revenue;

        jobsTotal += jobCount;
        revenueTotal += revenue;

        const isOpen = jobClosureReason === 'None';

        const status = isOpen ? 'open' : 'closed';

        const keyBasis = [ jobStage, status ];

        const stageStatusKey = keyBasis.join(delimiter);

        jobInfoCount[stageStatusKey] = jobInfoCount[stageStatusKey] || {
          jobCount: 0,
          revenue: 0,
        }

        jobInfoCount[stageStatusKey].jobCount += jobCount;
        jobInfoCount[stageStatusKey].revenue += revenue;

        const reason = isOpen ? attentionExplanation : jobClosureReason;

        keyBasis.push(reason);

        const stageStatusReasonKey = keyBasis.join(delimiter);

        jobInfoCount[stageStatusReasonKey] = jobInfoCount[stageStatusReasonKey] || {
          jobCount: 0,
          revenue: 0,
        }

        jobInfoCount[stageStatusReasonKey].jobCount += jobCount;
        jobInfoCount[stageStatusReasonKey].revenue += revenue;
      }

      const closedInvoiceKey = [ 'Invoice', 'closed'].join(delimiter);
      const completedInvoiceKey = [ 'Invoice', 'closed', 'complete' ].join(delimiter);

      if (jobInfoCount[closedInvoiceKey] && jobInfoCount[completedInvoiceKey]) {
        const incompleteKey = [ 'Invoice', 'closed', 'not-complete' ].join(delimiter);
        jobInfoCount[incompleteKey] = {
          jobCount: jobInfoCount[closedInvoiceKey].jobCount - jobInfoCount[completedInvoiceKey].jobCount,
          revenue: jobInfoCount[closedInvoiceKey].revenue - jobInfoCount[completedInvoiceKey].revenue,
        }
      }

      // Calculate cumulative stage count
      const cumulativeStageCount: Partial<Record<(typeof jobStages)[number], {
        jobCount: number;
        revenue: number;
      }>> = {};

      for (const jobStage of jobStages) {

        cumulativeStageCount[jobStage] = cumulativeStageCount[jobStage] || {
          jobCount: jobsTotal,
          revenue: revenueTotal,
        }

        if (!stageCount[jobStage]) { continue; }

        jobsTotal -= stageCount[jobStage].jobCount
        revenueTotal -= stageCount[jobStage].revenue;
      }

      return {
        groupByCount,
        jobInfoCount,
        cumulativeStageCount,
      };
    }
  );

  readonly pipelineChartData$ = this.select(
    this.pipelineBreakdown$,
    this.expandedFlows$,
    this.pipelineMetric$,
    this.groupLeadsBy$,
    this.selectedJobType$,
    this.currency$,
    this.attentionExplanations$,
    this.jobClosureReasons$,
    this.colorScheme.colorScheme.pipe(debounceTime(150)),
    (
      {
        groupByCount,
        cumulativeStageCount,
        jobInfoCount,
      },
      expandedFlows,
      metric,
      groupLeadsBy,
      selectedJobType,
      currency,
      attentionExplanations,
      jobClosureReasons,
    ) => {

    const {
      statusColumn,
      statusPriority,
      labelCount,
    } = calculateLabelCount(jobInfoCount, expandedFlows, attentionExplanations, jobClosureReasons);

    const column = {
      ...stageColumn,
      ...statusColumn,
    }

    const priority = {
      ...stagePriority,
      ...statusPriority,
    }

    const sortedGroups = [ ...Object.keys(groupByCount) ]
      .sort((a, b) => {
        if (metric === 'Job Count') {
          return groupByCount[b].jobCount - groupByCount[a].jobCount;
        }
        return groupByCount[b].revenue - groupByCount[a].revenue;
      });

    for (const [ index, group ] of sortedGroups.entries()) {;
      priority[group] = index;
    }

    const data: FlowInfo[] = [];

    // Enter flow from groups to lead
    for (const [ group, { jobCount, revenue } ] of Object.entries(groupByCount)) {

      const jobType: SelectedJobType = {
        jobStage: 'Lead',
      };

      switch (groupLeadsBy) {
        case "Source":
          jobType.source = group;
          break;
        case "Job Origin":
          jobType.jobOrigin = group;
          break;
        case "Estimate Method":
          jobType.estimateMethod = group;
          break;
        case "Estimator":
          jobType.estimator = group;
          break;
        case "Sales Agent":
          jobType.salesAgent = group;
          break;
        case "UTM Source":
          jobType.utmSource = group;
          break;
        case "UTM Medium":
          jobType.utmMedium = group;
          break;
        case "UTM Campaign":
          jobType.utmCampaign = group;
          break;
        case "UTM Content":
          jobType.utmContent = group;
          break;
        case "UTM Term":
          jobType.utmTerm = group;
          break;
      }

      const flow = metric === 'Job Count' ? jobCount : revenue;

      data.push({ from: group, to: 'Lead', flow, selectedJobType: jobType });
    }

    // Enter flow from one stage to the next
    for (let i = 0; i < jobStages.length; i++) {
      const jobStage = jobStages[i];
      const to = jobStages[i + 1];
      const metrics = cumulativeStageCount[to];

      if (!metrics) { continue; }

      const flow = metric === 'Job Count' ? metrics.jobCount : metrics.revenue;

      data.push({ from: jobStage, to, flow, selectedJobType: { jobStage } })
    }

    // Enter flow from stage to open/closed
    for (const [ to, { jobCount, revenue, jobType } ] of Object.entries(labelCount)) {

      const flow = metric === 'Job Count' ? jobCount : revenue;

      data.push({
        from: jobType.jobStage,
        to,
        flow,
        selectedJobType: jobType,
      });
    }

    const isName = [ 'Sales Agent', 'Estimator' ].includes(groupLeadsBy);

    const labels = {
      ...getLabelsFromCount(groupByCount, metric, currency, { isName, isSourceTier: groupLeadsBy === 'Source' }),
      ...getLabelsFromCount(cumulativeStageCount, metric, currency, { pluralizeLabel: true }),
      ...getLabelsFromCount(labelCount, metric, currency),
    };

    const fonts = [
      'Metropolis',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
    ];

    const documentStyle = getComputedStyle(document.documentElement);

    const dataSet: SankeyControllerDatasetOptions = {
      label: 'Pipeline',
      data,
      colorFrom: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle),
      colorTo: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle),
      hoverColorFrom: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle, true),
      hoverColorTo: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle, true),
      colorMode: 'gradient',
      labels,
      priority,
      column,
      size: 'max',
      parsing: {
        from: 'from',
        to: 'to',
        flow: 'flow',
      },
      // nodeWidth: 0,
      nodePadding: getMaxStackedFlows(data) * 5,
      borderWidth: 0,
      // modeX: 'even',
      color: documentStyle.getPropertyValue('--text-color'),
      font: {
        family: fonts.join(','),
        size: 12,
        style: 'normal',
        weight: 'normal',
        lineHeight: 1.2,
      },
    }

    return {
      datasets: [ dataSet ],
    };
  });

  readonly pipelineChartOptions$ = this.select(
    this.pipelineMetric$,
    this.currency$,
    (metric, selectedCurrency) => {

      const currency = selectedCurrency === 'All' ? baseEnvironment.defaultCurrency : selectedCurrency;

      const options: ChartOptions<'sankey'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        onHover(event, activeElements) {
          if (activeElements.length) {
            (event.native.target as any).style.cursor = 'pointer';
          } else {
            (event.native.target as any).style.cursor = 'default';
          }
        },
        plugins: {
          tooltip: {
            displayColors: false,
            callbacks: {
              label(context) {

                const data = context.raw as { flow: number; to: string; from: string; selectedJobType: SelectedJobType };

                const isUser = data.selectedJobType?.estimator || data.selectedJobType?.salesAgent;

                const from = isUser ? decodeUser(data.from).name : data.from;

                const amount = metric === 'Job Count' ? data.flow.toString() : formatCurrency(data.flow, currency);

                return `${from} to ${data.to}: ${amount}`;
              }
            }
          }
        }
      }

      return options;
    }
  )
  readonly actionableItems$ = this.select(
    this.pipelineBreakdown$,
    this.currency$,
    ({ jobInfoCount }, selectedCurrency) => {

      const currency = selectedCurrency === 'All' ? baseEnvironment.defaultCurrency : selectedCurrency;

      type ActionableItemKey = 'openLeads' | 'openInvoices' | 'openEstimates';

      const output: Partial<Record<ActionableItemKey, {
        show: boolean;
        revenue?: string;
        review?: () => void;
      }>> = {};

      const actionableStages = jobStages
        .filter((stage) => stage !== 'Booking');

      let hasActionableItems = false;

      for (const stage of actionableStages) {

        const jobInfoKey = [ stage, 'open' ].join(delimiter);

        const metrics = jobInfoCount[jobInfoKey];

        const outputKey = `open${stage}s`;

        output[outputKey] = {};

        if (!metrics || !metrics.jobCount) {
          output[outputKey].show = false;
          continue;
        }

        hasActionableItems = true;

        output[outputKey].show = true;

        if (metrics.revenue) {
          output[outputKey].revenue = formatCurrency(metrics.revenue, currency);
        }

        output[outputKey].review = () => {
          this.setJobStage(stage);
          this.setStatus('open');
          this.setAttentionExplanation(null);
          this.setJobClosureReason(null);
        }
      }

      return {
        ...output,
        hasActionableItems,
      };
    }
  )

  readonly pipelineChartHeader$ = this.select(
    this.period$,
    this.month$,
    this.customPeriod$,
    this.pipelinePeriod$,
    (period, month, customPeriod, pipelinePeriod) => {

      const whatHappenedTo = 'What happened to';

      const jobTypes: Record<PipelinePeriod, string> = {
          'Lead Created': 'leads created',
          Estimated: 'jobs estimated',
          Booked: 'jobs booked',
          Happening: 'jobs happening',
          Invoiced: 'jobs invoiced',
          Completed: 'jobs completed'
      }

      const jobsProcessed = jobTypes[pipelinePeriod];

      if (period === 'Month of Year') {
        return `${whatHappenedTo} ${jobsProcessed} in ${month}?`;
      }

      if (period === 'Custom') {
        const [ start, end ] = customPeriod;
        const startStr = dayjs(start).format('MMMM D');
        const endStr = dayjs(end).format('MMMM D');
        return `${whatHappenedTo} ${jobsProcessed} between ${startStr} and ${endStr}?`;
      }

      if (period === 'Since Last Run') {
        return `${whatHappenedTo} ${jobsProcessed} ${period.toLowerCase()}?`;
      }

      if (period === 'MTD Last Year') {
        return `${whatHappenedTo} ${jobsProcessed} MTD last year?`;
      }

      if (period.endsWith('hours') || period.endsWith('days') || period === 'Month After Next') {
        return `${whatHappenedTo} ${jobsProcessed} in the ${ period.toLowerCase() }?`;
      }

      const noPreposition: (typeof period)[] = [
        'Today',
        'Tomorrow',
        'Yesterday',
        'WTD',
        'MTD',
        'QTD',
        'YTD',
      ];

      if (noPreposition.includes(period) || period.startsWith('Last') || period.startsWith('Next') || period.startsWith('This')) {
        return `${whatHappenedTo} ${jobsProcessed} ${ period === period.toUpperCase() ? period : period.toLowerCase() }?`;
      }

      if (period === 'All Time') {
        return `${whatHappenedTo} all ${jobsProcessed}?`;
      }

      const pastAndFuture: (typeof period)[] = [
        'Past',
        'Future',
      ];

      if (pastAndFuture.includes(period) || period === period.toUpperCase()) {
        return `${whatHappenedTo} ${jobsProcessed} in the ${ period === period.toUpperCase() ? period : period.toLowerCase() }?`;
      }

      return `${whatHappenedTo} ${jobsProcessed} in ${ period.toLowerCase() }?`;
    }
  )

  readonly pipelineChartHeight$ = this.select(this.pipelineChartData$, ({ datasets }) => {

    const minHeight = 200;

    const [ { data } ] = datasets;

    if (!data.length) {
      return minHeight;
    }

    const maxStackedFlows = getMaxStackedFlows(datasets[0]?.data || []);

    return Math.max(maxStackedFlows * 45, minHeight) ;
  });

  readonly engineJobsTitle$ = this.select(
    this.selectedJobType$,
    this.attentionExplanations$,
    this.jobClosureReasons$,
    getTitle,
  );

  readonly conversionRate$ = this.select(
    this.pipelineBreakdown$,
    this.jobStage$,
    ({ cumulativeStageCount, jobInfoCount }, currentStage) => {

      const currentStageCount = cumulativeStageCount[currentStage].jobCount;

      if (currentStageCount === 0) {
        return 0;
      }

      const completedInvoicesKey = [ currentStage, 'closed', 'complete' ].join(delimiter);

      const nextStage = jobStages[ jobStages.indexOf(currentStage) + 1 ];

      const nextStageCount = currentStage === 'Invoice' ? jobInfoCount[completedInvoicesKey]?.jobCount || 0
        : cumulativeStageCount[nextStage]?.jobCount || 0;

      return (nextStageCount / currentStageCount) * 100;
    }
  );

  readonly engineJobsChips$ = this.select(
    this.pipelineBreakdown$,
    this.jobStage$,
    ({ cumulativeStageCount, jobInfoCount }, currentStage) => {

      const chips: {
        label: string;
        value: number;
        chipType: 'count' | 'percent' | 'currency';
      }[] = [];

      const leadCount = cumulativeStageCount['Lead']?.jobCount || 0;
      const estimateCount = cumulativeStageCount['Estimate']?.jobCount || 0;
      const bookingCount = cumulativeStageCount['Booking']?.jobCount || 0;
      const invoiceMetrics = cumulativeStageCount['Invoice'] || { jobCount: 0, revenue: 0 };
      const { jobCount: invoiceCount } = invoiceMetrics;

      const completedKey = [ 'Invoice', 'closed', 'complete' ].join(delimiter);

      const completedCount = jobInfoCount[completedKey]?.jobCount || 0;

      switch (currentStage) {
        case 'Lead':

          chips.push({
            label: 'Converted to estimate',
            value: this.getPercent({
              total: leadCount,
              converted: estimateCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Converted to booking',
            value: this.getPercent({
              total: leadCount,
              converted: bookingCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Converted to invoice',
            value: this.getPercent({
              total: leadCount,
              converted: invoiceCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Completed',
            value: this.getPercent({
              total: leadCount,
              converted: completedCount,
            }),
            chipType: 'percent',
          });

          break;
        case 'Estimate':

          chips.push({
            label: 'Estimated total',
            value: estimateCount,
            chipType: 'count',
          });

          chips.push({
            label: 'Converted to booking',
            value: this.getPercent({
              total: estimateCount,
              converted: bookingCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Converted to invoice',
            value: this.getPercent({
              total: estimateCount,
              converted: invoiceCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Completed',
            value: this.getPercent({
              total: estimateCount,
              converted: completedCount,
            }),
            chipType: 'percent',
          });

          break;
        case 'Booking':

          chips.push({
            label: 'Booked total',
            value: bookingCount,
            chipType: 'count',
          });

          chips.push({
            label: 'Converted to invoice',
            value: this.getPercent({
              total: bookingCount,
              converted: invoiceCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Completed',
            value: this.getPercent({
              total: bookingCount,
              converted: completedCount,
            }),
            chipType: 'percent',
          });
          break;

        case 'Invoice':

          chips.push({
            label: 'Invoiced total',
            value: invoiceCount,
            chipType: 'count',
          });

          const { revenue: invoiceRevenue } = invoiceMetrics;

          const averageInvoiceSize = invoiceCount ? invoiceRevenue / invoiceCount : 0;

          chips.push({
            label: 'Average invoice size',
            value: averageInvoiceSize,
            chipType: 'currency',
          });

          break;
        default:
        throw new Error(`Unhandled job stage ${currentStage}`);
      }

      return chips;
    }
  );

  readonly engineJobsBreadcrumbs$ = this.select(
    this.selectedJobType$,
    this.jobClosureReasons$,
    this.attentionExplanations$,
    (selectedJobType, jobClosureReasons, attentionExplanations) => {

      const styleClass = 'freya-link-button';

      const breadcrumbs: MenuItem[] = [];

      for (const jobStage of jobStages) {

        let label = pluralize(jobStage);

        if (jobStage === 'Lead') {
          label = `All ${label}`;
        }

        const command = () => {
          this.patchState({
            jobStage,
            status: null,
            attentionExplanation: null,
            jobClosureReason: null,
          });
        }

        breadcrumbs.push({ label, command, styleClass });

        if (jobStage === selectedJobType.jobStage) {
          break;
        }
      }

      if (selectedJobType.status) {

        const jobType = {
          jobStage: selectedJobType.jobStage,
          status: selectedJobType.status,
          attentionExplanation: null,
          jobClosureReason: null,
        };

        const command = () => {
          this.patchState(jobType);
        }

        breadcrumbs.push({
          label: getTitle(jobType, attentionExplanations, jobClosureReasons),
          command,
          styleClass,
        });
      }

      if (selectedJobType.attentionExplanation || selectedJobType.jobClosureReason) {


        if (
          selectedJobType.jobStage === 'Invoice' &&
          selectedJobType.status === 'closed' &&
          selectedJobType.jobClosureReason === 'complete'
        ) {
          breadcrumbs.pop();
        }

        breadcrumbs.push({
          label: getTitle(selectedJobType, attentionExplanations, jobClosureReasons, false),
          styleClass,
        });
      }

      const lastItem = breadcrumbs[breadcrumbs.length - 1];

      lastItem.command = undefined;
      lastItem.styleClass = undefined;

      return breadcrumbs;
    }
  );

  readonly comparedToText$ = this.select(
    this.period$,
    this.month$,
    this.pastMonth$,
    this.comparedTo$,
    (period, selectedMonth, pastMonth, comparedTo) => {

      // If user is in month mode and they selected a month other than the current one, show the name of the past month explicitly
      if (period === 'Month of Year') {

        const selectedMonthDate = dayjs(selectedMonth, 'MMMM YYYY');

        const isCurrentMonth = selectedMonthDate.isSame(dayjs(), 'month');

        if (!isCurrentMonth) {
          return `since ${pastMonth}`;
        }
      }

      return comparedTo === 'Previous Month' ? 'since last month' : 'since this month last year';
    },
  );

  readonly realizedRevenue$ = this.select((state) => state.realizedRevenue);
  readonly scheduledRevenue$ = this.select((state) => state.scheduledRevenue);
  readonly expectedRevenue$ = this.select((state) => state.expectedRevenue);
  readonly sharedRevenue$ = this.select((state) => state.sharedRevenue);
  readonly pastRealizedRevenue$ = this.select((state) => state.pastRealizedRevenue);
  readonly pastScheduledRevenue$ = this.select((state) => state.pastScheduledRevenue);
  readonly pastExpectedRevenue$ = this.select((state) => state.pastExpectedRevenue);
  readonly pastSharedRevenue$ = this.select((state) => state.pastSharedRevenue);

  readonly revenueAndLogisticsLoading$ = this.select((state) => state.revenueAndLogisticsLoading);
  readonly pastRevenueAndLogisticsLoading$ = this.select((state) => state.pastRevenueAndLogisticsLoading);

  readonly distanceBooked$ = this.select((state) => state.distanceBooked);
  readonly distanceCompleted$ = this.select((state) => state.distanceCompleted);
  readonly travelTimeBooked$ = this.select((state) => state.travelTimeBooked);
  readonly travelTimeCompleted$ = this.select((state) => state.travelTimeCompleted);

  readonly pastDistanceBooked$ = this.select((state) => state.pastDistanceBooked);
  readonly pastDistanceCompleted$ = this.select((state) => state.pastDistanceCompleted);
  readonly pastTravelTimeBooked$ = this.select((state) => state.pastTravelTimeBooked);
  readonly pastTravelTimeCompleted$ = this.select((state) => state.pastTravelTimeCompleted);

  readonly eventsCreated$ = this.select((state) => state.eventsCreated);
  readonly eventsCreatedLoading$ = this.select((state) => state.eventsCreatedLoading);
  readonly eventsCreatedError$ = this.select((state) => state.eventsCreatedError);
  readonly eventsHappening$ = this.select((state) => state.eventsHappening);
  readonly eventsHappeningLoading$ = this.select((state) => state.eventsHappeningLoading);
  readonly eventsHappeningError$ = this.select((state) => state.eventsHappeningError);

  readonly systemRevenue$ = this.select((state) => state.systemRevenue);
  readonly systemRevenueLoading$ = this.select((state) => state.systemRevenueLoading);
  readonly systemRevenueError$ = this.select((state) => state.systemRevenueError);

  readonly otrRevenue$ = this.select((state) => state.otrRevenue);
  readonly otrInterstate$ = this.select((state) => state.otrInterstate);
  readonly otrLocalRevenue$ = this.select((state) => state.otrLocalRevenue);
  readonly otrJobs$ = this.select((state) => state.otrJobs);
  readonly otrRevenueLoading$ = this.select((state) => state.otrRevenueLoading);
  readonly otrRevenueError$ = this.select((state) => state.otrRevenueError);

  readonly activityLoading$ = this.select((state) => state.activityLoading);

  readonly errors$ = this.select((state) => {

    const errors = Object.keys(state)
      .filter((key) => key.endsWith('Error'))
      .map((key) => state[key]);

    return uniq(errors.filter(Boolean));
  });

  readonly engineJobsInfo$ = this.select(
    this.selectedJobType$,
    this.pipelineBreakdown$,
    (selectedJobType, { jobInfoCount, cumulativeStageCount }) => {

      const {
        jobStage,
        status,
        attentionExplanation,
        jobClosureReason,
      } = selectedJobType;

      const jobTypes: {
        jobType: SelectedJobType,
        key: string,
        metrics: {
          jobCount: number;
          revenue: number;
        }
      }[] = [];

      if (!status) {

        // Open
        const openJobsKey = [ jobStage, 'open' ].join(delimiter);
        const openJobMetrics = jobInfoCount[openJobsKey];

        if (openJobMetrics?.jobCount > 0) {
          jobTypes.push({
            jobType: {
              jobStage,
              status: 'open',
              attentionExplanation: null,
              jobClosureReason: null,
            },
            key: openJobsKey,
            metrics: openJobMetrics,
          })
        }

        // Converted
        const nextStage = jobStages[ jobStages.indexOf(jobStage) + 1 ];

        const convertedJobType: SelectedJobType = {
          jobStage: nextStage,
          status: null,
          attentionExplanation: null,
          jobClosureReason: null,
        }

        let convertedJobKey: string = nextStage;
        let convertedJobMetrics = cumulativeStageCount[convertedJobKey]

        if (jobStage === 'Invoice') {
          convertedJobType.jobStage = 'Invoice';
          convertedJobType.status = 'closed';
          convertedJobType.jobClosureReason = 'complete';
          convertedJobKey = [ 'Invoice', 'closed', 'complete' ].join(delimiter);
          convertedJobMetrics = jobInfoCount[convertedJobKey];
        }

        if (convertedJobMetrics?.jobCount > 0) {
          jobTypes.push({
            jobType: convertedJobType,
            key: convertedJobKey,
            metrics: convertedJobMetrics,
          });
        }

        // Closed
        const closedJobsKeyBasis = [ jobStage, 'closed' ];

        if (jobStage === 'Invoice') {
          closedJobsKeyBasis.push('not-complete');
        }

        const closedJobsKey = closedJobsKeyBasis.join(delimiter);

        const closedJobMetrics = jobInfoCount[closedJobsKey];

        if (closedJobMetrics?.jobCount > 0) {
          jobTypes.push({
            jobType: {
              jobStage,
              status: 'closed',
              attentionExplanation: null,
              jobClosureReason: null,
            },
            key: closedJobsKey,
            metrics: closedJobMetrics,
          })
        }

      } else if (!attentionExplanation && !jobClosureReason) {

        const keyPrefix = [ jobStage, status  ].join(delimiter);

        const availableKeys = Object.keys(jobInfoCount)
          .filter((key) => key.startsWith(keyPrefix) && key.split(delimiter).length > 2);

        for (const key of availableKeys) {

          const reason = key.split(delimiter)[2];

          if (jobStage === 'Invoice' && status === 'closed' && [ 'complete', 'not-complete' ].includes(reason)) { continue; }

          const metrics = jobInfoCount[key];

          if (!metrics?.jobCount) { continue; }

          jobTypes.push({
            jobType: {
              jobStage,
              status,
              attentionExplanation: status === 'open' ? reason : null,
              jobClosureReason: status === 'closed' ? reason : null,
            },
            key,
            metrics,
          });
        }
      } else {

        const reason = status === 'open' ? attentionExplanation : jobClosureReason;

        const key = [ jobStage, status, reason ].join(delimiter);

        const metrics = jobInfoCount[key];

        jobTypes.push({
          jobType: selectedJobType,
          key,
          metrics,
        });
      }

      return {
        jobTypes,
        selectedJobType,
      };
    }
  );

  readonly engineJobSections = this.select(
    this.engineJobsInfo$,
    this.engineJobs$,
    this.attentionExplanations$,
    this.jobClosureReasons$,
    this.currency$,
    this.selectedJobType$,
    this.unixPeriod$,
    this.sortEngineJobsBy$,
    this.groupLeadsBy$,
    this.pipelinePeriod$,
    this.timezone$,
    (
      { jobTypes },
      engineJobs,
      attentionExplanations,
      jobClosureReasons,
      currency,
      selectedJobType,
      { start, end },
      sortBy,
      groupLeadsBy,
      pipelinePeriod,
      timezone,
    ) => {

      const sections: {
        title?: string;
        jobs: SelectedJob[];
        jobType: SelectedJobType;
        button?: {
          label: string;
          command: () => void;
        };
        reportParams: AnyParams;
        loadingSkeletons: number[];
      }[] = [];

      for (const { jobType, key, metrics } of jobTypes) {

        const title = getTitle(jobType, attentionExplanations, jobClosureReasons, false);

        let fullTitle = title;

        const graphQLKey = encodeKeyForGraphQL(key);

        const rows = engineJobs[graphQLKey]?.data?.rows || [];

        const jobs = parseJobs(rows);

        let loadingSkeletons = new Array(ENGINE_JOBS_QUERY_LIMIT).fill(0);

        // Verify that sort by is taken into account
        const reportParams = this.getQueryParams({
          start,
          end,
          currency,
          report: 'engineJobs',
          opts: {
            selectedJobType: {
              ...selectedJobType,
              ...jobType,
            },
            sortBy,
            groupLeadsBy,
            pipelinePeriod,
          },
        }, timezone);

        const displayCurrency = currency === 'All' ? baseEnvironment.defaultCurrency : currency;

        if (metrics) {

          const revenue = formatCurrency(metrics.revenue, displayCurrency);

          fullTitle = `${ title } - ${ metrics.jobCount } - ${ revenue }`;

          loadingSkeletons = new Array(Math.min(metrics.jobCount, ENGINE_JOBS_QUERY_LIMIT)).fill(0);
        }

        const section: typeof sections[number] = {
          title: fullTitle,
          jobs,
          jobType,
          loadingSkeletons,
          reportParams,
        }

        const isLeaf = jobType.status && (jobType.attentionExplanation || jobType.jobClosureReason);

        if (isLeaf && metrics?.jobCount <= ENGINE_JOBS_QUERY_LIMIT) {
          sections.push(section);
          continue;
        }

        const goToJobs = () => {

          this.setJobStage(jobType.jobStage);

          if (jobType.status) {
            this.setStatus(jobType.status);
            this.setExpandedFlows([{
              jobStage: jobType.jobStage,
              status: jobType.status,
            }]);
          }

          if (jobType.attentionExplanation) {
            this.setAttentionExplanation(jobType.attentionExplanation);
          }

          if (jobType.jobClosureReason) {
            this.setJobClosureReason(jobType.jobClosureReason);
          }
        }

        const labelVerb = isLeaf ? 'View all' : 'Go to';

        section.button = {
          label: `${labelVerb} ${title}`,
          command: goToJobs,
        };

        sections.push(section);
      }

      if (sections.length === 1 && (selectedJobType.attentionExplanation || selectedJobType.jobClosureReason)) {
        const [ section ] = sections;
        section.title = undefined;
        section.button = undefined;
      }

      return sections;
    }
  );


  /**
   * TRIGGERS
   */

  readonly bottomReached$ = new Subject<DashboardPath>;

  readonly jobUpdated$ = new Subject<string>();

  readonly reloadDataTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.routeActivated$,
    this.graphqlModule.zone,
    this.reloadKey$,
    (currency, { start, end }, path, zone, _key) => {
      if (!zone || path !== 'revenue') return null;
      return { start, end, currency, path, zone };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean)
  );

  readonly reloadPastDataTrigger$ = this.select(
    this.deltasEnabled$,
    this.currency$,
    this.pastUnixPeriod$,
    this.routeActivated$,
    this.graphqlModule.zone,
    this.reloadKey$,
    (deltasEnabled, currency, pastUnixPeriod, path, zone, _key) => {
      if (!zone || !deltasEnabled || path !== 'revenue') return null;
      const { start, end } = pastUnixPeriod || {};
      return { deltasEnabled, currency, start, end, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean)
  );

  readonly resetCustomPeriodTrigger$ = this.select(
    this.period$,
    this.month$,
    this.timezone$,
    (period, month, timezone) => ({ period, month, timezone })
  ).pipe(filter((input) => input.period !== 'Custom'));

  readonly setQueryParamsTrigger$ = this.select(
    this.period$,
    this.customPeriod$,
    this.month$,
    this.comparedTo$,
    this.currency$,
    this.groupLeadsBy$,
    this.pipelineMetric$,
    this.pipelinePeriod$,
    this.jobStage$,
    this.status$,
    this.jobOrigin$,
    this.source$,
    this.estimateMethod$,
    this.jobClosureReason$,
    this.attentionExplanation$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmTerm$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    this.role$,
    this.salesLeaderboardSortKey$,
    this.salesLeaderboardSortDesc$,
    (
      period,
      customPeriod,
      month,
      comparedTo,
      currency,
      groupLeadsBy,
      metric,
      pipelinePeriod,
      jobStage,
      status,
      jobOrigin,
      source,
      estimateMethod,
      jobClosureReason,
      attentionExplanation,
      estimator,
      salesAgent,
      utmSource,
      utmTerm,
      utmMedium,
      utmCampaign,
      utmContent,
      role,
      salesLeaderboardSortKey,
      salesLeaderboardSortDesc,
    ) => ({
      period,
      customPeriod,
      month,
      comparedTo,
      currency,
      groupLeadsBy,
      metric,
      pipelinePeriod,
      jobStage,
      status,
      jobOrigin,
      source,
      estimateMethod,
      jobClosureReason,
      attentionExplanation,
      estimator,
      salesAgent,
      utmSource,
      utmTerm,
      utmMedium,
      utmCampaign,
      utmContent,
      role,
      salesLeaderboardSortKey,
      salesLeaderboardSortDesc,
    }),
    { debounce: true },
  );

  readonly reloadSystemRevenueTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.routeActivated$,
    this.graphqlModule.zone,
    this.reloadKey$,
    (currency, { start, end }, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'system-revenue'),
  );

  readonly reloadOtrRevenueTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.routeActivated$,
    this.graphqlModule.zone,
    this.reloadKey$,
    (currency, { start, end }, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'otr-system-revenue'),
  );

  readonly reloadPipelineTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.pipelineMetric$,
    this.pipelinePeriod$,
    this.source$,
    this.jobOrigin$,
    this.estimateMethod$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    this.utmTerm$,
    this.groupLeadsBy$,
    this.routeActivated$,
    this.jobUpdated$.pipe(startWith(undefined)),
    this.graphqlModule.zone,
    this.reloadKey$,
    (
      currency,
      { start, end },
      metric,
      pipelinePeriod,
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      groupLeadsBy,
      path,
      jobUpdated,
      zone,
      _key,
    ) => {
      if(!zone) return null;
      return {
        start,
        end,
        currency,
        metric,
        pipelinePeriod,
        source,
        jobOrigin,
        estimateMethod,
        estimator,
        salesAgent,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
        groupLeadsBy,
        path,
        jobUpdated,
      };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'engine'),
  );

  readonly reloadEngineJobsTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.pipelinePeriod$,
    this.sortEngineJobsBy$,
    this.engineJobsInfo$,
    this.source$,
    this.jobOrigin$,
    this.estimateMethod$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    this.utmTerm$,
    this.routeActivated$,
    this.jobUpdated$.pipe(startWith(undefined)),
    this.graphqlModule.zone,
    this.reloadKey$,
    (
      currency,
      { start, end },
      pipelinePeriod,
      sortBy,
      { jobTypes, selectedJobType },
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      path,
      jobUpdated,
      _key,
    ) => {
      return {
        start,
        end,
        pipelinePeriod,
        currency,
        sortBy,
        jobTypes,
        selectedJobType,
        source,
        jobOrigin,
        estimateMethod,
        estimator,
        salesAgent,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
        path,
        jobUpdated,
      };
    },
    { debounce: true },
  ).pipe(
    filter(({ jobTypes, path }) => {
      return jobTypes.length && path === 'engine';
    }),
  );

  readonly filterChips$ = this.select(
    this.source$,
    this.jobOrigin$,
    this.estimateMethod$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmTerm$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    (
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmTerm,
      utmMedium,
      utmCampaign,
      utmContent,
    ) => {

      const chips: {
        label: string;
        onRemove: () => void;
      }[] = [];

      if (source) {
        chips.push({
          label: `Source: ${ source }`,
          onRemove: () => this.setSource(null),
        });
      }

      if (jobOrigin) {
        chips.push({
          label: `Job Origin: ${ jobOrigin }`,
          onRemove: () => this.setJobOrigin(null),
        });
      }

      if (estimateMethod) {
        chips.push({
          label: `Estimate Method: ${ estimateMethod }`,
          onRemove: () => this.setEstimateMethod(null),
        });
      }

      if (estimator) {
        chips.push({
          label: `Estimator: ${ getUserName(estimator) }`,
          onRemove: () => this.setEstimator(null),
        });
      }

      if (salesAgent) {
        chips.push({
          label: `Sales Agent: ${ getUserName(salesAgent) }`,
          onRemove: () => this.setSalesAgent(null),
        });
      }

      if (utmSource) {
        chips.push({
          label: `UTM Source: ${ utmSource }`,
          onRemove: () => this.setUtmSource(null),
        });
      }

      if (utmTerm) {
        chips.push({
          label: `UTM Term: ${ utmTerm }`,
          onRemove: () => this.setUtmTerm(null),
        });
      }

      if (utmMedium) {
        chips.push({
          label: `UTM Medium: ${utmMedium}`,
          onRemove: () => this.setUtmMedium(null),
        });
      }

      if (utmCampaign) {
        chips.push({
          label: `UTM Campaign: ${utmCampaign}`,
          onRemove: () => this.setUtmCampaign(null),
        });
      }

      if (utmContent) {
        chips.push({
          label: `UTM Content: ${ utmContent }`,
          onRemove: () => this.setUtmContent(null),
        });
      }

      return chips;
    }
  )

  /**
   * VIEW MODELS
   */
  readonly settingsViewModel$ = this.select({
    comparedTo: this.comparedTo$,
    currency: this.currency$,
    period: this.period$,
    month: this.month$,
    customPeriod: this.customPeriod$,
    isMonthMode: this.isMonthMode$,
    showComparedTo: this.deltasEnabled$,
    customPeriodDisabled: this.customPeriodDisabled$,
  });

  readonly sharedViewModel$ = this.select({
    loading: this.somethingLoading$,
    lastReload: this.lastReload$,
    errors: this.errors$,
    displayPeriod: this.displayPeriod$,
    month: this.month$,
    metric: this.pipelineMetric$,
    period: this.pipelinePeriod$,
    groupLeadsBy: this.groupLeadsBy$,
  });

  readonly averageInvoiceSizeViewModel$ = this.select({
    average: this.averageInvoiceSize$,
    description: this.averageInvoiceSizeDescription$,
    queryParams: this.getReportQueryParams('averageInvoiceSize'),
    loading: this.averageInvoiceSizeLoading$,
    error: this.select((state) => state.averageInvoiceSizeError),
    delta: this.select(
      this.averageInvoiceSizeLoading$,
      this.pastAverageInvoiceSizeLoading$,
      this.averageInvoiceSize$,
      this.pastAverageInvoiceSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
  });

  readonly averageJobSizeViewModel$ = this.select({
    average: this.averageJobSize$,
    description: this.averageJobSizeDescription$,
    queryParams: this.getReportQueryParams('averageJobSize'),
    loading: this.averageJobSizeLoading$,
    error: this.select((state) => state.averageJobSizeError),
    delta: this.select(
      this.averageJobSizeLoading$,
      this.pastAverageJobSizeLoading$,
      this.averageJobSize$,
      this.pastAverageJobSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    )
  });

  readonly averageBookingSizeViewModel$ = this.select({
    average: this.averageBookingSize$,
    description: this.averageBookingSizeDescription$,
    queryParams: this.getReportQueryParams('averageBookingSize'),
    loading: this.averageBookingSizeLoading$,
    error: this.select((state) => state.averageBookingSizeError),
    delta: this.select(
      this.averageBookingSizeLoading$,
      this.pastAverageBookingSizeLoading$,
      this.averageBookingSize$,
      this.pastAverageBookingSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    )
  });

  readonly averageScheduledJobSizeViewModel$ = this.select({
    average: this.averageScheduledJobSize$,
    description: this.averageScheduledJobSizeDescription$,
    queryParams: this.getReportQueryParams('averageScheduledJobSize'),
    loading: this.averageScheduledJobSizeLoading$,
    error: this.select((state) => state.averageScheduledJobSizeError),
    delta: this.select(
      this.averageScheduledJobSizeLoading$,
      this.pastAverageScheduledJobSizeLoading$,
      this.averageScheduledJobSize$,
      this.pastAverageScheduledJobSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
  });

  // Sales leaderboard query params, entries, and view model have been moved to
  // DashboardSalesLeaderboardStore (stores/dashboard-sales-leaderboard.store.ts).


  readonly systemRevenueViewModel$ = this.select(
    this.systemRevenue$,
    this.currency$,
    this.systemRevenueLoading$,
    this.systemRevenueError$,
    this.displayPeriod$,
    this.lastReload$,
    this.getReportQueryParams('systemRevenue'),
    (data, selectedCurrency, loading, error, displayPeriod, lastReload, queryParams) => {

      // Find raw indices of columns the dashboard uses but does not render, so
      // we can skip them when building visible headers and rows. Robust to
      // reordering — any column in SYSTEM_REVENUE_HIDDEN_HEADERS is dropped
      // regardless of position.
      const hiddenIndices = new Set(
        data.headers
          .map((header, i) => SYSTEM_REVENUE_HIDDEN_HEADERS.has(header.toLowerCase()) ? i : -1)
          .filter((i) => i !== -1),
      );

      const currencyHeaderIndex = data.headers.findIndex((header) => header.toLowerCase() === 'currency');
      const franchiseIdHeaderIndex = data.headers.findIndex((header) => header.toLowerCase() === 'franchise id');

      const headers = data.headers.filter((_, i) => !hiddenIndices.has(i));

      let currency: string = selectedCurrency;

      if (selectedCurrency === 'All') {
        if (currencyHeaderIndex === -1) {
          currency = baseEnvironment.defaultCurrency;
        } else {
          currency = data.rows[0]?.[currencyHeaderIndex] || baseEnvironment.defaultCurrency;
        }
      }

      // The first visible column is the franchise name; the rest are numeric.
      const numericColumnCount = Math.max(headers.length - 1, 0);
      const emptySums = () => new Array<number>(numericColumnCount).fill(0);
      const money = (value: number) => formatCurrency(Math.abs(value) < 0.005 ? 0 : value, currency);
      const formatSums = (sums: number[]) => sums.map((sum) => money(sum));

      const formatReportRow = (row: string[], firstCellLabel?: string) => {
        const franchiseId = franchiseIdHeaderIndex === -1 ? '' : row[franchiseIdHeaderIndex];

        const overrideParams = {
          groupBy: 'event',
          jobFranchises: franchiseId,
          dataColumns: [
            "franchise",
            "event",
            "job",
            "customer",
            "salesAgent",
            "discountedSubTotal",
          ],
          sort: 'salesAgent ASC',
        };

        const cells: SystemRevenueCell[] = [];
        const numericValues = emptySums();
        let visibleIndex = 0;

        for (let i = 0; i < data.headers.length; i++) {
          if (hiddenIndices.has(i)) { continue; }

          const raw = row[i];

          if (visibleIndex === 0) {
            const label = firstCellLabel ?? raw;
            const firstCell: SystemRevenueCell = {
              text: label,
              routerLink: [ '/reports', 'dynamic', 'new' ],
              queryParams: {
                ...queryParams,
                ...this.queryParamsService.formatSearchFormValues(overrideParams, {}),
              },
            };
            cells.push(firstCell);
          } else {
            const value = parseFloat(raw) || 0;
            numericValues[visibleIndex - 1] = value;
            cells.push(money(value));
          }

          visibleIndex++;
        }

        return { cells, franchiseId, numericValues };
      };

      const reportRows = data.rows.map((row) => {
        const franchiseId = franchiseIdHeaderIndex === -1 ? '' : row[franchiseIdHeaderIndex];
        const isInterstate = franchiseId === OTR_INTERSTATE_ZONE_ID;
        return formatReportRow(row, isInterstate ? 'Interstate' : undefined);
      });

      const franchiseRows = reportRows.filter(({ franchiseId }) => franchiseId !== OTR_INTERSTATE_ZONE_ID);
      const interstateRow = reportRows.find(({ franchiseId }) => franchiseId === OTR_INTERSTATE_ZONE_ID) ?? null;

      const franchiseSums = franchiseRows.reduce(
        (sums, row) => sums.map((sum, i) => sum + row.numericValues[i]),
        emptySums(),
      );
      const interstateValues = interstateRow?.numericValues ?? emptySums();
      const grandTotalSums = franchiseSums.map((sum, i) => sum + interstateValues[i]);

      const subtotalCells: SystemRevenueCell[] = [ 'Franchise Total', ...formatSums(franchiseSums) ];
      const grandTotalCells: SystemRevenueCell[] = [ 'Grand Total', ...formatSums(grandTotalSums) ];

      const rows: SystemRevenueDisplayRow[] = [
        ...franchiseRows.map(({ cells }) => ({ cells, kind: 'franchise' as const })),
        ...(franchiseRows.length ? [ { cells: subtotalCells, kind: 'subtotal' as const } ] : []),
        ...(interstateRow ? [ { cells: interstateRow.cells, kind: 'interstate' as const } ] : []),
        ...(franchiseRows.length && interstateRow ? [ { cells: grandTotalCells, kind: 'grandtotal' as const } ] : []),
      ];

      const cellText = (cell: SystemRevenueCell) => typeof cell === 'string' ? cell : cell.text;
      const parsed = data.rows.length
        ? [ headers, ...rows.map((row) => row.cells.map(cellText)) ]
        : [ headers ];

      const actions: MenuItem[] = [
        {
          label: 'Download as CSV',
          icon: 'pi pi-download',
          command: () => {
            const csv = unparse(parsed);

            const blob = new Blob([ csv ], { type: 'text/csv' });

            const link = document.createElement('a');

            link.href = window.URL.createObjectURL(blob);

            const zone = this.brandingService.currentZone().value?.name || 'current zone';
            const time = dayjs().format('YYYY/MM/DD HH:mm');

            link.download = `System Revenue at ${time} [${displayPeriod}] (${ zone }).csv`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
          },
        },
        {
          label: 'View Report',
          icon: 'pi pi-eye',
          routerLink: [ '/reports', 'dynamic', 'new' ],
          queryParams,
        }
      ];

      const copyToClipboard = () => {
        const csv = unparse(parsed, { delimiter: '\t'});
        this.clipboard.copy(csv);
        this.localNotify.success('Copied to clipboard');
      }

      return {
        actions,
        copyToClipboard,
        headers,
        rows,
        loading,
        error,
        isEmpty: !data.rows.length && !loading,
        displayPeriod,
        lastReload,
        queryParams,
      };
    },
  );

  readonly otrRevenueViewModel$ = this.select(
    this.otrRevenue$,
    this.otrInterstate$,
    this.otrLocalRevenue$,
    this.otrJobs$,
    this.otrRevenueLoading$,
    this.otrRevenueError$,
    this.displayPeriod$,
    this.lastReload$,
    this.currency$,
    this.getReportQueryParams('otrSystemRevenue'),
    (
      otrData, interstateData, localData, jobsData, loading, error, displayPeriod, lastReload,
      selectedCurrency, otrSystemRevenueReportParams,
    ) => {
      const headers = [ 'Franchise Partner', 'Local', 'OTR Jobs', 'OTR', 'Flat Labor', 'Total' ];

      // Column lookup helper (case-insensitive) for a {headers, rows} table.
      const indexer = (tableHeaders: string[]) => (name: string) =>
        tableHeaders.findIndex((h) => h.toLowerCase() === name.toLowerCase());

      // --- Franchise OTR / Flat Labor from the dynamic calculator report (A):
      // interstate jobs shared INTO each franchise, grouped by receiving franchise ---
      const otrIdx = indexer(otrData.headers);
      const otrFranchiseI = otrIdx('franchise');
      const otrFranchiseIdI = otrIdx('franchise id');
      // Requirement: `#6517` - sourced from calcOtrFpSplitRevenue ('OTR FP Revenue (Split)').
      const otrOtrI = otrIdx('otr fp revenue (split)');
      const otrFlatLaborI = otrIdx('otr labor subcontractor portion');
      const otrJobsI = otrIdx('job count');
      const otrCurrencyI = otrIdx('currency');

      const otrByFranchise = new Map<string, { name: string; jobCount: number; otr: number; flatLabor: number }>();
      let derivedCurrency = '';

      for (const row of otrData.rows) {
        const id = otrFranchiseIdI !== -1 ? row[otrFranchiseIdI] : '';
        if (!id) { continue; }
        // Requirement: `#6751` - dropped HERE rather than at the `franchiseIds` union below, so an
        // excluded franchise never reaches the map at all. `derivedCurrency` is read further down
        // this loop: filtering later would still let a test franchise decide the currency the whole
        // table renders in when the selector is "All".
        if (EXCLUDED_FRANCHISE_IDS.has(id)) { continue; }
        const rawName = otrFranchiseI !== -1 ? row[otrFranchiseI] : '';
        const name = parseHyperlink(rawName)?.text ?? rawName;
        if (!derivedCurrency && otrCurrencyI !== -1) { derivedCurrency = row[otrCurrencyI]; }
        otrByFranchise.set(id, {
          name,
          jobCount: otrJobsI !== -1 ? (parseInt(row[otrJobsI], 10) || 0) : 0,
          otr: otrOtrI !== -1 ? (parseFloat(row[otrOtrI]) || 0) : 0,
          flatLabor: otrFlatLaborI !== -1 ? (parseFloat(row[otrFlatLaborI]) || 0) : 0,
        });
      }

      // --- Interstate row from the dynamic OTR Margin aggregate (C): a single
      // "Group All" row over interstate-owned jobs (counted once) ---
      const interIdx = indexer(interstateData.headers);
      const interOtrI = interIdx('otr margin');
      const interJobsI = interIdx('job count');
      const interCurrencyI = interIdx('currency');

      // Requirement: `#6765` - the interstate aggregate now carries the whole lower half of the
      // table, so every figure is read from the same single row.
      const interValuationI = interIdx('insurance revenue');
      const interMarginI = interIdx('margin on fp otrs');
      const interAgencyI = interIdx('agency & pass-through');
      const interMissingLaborI = interIdx('jobs missing labor cost');
      const interCalcJobsI = interIdx('jobs with calculator output');
      const interDirectRevenueI = interIdx('otr direct revenue');
      const interDirectLaborI = interIdx('otr direct labor');
      const interDirectJobsI = interIdx('otr direct job count');
      const interUnattributedI = interIdx('partner fp not shared');
      const interUnattributedJobsI = interIdx('partner fp not shared job count');

      const interNum = (row: string[], index: number) =>
        index !== -1 ? (parseFloat(row[index]) || 0) : 0;
      const interCount = (row: string[], index: number) =>
        index !== -1 ? (parseInt(row[index], 10) || 0) : 0;

      let interstate: {
        valuation: number;
        margin: number;
        agency: number;
        missingLaborJobs: number;
        calculatedJobs: number;
      } | null = null;
      let otrDirect: { revenue: number; labor: number; jobCount: number } | null = null;
      let unattributed: { revenue: number; jobCount: number } | null = null;

      const interRow = interstateData.rows[0];
      if (interRow) {
        const valuation = interNum(interRow, interValuationI);
        const margin = interNum(interRow, interMarginI);
        const agency = interNum(interRow, interAgencyI);
        const jobCount = interCount(interRow, interJobsI);
        // Only show the interstate block when there is interstate activity in scope
        // (the aggregate returns a zero row when the zone excludes interstate).
        // `calcOtrMargin` is still read for the presence test alone — it is the one column that
        // was here before, so using it keeps the show/hide behaviour identical to today even
        // though the number itself is no longer rendered.
        const legacyOtrMargin = interNum(interRow, interOtrI);
        if (jobCount > 0 || legacyOtrMargin !== 0 || valuation !== 0 || margin !== 0 || agency !== 0) {
          interstate = {
            valuation,
            margin,
            agency,
            missingLaborJobs: interCount(interRow, interMissingLaborI),
            calculatedJobs: interCount(interRow, interCalcJobsI),
          };
        }

        // Requirement: `#6765` - OTR Direct behaves like any other franchise: it appears only when
        // it has something to report, and is otherwise absent rather than rendered as a zero row.
        const directRevenue = interNum(interRow, interDirectRevenueI);
        const directLabor = interNum(interRow, interDirectLaborI);
        const directJobs = interCount(interRow, interDirectJobsI);
        if (directJobs > 0 || directRevenue !== 0 || directLabor !== 0) {
          otrDirect = { revenue: directRevenue, labor: directLabor, jobCount: directJobs };
        }

        const unattributedRevenue = interNum(interRow, interUnattributedI);
        const unattributedJobs = interCount(interRow, interUnattributedJobsI);
        if (unattributedJobs > 0 || unattributedRevenue !== 0) {
          unattributed = { revenue: unattributedRevenue, jobCount: unattributedJobs };
        }

        if (!derivedCurrency && interCurrencyI !== -1) { derivedCurrency = interRow[interCurrencyI]; }
      }

      // --- Local from the canonical Internal Revenue dynamic report (B) ---
      const localIdx = indexer(localData.headers);
      const localFranchiseI = localIdx('franchise');
      const localFranchiseIdI = localIdx('franchise id');
      const localInternalI = localIdx('internal revenue');
      const localCurrencyI = localIdx('currency');

      const localByFranchise = new Map<string, { name: string; local: number }>();
      for (const row of localData.rows) {
        const id = localFranchiseIdI !== -1 ? row[localFranchiseIdI] : '';
        if (!id) { continue; }
        // Requirement: `#6751` - the `Local` column is the one that made this exclusion necessary,
        // and it arrives from a different report than the OTR columns, so it needs its own guard.
        // Same reason as above for dropping it here rather than at the union.
        if (EXCLUDED_FRANCHISE_IDS.has(id)) { continue; }
        const rawName = localFranchiseI !== -1 ? row[localFranchiseI] : '';
        const name = parseHyperlink(rawName)?.text ?? rawName;
        localByFranchise.set(id, { name, local: parseFloat(row[localInternalI]) || 0 });
        if (!derivedCurrency && localCurrencyI !== -1) { derivedCurrency = row[localCurrencyI]; }
      }

      let currency: string = selectedCurrency;
      if (selectedCurrency === 'All') {
        currency = derivedCurrency || baseEnvironment.defaultCurrency;
      }

      // --- Union of all franchises across both sources (the interstate zone is
      // shown as its own row, never as a franchise partner) ---
      const franchiseIds = uniq([ ...localByFranchise.keys(), ...otrByFranchise.keys() ])
        .filter((id) => id !== OTR_INTERSTATE_ZONE_ID);
      const franchiseRows = franchiseIds
        .map((id) => {
          const otrEntry = otrByFranchise.get(id);
          const localEntry = localByFranchise.get(id);
          const name = otrEntry?.name || localEntry?.name || id;
          const local = localEntry?.local ?? 0;
          const jobCount = otrEntry?.jobCount ?? 0;
          const otr = otrEntry?.otr ?? 0;
          const flatLabor = otrEntry?.flatLabor ?? 0;
          return { id, name, local, jobCount, otr, flatLabor, total: local + otr + flatLabor };
        })
        // Requirement: `#6765` - "OTR Direct" joins the partner franchises as an ordinary row:
        // OTR runs these jobs itself, so the payout the calculator no longer zeroes belongs to OTR
        // rather than to any partner. Pushed in BEFORE the sort so it lands alphabetically (between
        // Oklahoma City and Phoenix) and flows into the Franchise Total by itself — the stakeholder
        // asked to see it "as if it was another franchise", and doing it here means every downstream
        // consumer (subtotal, grand total, CSV, clipboard) treats it as one without further code.
        //
        // `local` is always 0: the Local column is franchise charge revenue, and OTR Direct is not a
        // franchise, so it has none. It renders blank rather than $0.00 (see franchiseCells).
        .concat(otrDirect ? [ {
          id: OTR_DIRECT_ROW_ID,
          name: 'OTR Direct',
          local: 0,
          jobCount: otrDirect.jobCount,
          otr: otrDirect.revenue,
          flatLabor: otrDirect.labor,
          total: otrDirect.revenue + otrDirect.labor,
        } ] : [])
        .sort((a, b) => a.name.localeCompare(b.name));

      // Franchise subtotal (sum of franchise rows). No jobCount: the OTR Jobs
      // count is intentionally blank in both total rows — the franchise rows and
      // the interstate row count the same interstate jobs, so it isn't additive.
      const franchiseTotal = franchiseRows.reduce(
        (acc, r) => ({
          local: acc.local + r.local,
          otr: acc.otr + r.otr,
          flatLabor: acc.flatLabor + r.flatLabor,
          total: acc.total + r.total,
        }),
        { local: 0, otr: 0, flatLabor: 0, total: 0 },
      );

      // Requirement: `#6765` - the Interstate row is now a bare label and the money sits on the
      // three rows beneath it. They are a DECOMPOSITION of what the old single figure bundled
      // (valuation + third-party + OTR's own fee), not additions to it — rendering both would
      // double-count, which is why the old row keeps no number.
      const interstateValuation = interstate?.valuation ?? 0;
      const interstateMargin = interstate?.margin ?? 0;
      const interstateAgency = interstate?.agency ?? 0;
      const interstateOtr = interstateValuation + interstateMargin + interstateAgency;

      // Requirement: `#6765` - unshared jobs' FP revenue is a bucket of its own. It is NOT part of
      // the three interstate rows: the margin row subtracts FP revenue out, so nothing above
      // contains it. Adding it here is additive, not double-counting — and leaving it out would
      // mean the Grand Total silently omitted money the table itself displays.
      const unattributedOtr = unattributed?.revenue ?? 0;

      // Grand total = franchise subtotal + the three interstate rows + unattributed. (No jobCount:
      // the OTR Jobs count is intentionally blank in the grand total — see grandTotalCells.)
      const grandTotal = {
        local: franchiseTotal.local,
        otr: franchiseTotal.otr + interstateOtr + unattributedOtr,
        flatLabor: franchiseTotal.flatLabor,
        total: franchiseTotal.total + interstateOtr + unattributedOtr,
      };

      const money = (value: number) => formatCurrency(Math.abs(value) < 0.005 ? 0 : value, currency);
      const count = (value: number) => String(value);

      // Requirement: `#6765` - the margin row is computed by subtracting labor from the customer
      // total, and ~40% of jobs have no labor cost recorded by the calculator at all (they predate
      // the keys; `ymm-otr-calculator.ts` is a mutable unversioned url). Cypher COALESCEs the
      // missing value to 0, so no labor is deducted for those jobs and the row comes out TOO HIGH.
      //
      // The error is systematic and one-directional, never self-cancelling, so there is no
      // threshold below which it is safe to stay quiet — any affected job triggers the note. It
      // disappears on its own once coverage is complete, which is the behaviour wanted from a
      // data-quality warning: loud while it is true, gone when it is not.
      // Requirement: `#6855` - the alerts are derived from the per-job fetch (jobsData) rather than
      // from the `groupBy: 'none'` aggregate, because a warning that cannot name its jobs makes the
      // reader hunt for them. The aggregate's own count column is still used for the margin
      // asterisk below, so the row marker and the alert agree by construction.
      const alertJobs = parseOtrAlertJobs(jobsData);

      const missingLaborJobs = interstate?.missingLaborJobs ?? 0;
      const marginIsProvisional = missingLaborJobs > 0;

      const alerts = buildOtrAlerts(alertJobs, currency);

      // All table cells are plain text. The per-job OTR report is reachable only
      // via the single "View Report" link (dropdown + info panel), which opens it
      // unscoped for the period; users can narrow it from the report page.
      const franchiseCells: SystemRevenueCell[][] = franchiseRows.map((r) => [
        r.name,
        money(r.local),
        count(r.jobCount),
        money(r.otr),
        money(r.flatLabor),
        money(r.total),
      ]);
      // Summary rows stay entirely plain (Total is cross-report; the OTR / Flat
      // Labor subtotals double-count multi-shared jobs vs a deduped drilldown).
      const subtotalCells: SystemRevenueCell[] = [
        'Franchise Total', money(franchiseTotal.local), '',
        money(franchiseTotal.otr), money(franchiseTotal.flatLabor), money(franchiseTotal.total),
      ];
      // Requirement: `#6765` - "Interstate:" carries no figures; it heads the three rows below it.
      // The job count that used to sit here was removed by request: it counted the same interstate
      // jobs the franchise rows count, so it was never additive with anything around it.
      const interstateHeadingCells: SystemRevenueCell[] = [ 'Interstate:', '', '', '', '', '' ];

      // The three components, each carrying its value in the OTR column and repeating it in Total
      // (they have no Local or Flat Labor of their own), exactly as the single Interstate row did.
      const interstateBreakdownCells: SystemRevenueCell[][] = [
        [ 'Insurance Revenue', '', '', money(interstateValuation), '', money(interstateValuation) ],
        // Marked with an asterisk while any job lacks a recorded labor cost. The asterisk keeps
        // using the ENGINE's count (calcOtrJobsMissingLaborCost) rather than the client-derived
        // alert below: it is the proven path and unchanged by #6855. The two agree because the
        // client test mirrors the engine predicate exactly — priceToCustomer present AND both
        // labor keys absent — over the same job set (same filter, same forkJoin).
        [
          `Margin on FP OTRs${ marginIsProvisional ? ' *' : '' }`,
          '', '', money(interstateMargin), '', money(interstateMargin),
        ],
        [ 'Agency & Pass-Through', '', '', money(interstateAgency), '', money(interstateAgency) ],
      ];
      // Requirement: `#6765` - interstate jobs never shared into any franchise. Their FP revenue
      // reaches no franchise row and no OTR Direct row, so before this it was simply missing from
      // the table. Carries its job count in the label because "29 jobs" and "2 jobs" call for very
      // different reactions to the same dollar figure.
      //
      // Sits with the interstate block rather than among the franchises: it is explicitly the money
      // that has NO franchise, so putting it in the franchise list would be self-contradicting.
      const unattributedCells: SystemRevenueCell[] = unattributed
        ? [
          `Partner FP — not shared with a franchise (${ unattributed.jobCount } job${ unattributed.jobCount === 1 ? '' : 's' })`,
          '', '', money(unattributed.revenue), '', money(unattributed.revenue),
        ]
        : [];

      // OTR Jobs is intentionally blank in the grand total: the franchise rows
      // and the interstate row count the SAME interstate jobs, so summing them
      // would double-count. (Money columns are different per-row components, so
      // they do sum correctly.)
      const grandTotalCells = [
        'Grand Total', money(grandTotal.local), '',
        money(grandTotal.otr), money(grandTotal.flatLabor), money(grandTotal.total),
      ];

      // The interstate row only comes back from the report when the interstate
      // zone is within the selected zone scope (mirrors System Revenue scoping).
      const showInterstate = interstate !== null;
      const showSubtotal = franchiseRows.length > 0;
      // Grand total = franchise subtotal + interstate; it only carries meaning
      // when both parts are present.
      const showGrandTotal = showInterstate && showSubtotal;

      // Display rows carry a `kind` so the template can style the summary rows.
      const rows: {
        cells: SystemRevenueCell[];
        kind: 'franchise' | 'subtotal' | 'interstate-heading' | 'interstate' | 'grandtotal';
      }[] = [
        ...franchiseCells.map((cells) => ({ cells, kind: 'franchise' as const })),
        ...(showSubtotal ? [ { cells: subtotalCells, kind: 'subtotal' as const } ] : []),
        ...(showInterstate ? [ { cells: interstateHeadingCells, kind: 'interstate-heading' as const } ] : []),
        ...(showInterstate
          ? interstateBreakdownCells.map((cells) => ({ cells, kind: 'interstate' as const }))
          : []),
        ...(showInterstate && unattributed
          ? [ { cells: unattributedCells, kind: 'interstate' as const } ]
          : []),
        ...(showGrandTotal ? [ { cells: grandTotalCells, kind: 'grandtotal' as const } ] : []),
      ];

      const hasData = showSubtotal || showInterstate;
      // Plain rows for CSV / clipboard export — cells may be link objects, so
      // extract their text (mirror the visible rows).
      const cellText = (cell: SystemRevenueCell) => typeof cell === 'string' ? cell : cell.text;
      const parsed = hasData
        ? [ headers, ...rows.map((r) => r.cells.map(cellText)) ]
        : [ headers ];

      const actions: MenuItem[] = [
        {
          label: 'Download as CSV',
          icon: 'pi pi-download',
          command: () => {
            const csv = unparse(parsed);
            const blob = new Blob([ csv ], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            const zone = this.brandingService.currentZone().value?.name || 'current zone';
            const time = dayjs().format('YYYY/MM/DD HH:mm');
            link.download = `OTR Revenue at ${ time } [${ displayPeriod }] (${ zone }).csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
          },
        },
        {
          label: 'See OTR jobs',
          icon: 'pi pi-eye',
          routerLink: [ '/reports', 'dynamic', 'new' ],
          queryParams: otrSystemRevenueReportParams,
          target: '_blank',
        },
      ];

      const copyToClipboard = () => {
        const csv = unparse(parsed, { delimiter: '\t' });
        this.clipboard.copy(csv);
        this.localNotify.success('Copied to clipboard');
      };

      return {
        actions,
        copyToClipboard,
        headers,
        rows: hasData ? rows : [],
        loading,
        error,
        isEmpty: !hasData && !loading,
        displayPeriod,
        lastReload,
        // Requirement: `#6765` - empty string when every job has a labor cost recorded, so the
        // template can render it unconditionally and it simply vanishes once the data is complete.
        // Requirement: `#6855` - suppressed with the rest of the table when there is nothing to
        // show, for the same reason the footnote was: an empty period has no data-quality story.
        alerts: hasData ? alerts : [],
      };
    },
  );

  readonly eventsViewModel$ = this.select({
    eventsCreated: this.select(
      this.eventsCreated$,
      (eventsCreated) => this.formatEventData(eventsCreated),
    ),
    createdLoading: this.eventsCreatedLoading$,
    createdEmpty: this.select(
      this.eventsCreated$,
      this.eventsCreatedLoading$,
      (created, loading) => !created.rows.length && !loading,
    ),
    eventsHappening: this.select(
      this.eventsHappening$,
      (eventsHappening) => this.formatEventData(eventsHappening),
    ),
    happeningLoading: this.eventsHappeningLoading$,
    happeningEmpty: this.select(
      this.eventsHappening$,
      this.eventsHappeningLoading$,
      (happening, loading) => !happening.rows.length && !loading,
    ),
    createdQueryParams: this.getReportQueryParams('eventsCreated'),
    happeningQueryParams: this.getReportQueryParams('eventsHappening'),
    error: this.select(
      this.eventsCreatedError$,
      this.eventsHappeningError$,
      (created, happening) => created || happening,
    ),
    eventsCreatedError: this.eventsCreatedError$,
    eventsHappeningError: this.eventsHappeningError$,
    period: this.displayPeriod$,
  });

  readonly activityViewModel$ = this.select({
    transactionsHappeningPerDay: this.select((state) => state.transactionsPaidPerDay),
    isMonthMode: this.isMonthMode$,
    period: this.unixPeriod$,
    eventsCreatedPerDay: this.select((state) => state.eventsCreatedPerDay),
    eventsHappeningPerDay: this.select((state) => state.eventsHappeningPerDay),
    transactionsPaidQueryParams: this.getReportQueryParams('transactionsPaid'),
    eventsCreatedQueryParams: this.getReportQueryParams('eventsCreatedPerDay'),
    eventsHappeningQueryParams: this.getReportQueryParams('eventsHappeningPerDay'),
    displayPeriod: this.displayPeriod$,
    loading: this.activityLoading$,
    error: this.select((state) => state.activityError),
  });

  readonly revenueViewModel$ = this.select({
    realized: this.realizedRevenue$,
    realizedRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.realizedRevenue$,
      this.pastRealizedRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    scheduled: this.scheduledRevenue$,
    scheduledRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.scheduledRevenue$,
      this.pastScheduledRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    expected: this.expectedRevenue$,
    expectedRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.expectedRevenue$,
      this.pastExpectedRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    shared: this.sharedRevenue$,
    sharedRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.sharedRevenue$,
      this.pastSharedRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    showShared: this.select(
      this.sharedRevenue$,
      this.pastSharedRevenue$,
      (current, past) => current.amount || past.amount,
    ),
    queryParams: this.getReportQueryParams('revenueAndLogistics'),
    loading: this.select((state) => state.revenueAndLogisticsLoading),
    error: this.select((state) => state.revenueAndLogisticsError),
    period: this.displayPeriod$,
  });

  readonly logisticsViewModel$ = this.select({
    distanceBooked: this.distanceBooked$,
    distanceBookedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.distanceBooked$,
      this.pastDistanceBooked$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    distanceCompleted: this.distanceCompleted$,
    distanceCompletedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.distanceCompleted$,
      this.pastDistanceCompleted$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    travelTimeBooked: this.travelTimeBooked$,
    travelTimeBookedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.travelTimeBooked$,
      this.pastTravelTimeBooked$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    travelTimeCompleted: this.travelTimeCompleted$,
    travelTimeCompletedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.travelTimeCompleted$,
      this.pastTravelTimeCompleted$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    units: this.select((state) => state.units),
    queryParams: this.getReportQueryParams('revenueAndLogistics'),
    loading: this.revenueAndLogisticsLoading$,
    error: this.select((state) => state.revenueAndLogisticsError),
    period: this.displayPeriod$,
  });

  readonly deltaViewModel$ = this.select({
    comparedToText: this.comparedToText$,
    enabled: this.deltasEnabled$,
  });

  readonly pipelineViewModel$ = this.select({
    chartData: this.pipelineChartData$,
    chartHeight: this.pipelineChartHeight$,
    chartOptions: this.pipelineChartOptions$,
    isEmpty: this.select(
      this.pipeline$,
      this.pipelineLoading$,
      (data, loading) => !data.length && !loading,
    ),
    error: this.select((state) => state.pipelineError),
    queryParams: this.getReportQueryParams('pipeline'),
    loading: this.pipelineLoading$,
  });

  readonly engineJobsViewModel$ = this.select({
    title: this.engineJobsTitle$,
    breadcrumbs: this.engineJobsBreadcrumbs$,
    chips: this.engineJobsChips$,
    currency: this.select(this.currency$, ((currency) => currency === 'All' ? baseEnvironment.defaultCurrency : currency)),
    sortBy: this.sortEngineJobsBy$,
    conversionRate: this.conversionRate$,
    jobStage: this.jobStage$,
    sections: this.engineJobSections,
    loading: this.engineJobsLoading$,
    isEmpty: this.select(
      this.engineJobsInfo$,
      this.pipelineLoading$,
      ({ jobTypes }, loading) => !jobTypes.length && !loading,
    ),
    fetchingMore: this.engineJobsFetchingMore$,
  });

  /**
   * EFFECTS
   */

  /**
   * When demo mode is active, patch all revenue-tab state with fixture data
   * whenever the revenue data trigger fires (period / zone / currency changes).
   * The trigger carries start/end/currency/zone so fixtures scale to the selected period.
   *
   * Uses the same combined stream as the live-fetch effects so that toggling
   * demo mode cancels any in-flight live request and immediately restores the
   * correct data in either direction.
   */
  readonly applyDemoRevenueFixtures = this.effect(() =>
    combineLatest([
      this.reloadDataTrigger$,
      this.demoModeService.isDemo$,
    ]).pipe(
      switchMap(([ trigger, isDemo ]) => isDemo
        ? of(null).pipe(tap(() => this.patchState(getDemoRevenuePatch(trigger))))
        : EMPTY,
      ),
    ),
  );

  /**
   * When demo mode is active, patch pipeline state with fixture data
   * whenever the pipeline trigger fires.  The trigger carries period, currency,
   * groupLeadsBy, and active filters so the pipeline responds to all controls.
   *
   * Uses the same combined stream as the live-fetch effects so that toggling
   * demo mode cancels any in-flight live request and immediately restores the
   * correct data in either direction.
   */
  readonly applyDemoPipelineFixtures = this.effect(() =>
    combineLatest([
      this.reloadPipelineTrigger$,
      this.demoModeService.isDemo$,
    ]).pipe(
      switchMap(([ trigger, isDemo ]) => isDemo
        ? of(null).pipe(tap(() => this.patchState(getDemoPipelinePatch(trigger))))
        : EMPTY,
      ),
    ),
  );

  readonly reloadAverageInvoiceSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageInvoiceSize',
    countKey: 'invoiceSizeCount',
    totalKey: 'invoiceSizeTotal',
    loadingKey: 'averageInvoiceSizeLoading',
    errorKey: 'averageInvoiceSizeError',
  });

  readonly reloadPastAverageInvoiceSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageInvoiceSize',
    countKey: 'pastInvoiceSizeCount',
    totalKey: 'pastInvoiceSizeTotal',
    loadingKey: 'pastAverageInvoiceSizeLoading',
    errorKey: 'averageInvoiceSizeError',
  });

  readonly reloadAverageJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageJobSize',
    countKey: 'jobSizeCount',
    totalKey: 'jobSizeTotal',

    loadingKey: 'averageJobSizeLoading',
    errorKey: 'averageJobSizeError',
  });

  readonly reloadPastAverageJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageJobSize',
    countKey: 'pastJobSizeCount',
    totalKey: 'pastJobSizeTotal',
    loadingKey: 'pastAverageJobSizeLoading',
    errorKey: 'averageJobSizeError',
  });

  readonly reloadAverageBookingSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageBookingSize',
    countKey: 'bookingSizeCount',
    totalKey: 'bookingSizeTotal',
    loadingKey: 'averageBookingSizeLoading',
    errorKey: 'averageBookingSizeError',
  });

  readonly reloadPastAverageBookingSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageBookingSize',
    countKey: 'pastBookingSizeCount',
    totalKey: 'pastBookingSizeTotal',
    loadingKey: 'pastAverageBookingSizeLoading',
    errorKey: 'averageBookingSizeError',
  });

  readonly reloadAverageScheduledJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageScheduledJobSize',
    countKey: 'scheduledJobSizeCount',
    totalKey: 'scheduledJobSizeTotal',
    loadingKey: 'averageScheduledJobSizeLoading',
    errorKey: 'averageScheduledJobSizeError',
  });

  readonly reloadPastAverageScheduledJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageScheduledJobSize',
    countKey: 'pastScheduledJobSizeCount',
    totalKey: 'pastScheduledJobSizeTotal',
    loadingKey: 'pastAverageScheduledJobSizeLoading',
    errorKey: 'pastAverageScheduledJobSizeError',
  });

  readonly reloadPipeline = this.effect(() => combineLatest([
    this.reloadPipelineTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => isDemo ? EMPTY : of(trigger)),
    map(({
      currency,
      start,
      end,
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      groupLeadsBy,
      jobUpdated,
      pipelinePeriod,
    }) => ({
      queryVars: this.getQueryVars({
        start,
        end,
        currency,
        report: 'pipeline',
        opts: {
          selectedJobType: {
            source,
            jobOrigin,
            estimateMethod,
            estimator,
            salesAgent,
            utmSource,
            utmMedium,
            utmCampaign,
            utmContent,
            utmTerm,
          },
          groupLeadsBy,
          pipelinePeriod,
        }
      }),
      jobUpdated,
    })),
    tap(() => this.patchState({
      pipelineLoading: true,
      pipelineError: null,
    })),
    switchMap(({
      queryVars,
      jobUpdated,
    }) => this.previewReportWithConfigsGQL.fetch(queryVars, { fetchPolicy: jobUpdated ? 'network-only' : 'cache-first' }).pipe(tapResponse({
      next: (res) => {
        this.patchState(() => ({
          pipeline: res.data.previewReport.data.rows,
          jobClosureReasons: getJobClosureReasons(res.data.closureReasonsAndAttentionExplanations),
          attentionExplanations: getAttentionExplanations(res.data.closureReasonsAndAttentionExplanations),
          pipelineLoading: false,
          lastReload: currentTimeSeconds(),
        }));
      },
      error: (err: ApolloError) => {
        this.patchState({
          pipelineLoading: false,
          pipelineError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadEngineJobs = this.effect(() => combineLatest([
    this.reloadEngineJobsTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        // Clear any cached live engine-job data so it cannot leak under the demo banner.
        this.patchState({
          engineJobs: {},
          engineJobsHasMore: false,
          engineJobsLoading: false,
          engineJobsError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    tap(() => this.patchState({
      engineJobsLoading: true,
      engineJobsError: null,
    })),
    switchMap((fetchEngineJobsInput) => this.fetchEngineJobs(fetchEngineJobsInput).pipe(
      tapResponse({
          next: (res) => {
            this.patchState({
              engineJobs: res.data,
              engineJobsLoading: false,
              lastReload: currentTimeSeconds(),
              engineJobsHasMore: this.getEngineJobsHasMore(res.data, fetchEngineJobsInput.jobTypes),
            });
          },
          error: (err: ApolloError) => {
            this.patchState({
              engineJobsLoading: false,
              engineJobsError: getDashboardErrorMessage(err),
            });
          },
        }),
    )),
  ));

  readonly fetchMoreEngineJobs = this.effect(() => this.bottomReached$.pipe(
    debounceTime(150),
    filter(() => !this.demoModeService.isDemo),
    withLatestFrom(this.reloadEngineJobsTrigger$, this.engineJobsHasMore$, this.engineJobs$),
    filter(([ path, { selectedJobType }, hasMore ]) => {

      if (path !== 'engine' || !hasMore) {
        return false;
      }
      return Boolean(selectedJobType.attentionExplanation || selectedJobType.jobClosureReason);
    }),
    tap(() => this.patchState({
      engineJobsFetchingMore: true,
      engineJobsError: null,
    })),
    map(([ _path, triggerData, _hasMore, currentJobs ]) => {

      const { selectedJobType: {
        jobStage,
        status,
        jobClosureReason,
        attentionExplanation,
      } } = triggerData;

      const keyBasis = [ jobStage, status ] as string[];

      if (status === 'open') {
        keyBasis.push(attentionExplanation);
      } else {
        keyBasis.push(jobClosureReason);
      }

      const key = keyBasis.join(delimiter);

      const graphQLKey = encodeKeyForGraphQL(key);

      const rows = currentJobs[graphQLKey]?.data?.rows || [];

      return {
        queryVars: {
          ...triggerData,
          skip: rows.length,
        },
        graphQLKey,
        currentJobs,
      }
    }),
    switchMap(({ queryVars, currentJobs, graphQLKey }) => this.fetchEngineJobs(queryVars).pipe(tapResponse({
      next: (res) => {
        this.patchState({
          engineJobs: mergeJobs({
            currentJobs,
            newJobs: res.data,
            graphQLKey,
          }),
          engineJobsFetchingMore: false,
          engineJobsHasMore: !((res.data[graphQLKey]?.data?.rows || []).length < ENGINE_JOBS_QUERY_LIMIT),
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          engineJobsFetchingMore: false,
          engineJobsError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  // TODO(demo-fixtures): The sales leaderboard skips the live fetch under demo
  // mode but does not yet have demo fixtures in `demo-fixtures.ts`. Until they
  // are added, the leaderboard table will render empty in demo mode. Add a
  // `getDemoSalesLeaderboardPatch` and wire it through `applyDemoSalesLeaderboardFixtures`
  // (mirroring `applyDemoPipelineFixtures`).
  readonly reloadSystemRevenue = this.effect(() => combineLatest([
    this.reloadSystemRevenueTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          systemRevenue: { headers: [], rows: [] },
          systemRevenueLoading: false,
          systemRevenueError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end }) => this.getQueryVars({
      start,
      end,
      currency,
      report: 'systemRevenue',
    })),
    tap(() => this.patchState({
      systemRevenueLoading: true,
      systemRevenueError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          systemRevenue: {
            headers: headers.length ? headers : placeholderSystemRevenueHeaders,
            rows,
          },
          systemRevenueLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          systemRevenueLoading: false,
          systemRevenueError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadOtrRevenue = this.effect(() => combineLatest([
    this.reloadOtrRevenueTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          otrRevenue: { headers: [], rows: [] },
          otrInterstate: { headers: [], rows: [] },
          otrLocalRevenue: { headers: [], rows: [] },
          otrJobs: { headers: [], rows: [] },
          otrRevenueLoading: false,
          otrRevenueError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    tap(() => this.patchState({
      otrRevenueLoading: true,
      otrRevenueError: null,
    })),
    switchMap(({ start, end, currency }) => forkJoin({
      // Franchise rows — calculator OTR / Flat Labor on interstate jobs shared
      // into each franchise, dated by job completion.
      otr: this.dataOnlyGQL.fetch(this.getQueryVars({ start, end, currency, report: 'otrFranchise' })),
      // Interstate row — OTR Margin aggregate over interstate-owned jobs.
      interstate: this.dataOnlyGQL.fetch(this.getQueryVars({ start, end, currency, report: 'otrInterstate' })),
      // Local — the canonical Internal Revenue dynamic report (matches System Revenue).
      local: this.dataOnlyGQL.fetch(this.getQueryVars({ start, end, currency, report: 'systemRevenue' })),
      // Requirement: `#6855` - one row per interstate job, for the data-quality alerts under the
      // table. Joins the same forkJoin rather than loading separately so the alerts can never
      // describe a different period than the figures they annotate.
      alertJobs: this.dataOnlyGQL.fetch(this.getQueryVars({ start, end, currency, report: 'otrAlertJobs' })),
    }).pipe(tapResponse({
      next: ({ otr, interstate, local, alertJobs }) => {
        this.patchState({
          otrRevenue: otr.data.previewReport.data,
          otrInterstate: interstate.data.previewReport.data,
          otrLocalRevenue: local.data.previewReport.data,
          otrJobs: alertJobs.data.previewReport.data,
          otrRevenueLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          otrRevenueLoading: false,
          otrRevenueError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadEventsCreated = this.effect(() => combineLatest([
    this.reloadDataTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => isDemo ? EMPTY : of(trigger)),
    map(({ currency, start, end }) => this.getQueryVars({ start, end, currency, report: 'eventsCreated' })),
    tap(() => this.patchState({
      eventsCreatedLoading: true,
      eventsCreatedError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          eventsCreated: { headers, rows },
          eventsCreatedLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          eventsCreatedLoading: false,
          eventsCreatedError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadEventsHappening = this.effect(() => combineLatest([
    this.reloadDataTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => isDemo ? EMPTY : of(trigger)),
    map(({ currency, start, end }) => this.getQueryVars({ start, end, currency, report: 'eventsHappening' })),
    tap(() => this.patchState({
      eventsHappeningLoading: true,
      eventsHappeningError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { rows, headers } = res.data.previewReport.data;

        this.patchState({
          eventsHappening: {
            headers: headers.length ? headers : placeholderEventHeaders,
            rows,
          },
          eventsHappeningLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          eventsHappeningLoading: false,
          eventsHappeningError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadActivity = this.effect(() => combineLatest([
    this.reloadDataTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => isDemo ? EMPTY : of(trigger)),
    tap(() => this.patchState({
      activityLoading: true,
      activityError: null,
    })),
    switchMap(({ start, end, currency }) => this.fetchActivity({ start, end, currency }).pipe(tapResponse({
      next: (res) => {
        this.patchState({
          transactionsPaidPerDay: res.data.transactionsPaid.data.rows,
          eventsCreatedPerDay: res.data.eventsCreated.data.rows,
          eventsHappeningPerDay: res.data.eventsHappening.data.rows,
          activityLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          activityLoading: false,
          activityError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadRevenueAndLogistics = this.effect(() => combineLatest([
    this.reloadDataTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => isDemo ? EMPTY : of(trigger)),
    map(({ currency, start, end }) => this.getQueryVars({
      start,
      end,
      currency,
      report: 'revenueAndLogistics',
    })),
    tap(() => this.patchState({
      revenueAndLogisticsLoading: true,
      revenueAndLogisticsError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(
      map((res) => getRevenueAndLogisticsTotals(res)),
      tapResponse({
        next: ({
          realizedRevenue,
          scheduledRevenue,
          expectedRevenue,
          sharedRevenue,
          distanceBooked,
          distanceCompleted,
          travelTimeBooked,
          travelTimeCompleted,
        }) => this.patchState({
          realizedRevenue,
          scheduledRevenue,
          expectedRevenue,
          sharedRevenue,
          distanceBooked,
          distanceCompleted,
          travelTimeBooked,
          travelTimeCompleted,
          revenueAndLogisticsLoading: false,
          lastReload: currentTimeSeconds(),
        }),
        error: (err: ApolloError) => this.patchState({
          revenueAndLogisticsLoading: false,
          revenueAndLogisticsError: getDashboardErrorMessage(err),
        }),
      }),
    )),
  ));

  readonly reloadPastRevenueAndLogistics = this.effect(() => combineLatest([
    this.reloadPastDataTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => isDemo ? EMPTY : of(trigger)),
    map(({ currency, start, end }) => this.getQueryVars({
      start,
      end,
      currency,
      report: 'revenueAndLogistics',
    })),
    tap(() => this.patchState({ pastRevenueAndLogisticsLoading: true })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(
      map((res) => getRevenueAndLogisticsTotals(res)),
      tapResponse({
        next: ({
          realizedRevenue: pastRealizedRevenue,
          scheduledRevenue: pastScheduledRevenue,
          expectedRevenue: pastExpectedRevenue,
          sharedRevenue: pastSharedRevenue,
          distanceBooked: pastDistanceBooked,
          distanceCompleted: pastDistanceCompleted,
          travelTimeBooked: pastTravelTimeBooked,
          travelTimeCompleted: pastTravelTimeCompleted,
        }) => this.patchState({
          pastRealizedRevenue,
          pastScheduledRevenue,
          pastExpectedRevenue,
          pastSharedRevenue,
          pastDistanceBooked,
          pastDistanceCompleted,
          pastTravelTimeBooked,
          pastTravelTimeCompleted,
          pastRevenueAndLogisticsLoading: false,
          lastReload: currentTimeSeconds(),
        }),
        error: (err: ApolloError) => this.patchState({
          revenueAndLogisticsLoading: false,
          revenueAndLogisticsError: getDashboardErrorMessage(err),
        }),
      }),
    )),
  ));

  readonly resetCustomPeriod = this.effect(() => this.resetCustomPeriodTrigger$.pipe(
    tap((input: ExtractObservableType<typeof this.resetCustomPeriodTrigger$>) => {
      const { month, period, timezone } = input;
      if (period === 'Month of Year') {
        const monthDayJs = dayjs.tz(month, 'MMMM YYYY', timezone);
        this.setCustomPeriod([
          monthDayJs.startOf('month').format('YYYY-MM-DD'),
          monthDayJs.endOf('month').format('YYYY-MM-DD'),
        ]);
      } else {
        const unixPeriod = this.periodService.getUnixPeriod(period, timezone);
        this.setCustomPeriod([
          dayjs.tz(unixPeriod.start * 1000, timezone).format('YYYY-MM-DD'),
          dayjs.tz(unixPeriod.end * 1000, timezone).format('YYYY-MM-DD'),
        ]);
      }
    }),
  ));

  readonly resetTicks = this.effect(() => this.activityViewModel$.pipe(
    tap((activity: ExtractObservableType<typeof this.activityViewModel$>) => {

      const ticks = transactionVolumeChartOptions.scales.y.ticks;

      const allValues = [
        ...activity.transactionsHappeningPerDay.map(([ _label, value ]) => parseFloat(value)),
        ...activity.eventsCreatedPerDay.map(([ _label, value ]) => parseFloat(value)),
        ...activity.eventsHappeningPerDay.map(([ _label, value ]) => parseFloat(value)),
      ];

      const min = Math.min(...allValues);
      const max = Math.max(...allValues);

      const finiteMin = isFinite(min) ? min : 1;
      const finiteMax = isFinite(max) ? max : 1;

      const nSteps = 4;

      // @ts-ignore, Doesn't match the updated type for v3 but seems to be backwards compatible
      ticks.min = finiteMin;

      // @ts-ignore, Doesn't match the updated type for v3 but seems to be backwards compatible
      ticks.stepSize = ((finiteMax - finiteMin) || 1) / nSteps;
    }),
  ));

  readonly resetUnits = this.effect(() => this.branding.currentBranding.asObservable().pipe(
    map((branding) => {

      const countryCode = branding.country || environment.defaultCountry;
      const country = jurisdictions.find((j) => j.country === countryCode);
      return country?.units || environment.defaultUnits;
    }),
    tap(( units: DistanceUnit ) => this.patchState({ units })),
  ));

  readonly setQueryParams = this.effect(() => this.setQueryParamsTrigger$.pipe(
    skip(1),
    tap((input: ExtractObservableType<typeof this.setQueryParamsTrigger$>) => {

      const queryParams: Params = {};

      const inputKeys: (keyof typeof input)[] = [
        'currency',
        'period',
        'groupLeadsBy',
        'metric',
        'pipelinePeriod',
        'jobStage',
        'status',
        'source',
        'jobOrigin',
        'estimateMethod',
        'jobClosureReason',
        'attentionExplanation',
        'estimator',
        'salesAgent',
        'utmSource',
        'utmMedium',
        'utmCampaign',
        'utmContent',
        'utmTerm',
        'role',
        'salesLeaderboardSortKey',
        'salesLeaderboardSortDesc',
      ];

      for (const key of inputKeys) {
        const val = input[key];
        if (val != null && val !== '') {
          queryParams[key] = typeof val === 'boolean' ? String(val) : String(val).replaceAll(' ', '-');
        }
      }

      if (input.period === 'Month of Year') {
        queryParams.month = input.month.replaceAll(' ', '-');
      } else if (input.period === 'Custom') {
        queryParams.customPeriod = input.customPeriod.join(';');
      }

      if (input.period === 'Month of Year' || input.period === 'MTD') {
        queryParams.comparedTo = input.comparedTo.replaceAll(' ', '-');
      }

      const searchParams = new URLSearchParams(queryParams).toString();

      const newUrl = `${location.pathname}?${searchParams}`;

      history.replaceState(null, '', newUrl);
    }),
  ));

  readonly storeComparedToInLocalStorage = this.effect(() => this.comparedTo$.pipe(
    skip(1),
    tap((comparedTo: ExtractObservableType<typeof this.comparedTo$>) => {
      localStorage.setItem(comparedToLsKey, comparedTo);
    }),
  ));

  getReloadAverageSizeEffect(input: {
    trigger$: Observable<any>;
    report: DashboardReport;
    loadingKey: KeyOfEndingWith<DashboardState, 'Loading'>;
    errorKey: KeyOfEndingWith<DashboardState, 'Error'>;
    countKey: KeyOfEndingWith<DashboardState, 'Count'>;
    totalKey: KeyOfEndingWith<DashboardState, 'Total'>;
  }
  ) {

    const {
      trigger$,
      report,
      loadingKey,
      errorKey,
      countKey,
      totalKey,
    } = input;

    return this.effect(() => combineLatest([
      trigger$,
      this.demoModeService.isDemo$,
    ]).pipe(
      switchMap(([ t, isDemo ]) => isDemo ? EMPTY : of(t)),
      map(({ currency, start, end }) => this.getQueryVars({ start, end, currency, report })),
        tap(() => this.patchState({ [loadingKey]: true })),
        switchMap((queryVars) => this.aggregationDataOnlyGQL.fetch(queryVars).pipe(
          map((res) => getCountAndTotal(res)),
          tapResponse({
            next: ({ count, total }) => this.patchState({
              [loadingKey]: false,
              [countKey]: count,
              [totalKey]: total,
              lastReload: currentTimeSeconds(),
            }),
            error: (err: ApolloError) => this.patchState({
              [loadingKey]: false,
              [errorKey]: getDashboardErrorMessage(err),
            }),
          }),
      )),
    ));

  }

  /**
   * Returns query variables for any report that serves as a data source for the dashboard.
   * Used by both dashboard widgets and their respective links to the underlying report,
   * ensuring that the link always points to the same data as the widget.
   */
  getQueryVars(input: {
    start: number;
    end: number;
    currency: string;
    report: DashboardReport;
    opts?: {
      sortBy?: string;
      selectedJobType?: SelectedJobType;
      groupLeadsBy?: GroupLeadsBy;
      skip?: number;
      pipelinePeriod?: PipelinePeriod;
      role?: SalesLeaderboardRole;
      salesLeaderboardSortKey?: SalesLeaderboardSortKey;
      salesLeaderboardSortDesc?: boolean;
    },
  }): PreviewReportQueryVariables {

    const { start, end, currency, report } = input;

    switch (report) {
      case 'averageInvoiceSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency),
          periodType: 'invoiceFinalizedAt',
          groupBy: 'invoice',
          dataColumns: [ ],
          aggregationColumns: [
            'invoiceDiscountedSubTotalSum',
            'invoiceCount',
            'invoiceDiscountedSubTotalAvg',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        }
      case 'averageJobSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            charge: { excludeDamages: true },
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'job',
          dataColumns: [ ],
          aggregationColumns: [
            'discountedSubTotalSum',
            'count',
            'discountedSubTotalAvg',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        }
      case 'averageBookingSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: {
              getJobsOfState: [ 'open', 'closed' ],
              getJobsOfStage: [ 'booking', 'invoice' ],
            },
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'jobBookingStageDate',
          groupBy: 'job',
          dataColumns: [ ],
          aggregationColumns: [
            'discountedSubTotalSum',
            'nonZeroCount',
            'discountedSubTotalNonZeroAvg',
            'count',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        }
      case 'averageScheduledJobSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: {
              getJobsOfState: [ 'open', 'closed' ],
              getJobsOfStage: [ 'booking', 'invoice' ],
            },
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventStart',
          groupBy: 'job',
          dataColumns: [ ],
          aggregationColumns: [
            'discountedSubTotalSum',
            'nonZeroCount',
            'discountedSubTotalNonZeroAvg',
            'count',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        }
      case 'eventsCreated':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventCreatedAt',
          groupBy: 'eventType',
          dataColumns: [
            'eventType',
            'discountedSubTotal',
            'eventCount',
            'averageDiscountedSubTotalPerEvent',
            'currency',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      case 'eventsHappening':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventStart',
          groupBy: 'eventType',
          dataColumns: [
            'eventType',
            'discountedSubTotal',
            'eventCount',
            'averageDiscountedSubTotalPerEvent',
            'currency',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
      };
      case 'systemRevenue':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventEnd',
          groupBy: 'franchise',
          dataColumns: [ ...systemRevenueDataColumns ],
          aggregationColumns: [],
          performance: 'skip-count',
          // 'sheets' returns currency cells as raw numeric strings so totals
          // can be summed without stripping symbols; the view model formats
          // them client-side via `formatCurrency`.
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
          sort: 'franchise ASC',
        };
      // OTR Revenue dashboard — franchise rows: calculator OTR/Flat Labor on
      // jobs shared INTO each franchise, grouped by the receiving franchise and
      // dated by the job's last scheduled event end (so a job appears in the
      // period of its final scheduled event regardless of completion status —
      // not-yet-completed booked/confirmed jobs are included). NOTE: we
      // deliberately do NOT filter by
      // franchiseIds=[interstate] — in the shared UNION path the engine's
      // franchiseIds predicate matches the RECEIVING zone (never under
      // interstate), so it would drop every shared row. The OTR/Flat Labor money
      // is already interstate-only because only OTR-calculator jobs carry
      // fpMargin / laborSubcontractorPortion (others COALESCE to 0).
      case 'otrFranchise':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          // Requirement: `#6517` - stays 'shared' only: that keeps jobs with no split on today's
          // receiving-franchise attribution, and the engine adds the 'fp-split' rows itself when
          // `calcOtrFpSplitRevenue` is requested (the value is internal and not accepted by the
          // enum-validated getEventsOfSharedStatus filter).
          filter: mergeInDefaultFilters(currency, {
            job: { getJobsOfStage: OTR_BOOKED_JOB_STAGES },
            event: { getEventsOfSharedStatus: [ 'shared' ] },
          }),
          periodType: 'lastScheduledWorkEventEndDate',
          groupBy: 'franchise',
          // calcOtrFpSplitRevenue = split-first with shared/owner fallback (replaces the plain
          // calcOtrTotalJobRevenueFp as the dashboard's FP-revenue number; the old column stays defined).
          dataColumns: [ 'franchise', 'franchiseId', 'calcOtrFpSplitRevenue', 'calcOtrLaborSubcontractorPortion', 'jobCount' ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
          sort: 'franchise ASC',
        };
      // OTR Revenue dashboard — interstate row: a single "Group All" aggregate of
      // OTR Margin over interstate-owned jobs, dated by the job's last scheduled
      // event end (regardless of completion status). groupBy 'none' (and no
      // franchise/shared columns) avoids the shared-revenue split, so each job is
      // counted exactly once. Mirrors the 7000.0 interstate branch.
      case 'otrInterstate':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: {
              franchiseIds: [ OTR_INTERSTATE_ZONE_ID ],
              getJobsOfStage: OTR_BOOKED_JOB_STAGES,
            },
          }),
          periodType: 'lastScheduledWorkEventEndDate',
          groupBy: 'none',
          // Requirement: `#6765` - every figure below the franchise rows comes from THIS ONE query.
          // The Interstate row lost its number and became a label; the three rows under it, the
          // OTR Direct row and the Unattributed row are all aggregates over the same interstate-owned
          // job set, so they ride here as extra columns rather than as new queries. The engine has
          // already collected the jobs for `calcOtrMargin`; each extra column is arithmetic over that
          // same collection — no extra traversal and no extra round trip.
          //
          // `calcOtrMargin` is kept deliberately although nothing renders it any more: it is the
          // figure the Interstate row used to show, so keeping it available makes the before/after
          // comparison possible while this is being verified. Remove it once that is done — and note
          // it is NOT a trustworthy baseline: `totalSubcontractors` is missing on ~40% of jobs, and
          // COALESCE turns that into "the whole customer total is margin", inflating it heavily.
          dataColumns: [
            'calcOtrMargin',
            'jobCount',
            // The "Interstate:" decomposition.
            'calcOtrInsuranceValuation',
            'calcOtrMarginOnFpOtrs',
            'calcOtrAgencyPayout',
            // Footnote inputs for the margin row (jobs with no labor cost recorded at all).
            'calcOtrJobsMissingLaborCost',
            'calcOtrJobsWithCalculatorOutput',
            // The OTR Direct franchise-style row.
            'calcOtrDirectFpRevenue',
            'calcOtrDirectLabor',
            'calcOtrDirectJobCount',
            // Interstate jobs never shared into any franchise.
            'calcOtrUnattributedFpRevenue',
            'calcOtrUnattributedJobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      // Requirement: `#6855` - the per-job set behind the data-quality alerts under the table.
      // Filter is byte-identical to 'otrSystemRevenue' below, deliberately: the alerts annotate the
      // dashboard's own figures, so they must be computed over exactly the job set the dashboard
      // counts. If one filter changes, change both — an alert naming a job the table never counted
      // (or missing one it did) is worse than no alert.
      //
      // NO columnFormats and linkFormat 'web' — both load-bearing, see OTR_ALERT_COLUMNS.
      case 'otrAlertJobs':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: {
              franchiseIds: [ OTR_INTERSTATE_ZONE_ID ],
              getJobsOfStage: OTR_BOOKED_JOB_STAGES,
            },
          }),
          periodType: 'lastScheduledWorkEventEndDate',
          groupBy: 'job',
          dataColumns: [ ...OTR_ALERT_COLUMNS ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'web',
          currencySource: currency === 'All' ? 'zone' : undefined,
          sort: 'lastScheduledEventEndDateTime DESC',
        };
      // Drill-down link for the OTR / Flat Labor / Interstate columns: one
      // row per job with the full set of raw per-job OTR calculator values behind
      // the dashboard aggregates (via the calculator_<key> "Add Calculator Column"
      // mechanism, allowed for groupBy: 'job'). Link-only; not fetched here.
      // Base filter = interstate-owned jobs (the canonical OTR job set, reconciles
      // with the Interstate row). The franchise-scoped links override this with
      // { jobFranchises, eventSharedStatuses: ['shared'] } to show jobs shared INTO
      // a franchise. Headers auto-title-case to the calculator's own field titles.
      case 'otrSystemRevenue':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: {
              franchiseIds: [ OTR_INTERSTATE_ZONE_ID ],
              getJobsOfStage: OTR_BOOKED_JOB_STAGES,
            },
          }),
          periodType: 'lastScheduledWorkEventEndDate',
          groupBy: 'job',
          dataColumns: [ ...OTR_SYSTEM_REVENUE_COLUMNS ],
          // Raw calculator_* columns are unformatted by default; flag them all as
          // currency so the opened report renders e.g. $525.18, not 525.1822…
          columnFormats: OTR_SYSTEM_REVENUE_CALC_COLUMNS.map((column) => ({ column, format: 'currency' })),
          // Requirement: `#6538` - calculator_* headers are auto-title-cased from the key, which
          // turns crewPayPaidToFp into "Crew Pay Paid To Fp". Name these two explicitly.
          customColumnNames: [
            { column: 'calculator_laborSubcontractorPortion', name: 'Flat Labor (FP)' },
            { column: 'calculator_crewPayPaidToFp', name: 'Labor Forwarded to FP' },
            // Requirement: `#6620` - same auto-title-casing problem: these would otherwise read
            // "Otr Revenue" and "Job Breakdown_third Party". Named to match the calculator's own
            // on-screen summary ("OTR Fees:" / "Third Party:") so the report and the Job page use
            // one vocabulary for the same four buckets.
            { column: 'calculator_otrRevenue', name: 'OTR Fees' },
            { column: 'calculator_jobBreakdown_thirdParty', name: 'Third Party' },
            { column: 'calculator_thirdPartyAgencyPayout', name: 'Outside Agency Payout' },
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
          sort: 'lastScheduledEventEndDateTime DESC',
        };
      // Requirement: #6598 - drill-down link for the OTR - Labor Tracking dashboard:
      // the same one-row-per-(job × crew member) crew-pay set the dashboard shows, opened
      // in the report viewer so it can be saved, scheduled or exported to Sheets.
      // Link-only; the dashboard fetches its own table (component.buildQueryVars).
      //
      // Deliberately WITHOUT the dashboard's calculator_* rate/crew-size columns: those
      // are admitted for jobCrewMember only via a schema pattern and are not enumerated by
      // getDataColumns, so the viewer would render them with mangled headers
      // ("FpLabor_crew Lead"), unformatted, and absent from the column picker. The rates
      // they carry are already visible in the dashboard's CSV export instead.
      //
      // No columnFormats needed: every crewPay* column is in the engine's CURRENCY_COLS.
      // Note the report shows PERSISTED crewPay values, while the dashboard recomputes
      // fees/total from live fpLabor_* rates — the two agree only after a Save + recompute.
      case 'otrLaborTracking':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          // Restrict to interstate-owned OTR jobs (mirrors the dashboard's own query).
          filter: mergeInDefaultFilters('All', {
            job: {
              franchiseIds: [ OTR_INTERSTATE_ZONE_ID ],
              // Requirement: `#6751` - same committed-work stage filter as OTR - System Revenue.
              // Without it this report showed crew pay for leads, estimates and cancelled jobs
              // that merely had a scheduled event in the window — rows nobody is owed anything
              // for, sitting on a sheet whose purpose is authorising payment.
              getJobsOfStage: OTR_BOOKED_JOB_STAGES,
            },
          }),
          // Requirement: `#6855` - same period rule as OTR - System Revenue, for the same reason
          // #6751 gave this report the stage filter above: a sheet that authorises payment should
          // not list work that was cancelled. Must stay identical to the dashboard's own fetch in
          // dashboard-otr-labor-tracking.component.ts (buildQueryVars) — this case is what the
          // "See crew pay rows" link opens, and a drill-down showing a different job set from the
          // table it drills into is worse than no drill-down.
          periodType: 'lastScheduledWorkEventEndDate',
          groupBy: 'jobCrewMember',
          dataColumns: [ ...OTR_LABOR_DATA_COLUMNS ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: null,
        };
      case 'salesLeaderboard': {
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        const groupBy = salesLeaderboardRoleToGroupBy[role];
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          // Requirement: Sales Leaderboard future visibility — the main query builds the row set
          // (the list of agents). Using only ['completed'] excluded agents whose only events in
          // the period are booked/confirmed (future or not-yet-completed), making them invisible
          // on the leaderboard even though they had scheduled work. Widened to include booked
          // and confirmed so agents with upcoming jobs appear. The discountedSubTotal column
          // from this query is overridden in the store (see dashboard-sales-leaderboard.store.ts)
          // by the salesLeaderboardBooked query, so revenue is not inflated by the wider filter.
          filter: mergeInDefaultFilters(currency, {
            ...estimatorNoShowExclusion(role),
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventEnd',
          groupBy,
          dataColumns: [ roleCol, ...SALES_LEADERBOARD_DATA_COLUMNS ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'discountedSubTotal DESC',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      case 'salesLeaderboardLeads': {
        // Count of ALL jobs created in the period per agent — no event or
        // comms_connected filter so the number is a true "leads in the month."
        // Requirement: ticket 7279 - Net Conversion's top number (connected qualified bookings) comes from the
        // same jobs, so it can't pass 100%: of the leads created in the period, those reached and now booked.
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        const groupBy = salesLeaderboardRoleToGroupBy[role];
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            ...estimatorNoShowExclusion(role),
            event: { getEventsOfStatus: [] },
          }),
          periodType: 'jobCreatedAt',
          groupBy,
          dataColumns: [ roleCol, ...SALES_LEADERBOARD_LEADS_DATA_COLUMNS ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      case 'salesLeaderboardCancellation': {
        // Cancellation rate: events booked in the period across all statuses.
        // The main salesLeaderboard query only sees completed events, so
        // cancelledEventCount is always 0 there. This separate query uses
        // periodType:eventBookedAt with all statuses so we get a real count
        // of both total booked events and how many are now cancelled.
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        const groupBy = salesLeaderboardRoleToGroupBy[role];
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            // Requirement: `#7020` - drops no-shows from both the cancelled count and the total.
            ...estimatorNoShowExclusion(role),
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed', 'cancelled' ],
              getEventsOfSharedStatus: [ 'not-shared' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventBookedAt',
          groupBy,
          dataColumns: [ roleCol, 'eventCount', 'cancelledEventCount' ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      // Requirement: ticket 7056 review - the Revenue, Estimated, Booked and booked-count queries return one row
      // per person, not per job: per-job rows timed out on large zones over long periods (Estimator / YTD), and
      // each person's sums and job counts come out the same.
      case 'salesLeaderboardRevenue': {
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            ...estimatorNoShowExclusion(role),
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
              getEventsOfSharedStatus: [ 'not-shared' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventEnd',
          groupBy: salesLeaderboardRoleToGroupBy[role],
          dataColumns: [ roleCol, ...SALES_LEADERBOARD_REVENUE_DATA_COLUMNS ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'discountedSubTotal DESC',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      case 'salesLeaderboardEstimated': {
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            ...estimatorNoShowExclusion(role),
            event: {
              // Include all event statuses so jobs freshly estimated (events still
              // in required/pending) show their quoted revenue, not $0.
              getEventsOfStatus: [ 'required', 'pending', 'booked', 'confirmed', 'completed' ],
              getEventsOfSharedStatus: [ 'not-shared' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'jobEstimateStageDate',
          groupBy: salesLeaderboardRoleToGroupBy[role],
          dataColumns: [ roleCol, ...SALES_LEADERBOARD_ESTIMATED_DATA_COLUMNS ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'discountedSubTotal DESC',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      case 'salesLeaderboardBooked': {
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            ...estimatorNoShowExclusion(role),
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
              getEventsOfSharedStatus: [ 'not-shared' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'jobBookingStageDate',
          groupBy: salesLeaderboardRoleToGroupBy[role],
          dataColumns: [ roleCol, ...SALES_LEADERBOARD_BOOKED_DATA_COLUMNS ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'discountedSubTotal DESC',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      case 'salesLeaderboardBookedByEstimate': {
        // Requirement: ticket 7056 - Booked and Quote-to-close count jobs by the month they were
        // ESTIMATED: estimated in the period and now in the booking or invoice stage. Same events and
        // columns as salesLeaderboardBookedCount, which stays on the booking date for AJS.
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: { getJobsOfStage: BOOKED_JOB_STAGES, ...estimatorNoShowExclusion(role).job },
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
              getEventsOfSharedStatus: [ 'not-shared' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'jobEstimateStageDate',
          groupBy: salesLeaderboardRoleToGroupBy[role],
          dataColumns: [ roleCol, 'jobCount' ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'jobCount DESC',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      case 'salesLeaderboardBookedCount': {
        const role = input.opts?.role ?? 'Sales Agent';
        const roleCol = role === 'Estimator' ? 'estimator' : 'salesAgent';
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            ...estimatorNoShowExclusion(role),
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
              getEventsOfSharedStatus: [ 'not-shared' ],
            },
            charge: { excludeDamages: true },
          }),
          periodType: 'jobBookingStageDate',
          groupBy: salesLeaderboardRoleToGroupBy[role],
          dataColumns: [ roleCol, 'jobCount' ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'jobCount DESC',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      }
      case 'revenueAndLogistics':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventEnd',
          groupBy: 'none',
          dataColumns: [
            'realizedRevenue',
            'unrealizedRevenue',
            'sharedRevenue',
            'distanceBooked',
            'distanceCompleted',
            'travelTimeBooked',
            'travelTimeCompleted',
            'currency',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      case 'leadConversions':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [] },
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobOrigin',
          dataColumns: [
            'jobOrigin',
            'jobCount',
            'convertedJobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'oseConversion':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: { hasCompletedEventOfType: [ 'estimating' ] },
            event: { getEventsOfStatus: [] }
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobStage',
          dataColumns: [
            'jobStage',
            'jobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'veConversion':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: { hasCompletedEventOfType: [ 'virtualEstimate' ] },
            event: { getEventsOfStatus: [] }
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobStage',
          dataColumns: [
            'jobStage',
            'jobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'estimateConversion':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: { hasCompletedEventOfType: [
              'estimating',
              'virtualEstimate',
            ] },
            event: { getEventsOfStatus: [] }
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobStage',
          dataColumns: [
            'jobStage',
            'jobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'transactionsPaid':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency),
          periodType: 'transactionPaidAt',
          groupBy: 'transactionPaidDate',
          dataColumns: [
            'transactionPaidDate',
            'transactionPaidTotal',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      case 'eventsHappeningPerDay':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventStart',
          groupBy: 'eventBookingDate',
          dataColumns: [
            'eventBookingDate',
            'discountedSubTotal',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'eventBookingDate ASC',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      case 'eventsCreatedPerDay':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventCreatedAt',
          groupBy: 'eventCreatedAt',
          dataColumns: [
            'eventCreatedAt',
            'discountedSubTotal',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          currencySource: currency === 'All' ? 'zone' : undefined,
        };
      case 'pipeline':
      case 'engineJobs':

        const { opts: {
          selectedJobType,
          groupLeadsBy,
          skip,
          sortBy,
          pipelinePeriod,
        } = {} } = input;

        if (isNil(selectedJobType)) {
          throw new Error(`Dashboard report ${report} requires selectedJobType but none was provided`);
        }

        // Filters that will apply to both the pipeline chart and the preview section
        const engineJobFilters: PreviewReportFilter['job'] = {
          // Tiered sources: send the active source path as a subtree filter so
          // descendant tiers (e.g. `Realtor > Remax`) are included when the
          // user has drilled to `Realtor`. See docs/tiered-job-sources.md.
          sourceTiers: has(selectedJobType, 'source', 'Not specified') ? [ selectedJobType.source ] : [],
          origins: has(selectedJobType, 'jobOrigin', 'Not specified') ? [ selectedJobType.jobOrigin ] : [],
          estimatorIds: has(selectedJobType, 'estimator') ? [ decodeUser(selectedJobType.estimator).id ] : [],
          salesAgentIds: has(selectedJobType, 'salesAgent') ? [ decodeUser(selectedJobType.salesAgent).id ] : [],
          estimateMethods: has(selectedJobType, 'estimateMethod') ? [ selectedJobType.estimateMethod ] : [],
          utmSources: has(selectedJobType, 'utmSource', 'Not specified') ? [ selectedJobType.utmSource ] : [],
          utmMedia: has(selectedJobType, 'utmMedium', 'Not specified') ? [ selectedJobType.utmMedium ] : [],
          utmCampaigns: has(selectedJobType, 'utmCampaign', 'Not specified') ? [ selectedJobType.utmCampaign ] : [],
          utmContents: has(selectedJobType, 'utmContent', 'Not specified') ? [ selectedJobType.utmContent ] : [],
          utmTerms: has(selectedJobType, 'utmTerm', 'Not specified') ? [ selectedJobType.utmTerm ] : [],
          noSource: selectedJobType.source === 'Not specified',
          noOrigin: selectedJobType.jobOrigin === 'Not specified',
          noEstimateMethod: selectedJobType.estimateMethod === 'None',
          noEstimator: selectedJobType.estimator === 'None',
          noSalesAgent: selectedJobType.salesAgent === 'None',
          noUtmSource: selectedJobType.utmSource === 'Not specified',
          noUtmMedium: selectedJobType.utmMedium === 'Not specified',
          noUtmCampaign: selectedJobType.utmCampaign === 'Not specified',
          noUtmContents: selectedJobType.utmContent === 'Not specified',
          noUtmTerm: selectedJobType.utmTerm === 'Not specified',
          currency: currency === 'All' ? undefined : currency,
        };

        if (pipelinePeriod === 'Completed') {
          engineJobFilters.closedReason = 'complete';
        }

        const engineEventFilters: PreviewReportFilter['event'] = {
          getEventsOfType: getEventTypeFilter(pipelinePeriod),
        };

        const engineChargeFilter: PreviewReportFilter['charge'] = {
          eventStatuses: [ 'required', 'pending', 'booked', 'confirmed', 'completed' ],
        }

        const groupLeadsByMap: Record<GroupLeadsBy, string> = {
          'Source': 'jobStageStatusSource',
          'Job Origin': 'jobStageStatusOrigin',
          'Estimate Method': 'jobStageStatusEstimateMethod',
          'Estimator': 'jobStageStatusEstimator',
          'Sales Agent': 'jobStageStatusSalesAgent',
          'UTM Source': 'jobStageStatusUTMSource',
          'UTM Medium': 'jobStageStatusUTMMedium',
          'UTM Campaign': 'jobStageStatusUTMCampaign',
          'UTM Content': 'jobStageStatusUTMContent',
          'UTM Term': 'jobStageStatusUTMTerm',
        };

        const groupBy = groupLeadsByMap[groupLeadsBy];

        if (report === 'pipeline') {
          return {
            skip: 0,
            limit: -1,
            start,
            end,
            filter: {
              job: {
                ...engineJobFilters,
                getJobsOfState: [ 'open', 'closed' ],
                getJobsOfStage: [ 'lead', 'estimate', 'booking', 'invoice' ],
              },
              event: {
                ...engineEventFilters,
              },
              charge: {
                ...engineChargeFilter,
              }
            },
            periodType: getPeriodTypeForPipelinePeriod(pipelinePeriod),
            groupBy,
            dataColumns: [ groupBy, 'jobCount', 'discountedSubTotal'],
            aggregationColumns: [],
            performance: 'skip-count',
            linkFormat: 'sheets',
            currencySource: currency === 'All' ? 'zone' : undefined,
          };
        }

        const engineJobsDataColumns = [ ...selectedJobsColumns ] as string[];

        let sort: string | undefined = undefined;

        if (sortBy) {

          sort = sortByMap[sortBy];

          const [ sortColumn ] = sort.split(' ');

          if (!engineJobsDataColumns.includes(sortColumn)) {
            engineJobsDataColumns.push(sortColumn);
          }
        }

        const engineJobsQueryVars: Pick<PreviewReportQueryVariables,
            | 'periodType'
            |  'groupBy'
            |  'dataColumns'
            |  'aggregationColumns'
            |  'performance'
            |  'linkFormat'
            |  'sort'
            |  'currencySource'
            > = {
          periodType: getPeriodTypeForPipelinePeriod(pipelinePeriod),
          groupBy: 'job',
          dataColumns: engineJobsDataColumns,
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'web',
          sort,
          currencySource: currency === 'All' ? 'zone' : undefined,
        };

        const excludeComplete = selectedJobType.jobStage === 'Invoice'
          && selectedJobType.status === 'closed'
          && selectedJobType.jobClosureReason !== 'complete';

        const isSpecificStage = Boolean(selectedJobType.status || selectedJobType.jobClosureReason || selectedJobType.attentionExplanation);

        return {
          skip,
          limit: ENGINE_JOBS_QUERY_LIMIT,
          start,
          end,
          filter: {
            job: {
              stage: isSpecificStage ? selectedJobType.jobStage.toLowerCase() : undefined,
              getJobsOfStage: !isSpecificStage ? getCurrentAndFutureStages(selectedJobType.jobStage, true) : undefined,
              getJobsOfState: selectedJobType.status ? [ selectedJobType.status ] : [ 'open', 'closed', 'archived' ],
              closedReason: has(selectedJobType, 'jobClosureReason') ? selectedJobType.jobClosureReason : undefined,
              excludeClosedReasons: excludeComplete ? [ 'complete' ] : [],
              attentionExplanations: has(selectedJobType, 'attentionExplanation') ? [ selectedJobType.attentionExplanation ] : undefined,
              noAttentionExplanation: selectedJobType?.attentionExplanation === 'None',
              ...engineJobFilters,
            },
            event: {
              ...engineEventFilters,
            },
            charge: {
              ...engineChargeFilter,
            }
          },
          ...engineJobsQueryVars,
        };
      default:
        throw new Error(`Not handled: ${report}`);
    }
  }

  /**
   * Returns an observable of the query parameters
   * needed to navigate to the report that supplies the data for a given dashboard item.
   */
  getReportQueryParams(report: DashboardReport) {
    return this.select(
      this.currency$,
      this.unixPeriod$,
      this.selectedJobType$,
      this.timezone$,
      // We include zone even though we do not use it to avoid memoizing solely based on currency and month
      this.graphqlModule.zone,
      (currency, { start, end }, selectedJobType, timezone, zone) => {

        if(!zone) return null;

        return this.getQueryParams({
          start,
          end,
          currency,
          report,
          opts: {
            selectedJobType,
          }
        }, timezone);
      },
    ).pipe(filter(Boolean));
  }

  getQueryParams(input: Parameters<typeof this.getQueryVars>[0], timezone: string) {

    const {
      start,
      end,
      currency,
      report,
      opts,
    } = input;

    const queryVars = this.getQueryVars({
      start,
      end,
      currency,
      report,
      opts,
    });

    return this.queryVarsToQueryParams(queryVars, timezone);
  }

  queryVarsToQueryParams(queryVars: PreviewReportQueryVariables, timezone: string) {

    const formValue: { [ K in keyof ReportParamsForm['value'] ] : any }= {
      period: 'Custom',
      customPeriod: [
        dayjs.tz(queryVars.start * 1000, timezone).toDate(),
        dayjs.tz(queryVars.end * 1000, timezone).toDate(),
      ],
      periodType: queryVars.periodType,
      jobStage: queryVars.filter?.job?.getJobsOfStage?.length ?
        queryVars.filter?.job?.getJobsOfStage : queryVars.filter?.job?.stage ?
        [ queryVars.filter.job.stage ] : [],
      jobState: queryVars.filter?.job?.getJobsOfState?.length ? queryVars.filter?.job?.getJobsOfState : [ 'any' ],
      currency: queryVars.filter?.job?.currency,
      jobHasCompletedEventOfType: queryVars.filter?.job?.hasCompletedEventOfType,
      eventStatuses: queryVars.filter?.event?.getEventsOfStatus?.length ? queryVars.filter?.event?.getEventsOfStatus : [ 'any' ],
      eventTypes: queryVars.filter?.event?.getEventsOfType,
      // Carry the franchise / shared-status filters into the report URL so
      // "explore in report" links open scoped (the viewer maps jobFranchises →
      // franchiseIds and eventSharedStatuses → getEventsOfSharedStatus).
      jobFranchises: queryVars.filter?.job?.franchiseIds,
      eventSharedStatuses: queryVars.filter?.event?.getEventsOfSharedStatus,
      groupBy: queryVars.groupBy,
      sort: queryVars.sort,
      currencySource: queryVars.currencySource,
      dataColumns: queryVars.dataColumns as string[],
      aggregationColumns: queryVars.aggregationColumns as string[],
      excludeDamages: queryVars.filter?.charge?.excludeDamages,
      transactionStages: queryVars.filter?.transaction?.stages?.length ? queryVars.filter.transaction.stages : [],
      jobSources: queryVars.filter?.job?.sources?.length ? queryVars.filter.job.sources : [],
      jobSourceTiers: queryVars.filter?.job?.sourceTiers?.length ? queryVars.filter.job.sourceTiers : [],
      noSource: queryVars.filter?.job?.noSource,
      noOrigin: queryVars.filter?.job?.noOrigin,
      jobOrigins: queryVars.filter?.job?.origins?.length ? queryVars.filter.job?.origins : [],
      jobEstimateMethods: queryVars.filter?.job?.estimateMethods?.length ? queryVars.filter.job.estimateMethods : [],
      jobEstimators: queryVars.filter?.job?.estimatorIds?.length ? queryVars.filter.job.estimatorIds : [],
      jobSalesAgents: queryVars.filter?.job?.salesAgentIds?.length ? queryVars.filter.job.salesAgentIds : [],
      attentionExplanations: queryVars.filter?.job?.attentionExplanations?.length ? queryVars.filter.job.attentionExplanations : [],
      noAttentionExplanation: queryVars.filter?.job?.noAttentionExplanation,
      closedReason: queryVars.filter?.job?.closedReason,
      utmSources: queryVars.filter?.job?.utmSources?.length ? queryVars.filter.job.utmSources : [],
      noUtmSource: queryVars.filter?.job?.noUtmSource,
      utmContents: queryVars.filter?.job?.utmContents?.length ? queryVars.filter.job.utmContents : [],
      noUtmContent: queryVars.filter?.job?.noUtmContents,
      utmMedia: queryVars.filter?.job?.utmMedia?.length ? queryVars.filter.job.utmMedia : [],
      noUtmMedium: queryVars.filter?.job?.noUtmMedium,
      utmTerms: queryVars.filter?.job?.utmTerms?.length ? queryVars.filter.job.utmTerms : [],
      noUtmTerm: queryVars.filter?.job?.noUtmTerm,
    }

    // Per-column format overrides (e.g. flagging raw calculator_* columns as
    // currency). Encoded as 'column:format' strings — the same array shape the
    // report viewer reads from the URL and parses back into its columnFormats map.
    const columnFormatsInput = queryVars.columnFormats ?? [];
    const columnFormats = ([] as { column: string; format: string }[])
      .concat(columnFormatsInput)
      .map(({ column, format }) => `${ column }:${ format }`);

    // Per-column header overrides. Encoded as 'column:name' strings — the same
    // array shape the report viewer reads from the URL into its customColumnNames map.
    const customColumnNamesInput = queryVars.customColumnNames ?? [];
    const customColumnNames = ([] as { column: string; name: string }[])
      .concat(customColumnNamesInput)
      .map(({ column, name }) => `${ column }:${ name }`);

    return this.queryParamsService.formatSearchFormValues({
      ...formValue,
      columnFormats: columnFormats.length ? columnFormats : undefined,
      customColumnNames: customColumnNames.length ? customColumnNames : undefined,
    }, {
      dateRange: { controls: [ 'customPeriod' ]},
    }, { timezone, format: DATE_PARAM_FORMAT });
  }

  getPercent(input: { total: number; converted: number }): number {
    return input.total ? ((input.converted / input.total) * 100) : 0;
  }

  calculateAverage(state: DashboardState, prefix: SizePrefix) {

    const countKey = `${prefix}Count` as const;

    const totalKey = `${prefix}Total` as const;

    const count = state[countKey];

    const total = state[totalKey];

    const average = count ? total / count : 0;

    const currency = state.currency === 'All' ? baseEnvironment.defaultCurrency : state.currency;

    return {
      count,
      total,
      average,
      currency,
    }
  }

  getDescription(input: {
    baseDescription: string;
    data: {
      total: number;
      count: number;
      currency: string;
    };
    extra?: string
  }) {

    const {
      baseDescription,
      data,
      extra,
    } = input;

    let output = baseDescription;

    const total = formatCurrency(data.total, data.currency);

    output += `the sum of their net subtotal (${total}) divided by their count (${data.count}). `

    output += extra || '';

    return output;
  }

  getCurrencyDelta(input: { current: CurrencyAmount; past: CurrencyAmount }): Omit<CurrencyDelta, 'loading'> {
    const amount = Math.abs(input.past.amount - input.current.amount);
    const sign = input.past.amount === input.current.amount ? 'neutral' :
      input.past.amount > input.current.amount ? 'negative' : 'positive';
    return {
      amount,
      sign,
      currency: input.current.currency,
    };
  }

  getAmountDelta(input: { current: number; past: number }): Omit<AmountDelta, 'loading'> {
    const amount = Math.abs(input.past - input.current);
    const sign = input.past === input.current ? 'neutral' :
      input.past > input.current ? 'negative' : 'positive';
    return {
      amount,
      sign,
    };
  }

  fetchActivity(input: {
    start: number;
    end: number;
    currency: string;
  }) {

    const { start, end, currency } = input;

    const transactionsPaidQueryVars = this.getQueryVars({ start, end, currency, report: 'transactionsPaid' });
    const eventsHappeningQueryVars = this.getQueryVars({ start, end, currency, report: 'eventsHappeningPerDay' });
    const eventsCreatedQueryVars = this.getQueryVars({ start, end, currency, report: 'eventsCreatedPerDay' });

    const customParams = {
      start: '$start',
      end: '$end',
      filter: '$filter'
    };

    const query = gql`
      query DashboardActivity(
        $start: Int!,
        $end: Int!,
        $filter: PreviewReportFilter,
      ) {
        transactionsPaid: previewReport(
          ${this.generatePreviewReportParams(transactionsPaidQueryVars, customParams)}
        ) {
          data
        }
        eventsHappening: previewReport(
          ${this.generatePreviewReportParams(eventsHappeningQueryVars, customParams)}
        ) {
          data
        }
        eventsCreated: previewReport(
          ${this.generatePreviewReportParams(eventsCreatedQueryVars, customParams)}
        ) {
          data
        }
      }
    `;

    return this.apollo.query<{
      transactionsPaid: { data?: any },
      eventsHappening: { data?: any },
      eventsCreated: { data?: any},
    }>({
      query,
      variables: {
        start,
        end,
        currency,
        filter: eventsCreatedQueryVars.filter,
      },
    });
  }

  fetchEngineJobs(input: {
    start: number;
    end: number;
    jobTypes: {
      jobType: SelectedJobType,
      key: string,
      metrics: {
        jobCount: number,
        revenue: number,
      }
    }[],
    currency: string;
    sortBy: SortBy;
    pipelinePeriod: PipelinePeriod;
    source?: string;
    jobOrigin?: string;
    estimateMethod?: string;
    estimator?: string;
    salesAgent?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
    skip?: number;
    jobUpdated?: string;
  }) {

    const {
      start,
      end,
      jobTypes,
      currency,
      sortBy,
      skip,
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      jobUpdated,
      pipelinePeriod,
    } = input;

    const queryFilters: string[] = [];
    const queries: string[] = [];
    const filterVariables: Record<string, PreviewReportFilter> = {};

    for (const jobType of jobTypes) {

      const key = encodeKeyForGraphQL(jobType.key);

      const selectedJobType = jobType.jobType;

      const filterKey = `${key}_filter`;

      const filterName = `$${filterKey}`;

      const filter = `${filterName}: PreviewReportFilter`;

      queryFilters.push(filter);

      const queryVars = this.getQueryVars({
        start,
        end,
        currency,
        report: 'engineJobs',
        opts: {
          selectedJobType: {
            ...selectedJobType,
            source,
            jobOrigin,
            estimateMethod,
            estimator,
            salesAgent,
            utmSource,
            utmMedium,
            utmCampaign,
            utmContent,
            utmTerm,
          },
          pipelinePeriod,
          sortBy,
          skip,
        },
      });

      filterVariables[filterKey] = queryVars.filter;

      const query = `
        ${key}: previewReport(
          ${this.generatePreviewReportParams(queryVars, { start: '$start', end: '$end', skip: '$skip', filter: filterName })}
        ) {
          data
        }
      `;

      queries.push(query);
    }

    const query = gql`
      query EngineJobs(
        $start: Int!,
        $end: Int!,
        $skip: Int,
        ${queryFilters.join(',\n')}
      ) {
        ${queries.join('\n')}
      }
    `;

    return this.apollo.query<DashboardState['engineJobs']>({
      query,
      variables: {
        start,
        end,
        skip,
        ...filterVariables,
      },
      fetchPolicy: jobUpdated ? 'network-only' : 'cache-first',
    });
  }

  generatePreviewReportParams(
    input: PreviewReportQueryVariables,
    customParams?: Partial<Record<keyof PreviewReportQueryVariables, string>>,
  ) {

    const params: (keyof PreviewReportQueryVariables)[] = [
      'start',
      'end',
      'skip',
      'filter',
      'groupBy',
      'periodType',
      'dataColumns',
      'aggregationColumns',
      'sort',
      'performance',
      'linkFormat',
      'limit',
      'currencySource',
    ];

    const lines = [];

    for (const param of params) {

      if (customParams[param]) {
        lines.push(`${param}: ${customParams[param]}`);
        continue;
      }

      const value = input[param];

      if (!value) { continue; }

      const isArray = Array.isArray(value);

      if (isArray) {
        lines.push(`${param}: [ ${value.map((el) => `"${el}"`).join(',')} ]`);
        continue;
      }

      if (typeof value === 'number') {
        lines.push(`${param}: ${value}`);
        continue;
      }

      // By this point, we'll assume the value is of string type
      lines.push(`${param}: "${value}"`);
    }

    return lines.join(`, `)
  }

  /**
   * Formats event currency and adds a total row to the end of the data.
   */
  formatEventData(data: ReportData) {

    const { headers, rows } = data;

    const currencyIndex = headers.indexOf('Currency');

    const formattedHeaders = headers.slice(0, currencyIndex);

    if (!data.rows.length) {
      return {
        headers: formattedHeaders,
        rows: [],
      }
    }

    // With `currencySource: 'zone'` set when 'All', every row in the response
    // shares one currency, so taking it from the first row is sufficient.
    const currency = rows[0]?.[currencyIndex] || baseEnvironment.defaultCurrency;

    let subTotalSum = 0;
    let eventCountSum = 0;

    const eventTypeMap: Record<string, {
      netSubTotal: number;
      eventCount: number;
      average: number;
    }> = {};

    for (const [ eventType, netSubTotalRaw, eventCountRaw ] of rows) {

      const currentValue = eventTypeMap[eventType];

      const defaultValue = {
        netSubTotal: 0,
        eventCount: 0,
        average: 0,
      };

      const value = currentValue || defaultValue;

      const eventCount = parseInt(eventCountRaw);
      const netSubTotal = parseFloat(netSubTotalRaw);

      value.eventCount += eventCount;
      value.netSubTotal += netSubTotal;
      value.average = value.eventCount ? value.netSubTotal / value.eventCount : 0;

      subTotalSum += netSubTotal;
      eventCountSum += eventCount;

      eventTypeMap[eventType] = value;
    }

    const average = eventCountSum ? subTotalSum / eventCountSum : 0;

    const formattedRows: string[][] = Object.entries(eventTypeMap)
      .map(([ eventType, { netSubTotal, eventCount, average }]) => {

        let formattedEventType = eventType;

        if (eventType === 'virtualEstimate') {
          formattedEventType = 'Virtual Estimate';
        }

        // Requirement: `#6160` - normalize both legacy and corrected OTR event keys for display stability.
        if ([ 'otr-fees', 'otr-fess' ].includes(eventType?.toLowerCase())) {
          formattedEventType = 'OTR Fees';
        }

        return [
          formattedEventType,
          formatCurrency(netSubTotal, currency),
          eventCount.toString(),
          formatCurrency(average, currency),
        ];
      });

    formattedRows.push([
      'Total',
      formatCurrency(subTotalSum, currency),
      eventCountSum.toString(),
      formatCurrency(average, currency),
    ]);

    return {
      headers: formattedHeaders,
      rows: formattedRows
    }
  }

  notifyBottomReached(path: DashboardPath) {
    this.bottomReached$.next(path);
  }

  notifyJobUpdated(jobId: string) {
    this.jobUpdated$.next(jobId);
  }

  get routeActivated$() {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute.firstChild.snapshot.routeConfig.path as DashboardPath),
      startWith(this.activatedRoute.firstChild?.snapshot?.routeConfig?.path as DashboardPath),
    );
  }

  getEngineJobsHasMore(
    newJobs: DashboardState['engineJobs'],
    jobTypes: ExtractObservableType<typeof this.engineJobsInfo$>['jobTypes'],
  ) {

    if (jobTypes.length !== 1) { return false; }

    const [ { key } ] = jobTypes;

    const graphQLKey = encodeKeyForGraphQL(key);

    const newRows = newJobs[graphQLKey]?.data?.rows || [];

    return !(newRows.length < ENGINE_JOBS_QUERY_LIMIT);
  }

}

function getCountAndTotal(serverResponse: ApolloQueryResult<AggregationDataOnlyQuery>): { count: number; total: number; } {

  let total = 0;
  let count = 0;

  for (const [ rowAmount, rowCount ] of serverResponse.data.previewReport.aggregationData.rows) {
    total += parseFloat(rowAmount);
    count += parseInt(rowCount);
  }

  return { total, count };
}

function getRevenueAndLogisticsTotals(res: ApolloQueryResult<DataOnlyQuery>) {

  const { rows } = res.data.previewReport.data;

  if (!rows.length) {
    return {
      realizedRevenue: getDefaultAmount(),
      scheduledRevenue: getDefaultAmount(),
      expectedRevenue: getDefaultAmount(),
      sharedRevenue: getDefaultAmount(),
      distanceBooked: 0,
      distanceCompleted: 0,
      travelTimeBooked: 0,
      travelTimeCompleted: 0,
    }
  }

  // With `currencySource: 'zone'` set when 'All', every row in the response
  // shares one currency, so taking it from the first row is sufficient.
  const currency = rows[0]?.[7] || baseEnvironment.defaultCurrency;

  let realizedRevenueTotal = 0;
  let scheduledRevenueTotal = 0;
  let sharedRevenueTotal = 0;
  let distanceBookedTotal = 0;
  let distanceCompletedTotal = 0;
  let travelTimeBookedTotal = 0;
  let travelTimeCompletedTotal = 0;

  for (const [
    realizedRevenue,
    scheduledRevenue,
    sharedRevenue,
    distanceBooked,
    distanceCompleted,
    travelTimeBooked,
    travelTimeCompleted,
  ] of rows) {
    realizedRevenueTotal += parseFloat(realizedRevenue);
    scheduledRevenueTotal += parseFloat(scheduledRevenue);
    sharedRevenueTotal += parseFloat(sharedRevenue);
    distanceBookedTotal += parseFloat(distanceBooked);
    distanceCompletedTotal += parseFloat(distanceCompleted);
    travelTimeBookedTotal += parseFloat(travelTimeBooked);
    travelTimeCompletedTotal += parseFloat(travelTimeCompleted);
  }

  const expectedRevenueTotal = realizedRevenueTotal + scheduledRevenueTotal;

  return {
    realizedRevenue: {
      amount: realizedRevenueTotal,
      currency,
    },
    scheduledRevenue: {
      amount: scheduledRevenueTotal,
      currency,
    },
    expectedRevenue: {
      amount: expectedRevenueTotal,
      currency,
    },
    sharedRevenue: {
      amount: sharedRevenueTotal,
      currency,
    },
    distanceBooked: distanceBookedTotal,
    distanceCompleted: distanceCompletedTotal,
    travelTimeBooked: travelTimeBookedTotal,
    travelTimeCompleted: travelTimeCompletedTotal,
  };
}

function getLabelsFromCount(
  metricsBreakdown: Record<string, { jobCount: number; revenue: number }>,
  selectedMetric: PipelineMetric,
  currency: string,
  opts?: {
    pluralizeLabel?: boolean,
    isName?: boolean,
    isSourceTier?: boolean,
  }
) {

  const {
    pluralizeLabel,
    isName,
    isSourceTier,
  } = opts || {};

  if (currency === 'All') {
    currency = baseEnvironment.defaultCurrency;
  }

  return Object.fromEntries(Object.entries(metricsBreakdown)
    .map(( [ key, metrics ]) => {

      const amount = selectedMetric === 'Job Count' ? metrics.jobCount : metrics.revenue;

      const formattedAmount = selectedMetric === 'Job Count' ? amount : formatCurrency(amount, currency);

      let label = key;

      if (pluralizeLabel) {
        label = pluralize(label);
      }

      if (isName && label !== 'None') {
        label = decodeUser(label).name;
      }

      // Source nodes carry the full tier path as their id (so clicks filter on
      // the full path), but display only the last segment, e.g. `Remax`.
      if (isSourceTier) {
        label = lastTierOf(label);
      }

      return ( [ key, `${ label } (${ formattedAmount })` ]);
    }));;
}


function isSelected(flowJobType: SelectedJobType, selectedJobType: SelectedJobType) {

  const isStageMatch = selectedJobType.jobStage === flowJobType.jobStage;

  if (isNil(selectedJobType.status)) {
    return isStageMatch;
  }

  const isStatusMatch = selectedJobType.status === flowJobType.status;

  // Special case
  if (selectedJobType.jobStage === 'Invoice' && selectedJobType.status === 'closed' && isNil(selectedJobType.jobClosureReason)) {
    return isStageMatch && isStatusMatch && (!flowJobType.jobClosureReason || flowJobType.jobClosureReason !== 'complete');
  }

  if (isNil(selectedJobType.jobClosureReason) && isNil(selectedJobType.attentionExplanation)) {
    return isStageMatch && isStatusMatch;
  }

  if (selectedJobType.status === 'closed') {
    return isStageMatch && isStatusMatch
     && selectedJobType.jobClosureReason === flowJobType.jobClosureReason;
  }

  return isStageMatch && isStatusMatch
    && selectedJobType.attentionExplanation === flowJobType.attentionExplanation;
}

function getCurrentAndFutureStages(jobStage?: JobStage, toLower?: boolean) {
  if (!jobStage) { return [] };

  const stageIndex = jobStages.indexOf(jobStage);

  const currentAndFutureStages = jobStages
    .slice(stageIndex);

  if (!toLower) {
    return currentAndFutureStages;
  }

  return currentAndFutureStages.map((s) => s.toLowerCase());
}


export function getSelectedJobData(row: string[], columnKey: typeof selectedJobsColumns[number]) {
  const columnIndex = selectedJobsColumns.indexOf(columnKey);
  return row[columnIndex];
}

export function getCurrentMonth() {
  return dayjs().format('MMMM YYYY');
}

export function getCurrentMonthPeriod(): string[] {
  const today = dayjs();
  return [ today.startOf('month').format('YYYY-MM-DD'), today.endOf('month').format('YYYY-MM-DD') ];
}

/**
 * Requirement: `#6855` - one parsed row of the OTR alert fetch. `total` / `priceToCustomer` /
 * `fpRevenue` are null when the underlying value is absent, NOT 0 — the whole point of fetching
 * without `columnFormats` is that a missing calculator key arrives as 'None' rather than as
 * '$0.00'. Collapsing that to 0 here would re-introduce the bug the fetch avoids.
 */
interface OtrAlertJob {
  code: string;
  routerLink: any[];
  queryParams: Record<string, unknown>;
  total: number | null;
  priceToCustomer: number | null;
  fpRevenue: number | null;
  executionType: string;
  sharedWith: string;
  hasNoLaborCost: boolean;
}

/**
 * Currency cells arrive formatted ('$1,700.01') because the alert fetch uses linkFormat 'web' to
 * keep job ids recoverable — see OTR_ALERT_COLUMNS. Strips the symbols; returns null for an absent
 * value ('None' / '') so callers can tell "no such key" from "zero".
 */
function parseAlertNumber(raw: string | undefined): number | null {
  if (raw === undefined || raw === null) { return null; }
  const trimmed = raw.trim();
  if (!trimmed || trimmed === 'None') { return null; }
  const parsed = parseFloat(trimmed.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Requirement: `#6855` - parse the `CODE <url>` job cell into a code plus a routerLink to the job.
 * `parseHyperlink` is not usable here: it returns { text, zoneId } and drops the path, so the job
 * id — the only thing that makes a link possible — is lost. Mirrors the OTR - Labor Tracking
 * component's `parseEntity`.
 */
function parseOtrAlertJobs(data: ReportData): OtrAlertJob[] {

  const indexOf = (name: string) =>
    data.headers.findIndex((h) => h.toLowerCase() === name.toLowerCase());

  const jobI = indexOf('job');
  if (jobI === -1) { return []; }

  const totalI = indexOf('total');
  const priceI = indexOf('total price to customer');
  const fpI = indexOf('total job revenue f p');
  const execI = indexOf('execution type');
  const sharedI = indexOf('shared with franchises');
  const laborI = indexOf('job breakdown_labor');
  const assumedI = indexOf('total labor assumed');

  const cell = (row: string[], index: number) => index === -1 ? '' : (row[index] ?? '');

  const jobs: OtrAlertJob[] = [];

  for (const row of data.rows) {
    const parsed = parseEmbeddedUrl(cell(row, jobI));
    if (!parsed) { continue; }
    // `url` is already a bare pathname (parseEmbeddedUrl returns `new URL(...).pathname`), so the
    // id is its last segment and the zone rides in `queryParams` — which is what the link needs,
    // NOT the dashboard's report params.
    const id = parsed.url.replace(/\/$/, '').split('/').pop() ?? '';
    if (!id) { continue; }

    jobs.push({
      code: parsed.before.trim(),
      routerLink: [ '/jobs', id ],
      queryParams: parsed.queryParams,
      total: parseAlertNumber(cell(row, totalI)),
      priceToCustomer: parseAlertNumber(cell(row, priceI)),
      fpRevenue: parseAlertNumber(cell(row, fpI)),
      executionType: cell(row, execI).trim(),
      sharedWith: cell(row, sharedI).trim(),
      // Both absent — matches the engine's COALESCE(jobBreakdown_labor, totalLaborAssumed, 0),
      // which is what silently deducts no labor and inflates Margin on FP OTRs.
      hasNoLaborCost: parseAlertNumber(cell(row, laborI)) === null
        && parseAlertNumber(cell(row, assumedI)) === null,
    });
  }

  return jobs;
}

/**
 * Requirement: `#6855` - the data-quality warnings rendered under the OTR - System Revenue table.
 * Every alert is "a sentence, then the jobs that caused it"; an alert with no jobs is omitted
 * entirely rather than rendered as a zero, so the block disappears by itself once the data is
 * clean — the behaviour the single margin footnote already had.
 */
function buildOtrAlerts(jobs: OtrAlertJob[], currency: string): DashboardAlert[] {

  const alerts: DashboardAlert[] = [];
  const plural = (n: number, one: string, many: string) => n === 1 ? one : many;

  // 1. Charges vs calculator. 2 cents rather than exact: the two are rounded independently and
  // 1-cent disagreements are pure noise — measured on prod, 85 of 90 August jobs sat within 2c and
  // NOTHING sat between 2c and $100, so the threshold separates noise from signal cleanly.
  //
  // Fires on two genuinely different causes, which is why the wording stays neutral about which:
  // charges edited without re-saving the calculator (or the reverse), AND charges stranded on a
  // cancelled event (IUSA-6JK-9DI on prod: $412.84 live against a $5,000 calculator, because
  // $4,587.16 sits on a cancelled delivery). Both need a person to look.
  const outOfSync = jobs.filter((j) =>
    j.priceToCustomer !== null && Math.abs((j.total ?? 0) - j.priceToCustomer) > 0.02);

  if (outOfSync.length) {
    alerts.push({
      key: 'charges-out-of-sync',
      severity: 'warn',
      text: `${ outOfSync.length } ${ plural(outOfSync.length, 'job', 'jobs') } in this period`
        + ` ${ plural(outOfSync.length, 'has', 'have') } charges that do not match the calculator's`
        + ' Total Price to Customer. Either the charges changed without the calculator being'
        + ' re-saved, or charges are sitting on a cancelled event:',
      jobs: outOfSync,
    });
  }

  // 2. Partner FP with no receiving franchise. Reproduces the engine's own
  // calcOtrUnattributedFpRevenue predicate exactly — 'Partner FP' covers both an explicit
  // partnerFp and an unset jobExecutionType (the mapped column's CASE falls through to it), and an
  // empty Shared With Franchises means no SHARED_WITH_ZONE edge to any franchise, status-blind,
  // just as the engine tests it. Verified against prod: 1 job / $2,515.69 both ways for Aug 2026.
  const notShared = jobs.filter((j) =>
    j.priceToCustomer !== null && j.executionType === 'Partner FP' && !j.sharedWith);

  if (notShared.length) {
    const unattributed = notShared.reduce((sum, j) => sum + (j.fpRevenue ?? 0), 0);
    alerts.push({
      key: 'partner-fp-not-shared',
      severity: 'warn',
      text: `Partner FP — not shared with a franchise: ${ notShared.length }`
        + ` ${ plural(notShared.length, 'job', 'jobs') } carrying`
        + ` ${ formatCurrency(unattributed, currency) } of FP revenue reached no franchise row,`
        + ' so the payout is owed but unattributed:',
      jobs: notShared,
    });
  }

  // 3. No labor cost recorded at all. Kept from the previous single footnote, wording intact: the
  // margin row subtracts labor from the customer total, Cypher COALESCEs a missing labor value to
  // 0, so no labor is deducted and Margin on FP OTRs comes out TOO HIGH. Systematic and
  // one-directional, never self-cancelling, so any affected job is worth naming.
  const noLabor = jobs.filter((j) => j.priceToCustomer !== null && j.hasNoLaborCost);

  if (noLabor.length) {
    alerts.push({
      key: 'missing-labor-cost',
      severity: 'warn',
      text: `Margin on FP OTRs is too high: ${ noLabor.length }`
        + ` ${ plural(noLabor.length, 'job', 'jobs') } in this period`
        + ` ${ plural(noLabor.length, 'was', 'were') } priced by an older calculator that recorded`
        + ' no labor cost, so no labor was deducted. Re-open and save'
        + ` ${ plural(noLabor.length, 'it', 'them') } to record the labor cost:`,
      jobs: noLabor,
    });
  }

  return alerts;
}

export function mergeInDefaultFilters(
  currency: string,
  additionalFilters?: PreviewReportQueryVariables['filter'],
): PreviewReportQueryVariables['filter'] {
  const defaultFilters = {
    job: {
      getJobsOfState: [ 'open', 'closed', 'archived' ],
      currency: currency === 'All' ? undefined : currency,
    },
    event: {
      getEventsOfStatus: [ 'required', 'pending', 'booked', 'confirmed', 'completed' ],
    },
  }

  if (!additionalFilters) {
    return defaultFilters;
  }

  // Do not merge arrays, just replace them
  const customizer = (objVal, srcVal) => {
    if (Array.isArray(objVal)) {
      return srcVal;
    }
  }

  // mergeWith mutates its first argument, clone to avoid bugs
  const clone = cloneDeep(defaultFilters);

  return mergeWith(clone, additionalFilters, customizer);
}

function extractName(userString: string) {
  const endOfNameIndex = userString.indexOf(' <');
  return userString.slice(0, endOfNameIndex);
}

function extractId(userString: string) {
  const startOfIdIndex = userString.lastIndexOf('/') + 1;
  const endOfIdIndex = userString.indexOf('?');
  return userString.slice(startOfIdIndex, endOfIdIndex);
}

function parseJobs(rows: string[][]): SelectedJob[] {

  const output = [];

  for (const row of rows) {

    const rawValues = selectedJobsColumns
    .reduce((acc, col) => {

      const value = row[selectedJobsColumns.indexOf(col)];

      if (value === 'None') {
        return acc;
      }

      return {
        ...acc,
        [col]: value,
      }
    }, {}) as Record<
    typeof selectedJobsColumns[number],
    string
    >;

    const parsedJob: SelectedJob = {
      code: extractName(rawValues.job),
      id: extractId(rawValues.job),
      icon: jobStageIcons[rawValues.jobStage.toLowerCase()],
      discountedSubTotal: rawValues.discountedSubTotal,
      createdAt: rawValues.jobCreatedAt,
      description: `${rawValues.jobStatus} ${rawValues.jobStage}`,
      customer: {
        name: extractName(rawValues.customer),
        id: extractId(rawValues.customer),
      },
      status: rawValues.jobStatus as 'Open' | 'Closed',
    };

    if (rawValues.estimator) {
      parsedJob.estimator = {
        name: extractName(rawValues.estimator),
        id: extractId(rawValues.estimator),
      }
    }

    output.push(parsedJob);
  }

  return output;
}

function calculateLabelCount(
  jobInfoCount: Record<string, {
    jobCount: number;
    revenue: number;
  }>,
  expandedFlows: ExpandedFlow[],
  attentionExplanations: DashboardState['attentionExplanations'],
  jobClosureReasons: DashboardState['jobClosureReasons']
) {

  // Top to bottom
  const statusPriority: Record<string, number> = {};

  // Left to right
  const statusColumn: Record<string, number> = {};

  const labelCount: Record<string, { jobCount: number; revenue: number; jobType: SelectedJobType }> = {};

  const jobInfoKeys = Object.keys(jobInfoCount)
    .map((key) => key.split(delimiter))
    .filter(([ stage, status, reason ]) => {

      const jobStage = stage as JobStage;

      const jobStatus = status as 'open' | 'closed' | undefined;

      const isClosedInvoice = jobStage === 'Invoice' && jobStatus === 'closed';

      // Should always be included
      if (isClosedInvoice && reason === 'complete') {
        return true;
      }

      // Should never be included
      if (isClosedInvoice && !reason) {
        return false;
      }

      const doExpand = expandedFlows
        .some((flow) => flow.jobStage === jobStage && status === flow.status);

      if (isClosedInvoice && reason === 'not-complete') {
        return !doExpand;
      }

      return doExpand === Boolean(reason);
    })
    .map((jobInfo) => jobInfo.join(delimiter));

  for (const serializedJobInfo of jobInfoKeys) {

    const [ stage, jobStatus, reason ] = serializedJobInfo.split(delimiter);

    const jobStage = stage as JobStage;

    const status = jobStatus as 'open' | 'closed';

    const isClosed = status === 'closed';

    const isInvoice = jobStage === 'Invoice';

    const isFinalStage = isInvoice && isClosed && reason === 'complete';

    const addDetails = expandedFlows
      .some((flow) => flow.jobStage === jobStage && (status === flow.status))
      || isFinalStage;

    const jobClosureReason = isClosed ? reason : null;

    const attentionExplanation = !isClosed ? reason : null;

    const jobType = {
      jobStage,
      status: status,
      attentionExplanation: !isClosed && addDetails ? attentionExplanation : null,
      jobClosureReason: isClosed && addDetails ? jobClosureReason : null,
    };

    const label = getTitle(jobType, attentionExplanations, jobClosureReasons);

    // // Completed invoices in the middle, closed jobs at the bottom, open jobs at the top
    statusPriority[label] = isFinalStage ? 1 : isClosed ? 2 : 0;
    statusColumn[label] = stageColumn[jobStage] + 1;

    labelCount[label] = labelCount[label] || {
      jobCount: 0,
      revenue: 0,
      jobType,
    }

    const { jobCount, revenue } = jobInfoCount[serializedJobInfo];

    labelCount[label].jobCount += jobCount;
    labelCount[label].revenue += revenue;
  }

  return { labelCount, statusColumn, statusPriority };
}

function getJobClosureReasons(configs: BaseConfigValueFragment[]) {
  const closureReasons: {
    id: string;
    title: string;
    stages: string[];
  }[] = getParsedConfigValueByKey(configs, 'jobs.closedReasons', []);

  return closureReasons.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.title }), {});
}

function getAttentionExplanations(configs: BaseConfigValueFragment[]) {
  const attentionExplanations: {
    attentionExplanation: string;
    label: string;
    description: string;
  }[] = configs
    .filter((c) => c.key.startsWith('attention'))
    .map((c) => JSON.parse(c.value));

  return attentionExplanations.reduce((acc, curr) => ({
    ...acc,
    [curr.attentionExplanation]: { label: curr.label, description: curr.description },
  }), {});
}

function getTitle(
  selectedJobType: SelectedJobType,
  attentionExplanations: DashboardState['attentionExplanations'],
  closureReasons: DashboardState['jobClosureReasons'],
  includeStageAndStatus = true,
  defaultTitle = 'No Jobs Selected'
) {

  if (selectedJobType === null) {
    return defaultTitle;
  }

  const {
    jobStage,
    status,
    jobClosureReason,
    attentionExplanation,
  } = selectedJobType;

  let title = `${pluralize(jobStage)}`;

  if (!status) {
    return title;
  }

  title = `${strToTitleCase(status)} ${title}`;

  if (jobClosureReason) {

    const formattedClosureReason = closureReasons[jobClosureReason] || startCase(jobClosureReason);

    title = includeStageAndStatus ? `${title}: ${ formattedClosureReason }` : formattedClosureReason;
  }

  if (attentionExplanation && attentionExplanation === 'None') {

    const noneTitle = 'No Action Required';

    title = includeStageAndStatus ? `${title} (${noneTitle})` : noneTitle;

  } else if (attentionExplanation) {

    const formattedAttentionExplanation = attentionExplanations[attentionExplanation] ?
      attentionExplanations[attentionExplanation].label :
      startCase(attentionExplanation);

    title = includeStageAndStatus ? `${title}: ${formattedAttentionExplanation}` : formattedAttentionExplanation;
  }

  return title;
}

function getMaxStackedFlows(dataPoints: SankeyDataPoint[]) {

  const flowCount = {};

  for (const dataPoint of dataPoints) {
    const fromKey = [ 'from', dataPoint.from ].join(':');
    flowCount[fromKey] = flowCount[fromKey] || 0;
    flowCount[fromKey] += 1;

    const toKey = [ 'to', dataPoint.to ].join(':');
    flowCount[toKey] = flowCount[toKey] || 0;
    flowCount[toKey] += 1;
  }

  return Math.max(...Object.values(flowCount) as number[]);
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i< str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * Encodes a key by replacing all hyphens '-' with three underscores '___'
 * for use in GraphQL queries as aliases or field names.
 *
 * @param key The key to be encoded.
 * @returns The encoded key.
 */
function encodeKeyForGraphQL(key: string): string {
  return key.replace(/-| /g, '___');
}

function mergeJobs(input: {
  currentJobs: DashboardState['engineJobs'];
  newJobs: DashboardState['engineJobs'];
  graphQLKey: string;
}) {

  const { currentJobs, newJobs, graphQLKey } = input;

  const newRows = newJobs[graphQLKey]?.data?.rows;

  // Nothing to merge
  if (!newRows.length) {
    return currentJobs;
  }

  const currentRows = currentJobs[graphQLKey]?.data?.rows || [];

  const updatedJobs = cloneDeep(currentJobs);

  updatedJobs[graphQLKey] = {
    data: {
      rows: [ ...currentRows, ...newRows ],
    }
  }

  return updatedJobs;
}

function getColor(
  flowJobType: SelectedJobType,
  selectedJobType: SelectedJobType,
  style: CSSStyleDeclaration,
  highlightAll?: boolean,
) {

  const defaultColor = '#cc6fbc';

  if (!flowJobType) {
    return defaultColor;
  }

  const categoryKeys: (keyof SelectedJobType)[] = [
    'source',
    'jobOrigin',
    'estimateMethod',
    'estimator',
    'salesAgent',
    'utmSource',
    'utmTerm',
    'utmMedium',
    'utmCampaign',
    'utmContent',
  ];

  for (const key of categoryKeys) {
    if (!flowJobType[key]) { continue; }

    if ([ 'estimator', 'salesAgent' ].includes(key)) {
      return style.getPropertyValue(getColorByCategory(getUserName(flowJobType[key as ('salesAgent' | 'estimator')])));
    }

    return style.getPropertyValue(getColorByCategory(flowJobType[key] as string));
  }

  const { jobStage } = flowJobType;

  const stageColors = {
    'Lead': '--cyan-200',
    'Estimate': '--yellow-500',
    'Booking': '--purple-600',
    'Invoice': '--success',
  };

  const stagecolor = style.getPropertyValue(stageColors[jobStage]);

  if (highlightAll || isSelected(flowJobType, selectedJobType)) {
    return getHoverColor(stagecolor);
  }

  return stagecolor;
}

function getColorByCategory(categoryName: string): string {

  const colorPaletteArray = [
    '--red-light',
    '--maroon-light',
    '--orange-light',
    '--pink-light',
    '--beige-light',
    '--green-light',
    '--teal-light',
    '--cyan-light',
    '--navy-light',
    '--blue-light',
    '--magenta-light',
    '--blue-600',
    '--green-600',
    '--yellow-600',
    '--pink-600',
    '--indigo-600',
    '--teal-600',
    '--orange-500',
  ];

  const index = hashString(categoryName) % colorPaletteArray.length;
  return colorPaletteArray[index];
}

function getPeriodTypeForPipelinePeriod(pipelinePeriod: PipelinePeriod) {

  const periodMap: Record<PipelinePeriod, string> = {
      'Lead Created': 'jobCreatedAt',
      Estimated: 'jobEstimateStageDate',
      Booked: 'jobBookingStageDate',
      Happening: 'eventStart',
      Invoiced: 'jobInvoiceStageDate',
      Completed: 'jobClosedAt',
  };

  return periodMap[pipelinePeriod];
}

function getEventTypeFilter(pipelinePeriod: PipelinePeriod): PreviewReportFilter['event']['getEventsOfType'] {

  if (pipelinePeriod === 'Happening') {
    return [ 'moving', 'delivery', 'packing', 'unpacking' ];
  }

  return [];
}

function getUserName(raw: string) {

  if (raw === 'None') { return raw; }

  return decodeUser(raw).name;
}

export function decodeUser(raw: string): User {

  if (raw === 'None') { return; }

  const [ givenName, familyName, id ] = raw.split('.');

  return {
    name: `${givenName} ${familyName}`,
    id,
  }
}

function has<T extends object, K extends keyof T>(
  value: T,
  key: K,
  invalid: 'None' | 'Not specified' = 'None',
) {
  if (!value[key]) { return false; }
  return value[key] !== invalid;
}
